import { isCoalescibleText, SnapshotHistory, DEFAULT_HISTORY_LIMIT, writeFlavoursTo, explodeRawHtml, encodeBlockList, legacyProjectionsFromStructured, parseBlockAddress, createStructuredDoczHandle, DocumentMirror, mirrorBlocksFromHandle, legacyFromStructuredBlock, carrySectionDecoration } from '@norkron/web-docz';
export { DocumentMirror, MIRROR_PROFILE, MIRROR_ROOT_STYLE, MIRROR_VISIBLE_STYLE, buildMirrorArticle, hideFromAssistiveTech as hideCanvasesFrom, hideFromAssistiveTech, mirrorBlocksFromHandle, mirrorPlainText, normaliseLang, projectHandle } from '@norkron/web-docz';

// src/index.ts

// src/activation.ts
function orderActivatable(ranges) {
  return [...ranges].sort((a, b) => a.blockOrd - b.blockOrd || a.start - b.start || a.end - b.end);
}
function nextActivationIndex(count, from, step) {
  if (count <= 0) return null;
  const current = from !== null && Number.isInteger(from) && from >= 0 && from < count ? from : null;
  if (current === null) return step === 1 ? 0 : count - 1;
  const next = current + step;
  return next >= 0 && next < count ? next : null;
}

// src/soa.ts
var MAGIC = 826756176;
var VERSION = 14;
var MIN_VERSION = 1;
var FLAG_GLYPH_GRANULAR = 1;
var FLAG_CONTINUOUS = 2;
var SUPPRESSED_SHIFT = 8;
var SUPPRESSED_MASK = 65280;
var SUPPRESSED_PAGE_CHROME = 1 << 0;
var SUPPRESSED_PAGE_BORDERS = 1 << 1;
var SUPPRESSED_LINE_NUMBERING = 1 << 2;
var SUPPRESSED_PAGE_BREAKS = 1 << 3;
var SUPPRESSED_PAGE_BACKGROUND = 1 << 4;
function decodeSuppressed(flags) {
  const b = (flags & SUPPRESSED_MASK) >>> SUPPRESSED_SHIFT;
  return {
    pageChrome: (b & SUPPRESSED_PAGE_CHROME) !== 0,
    pageBorders: (b & SUPPRESSED_PAGE_BORDERS) !== 0,
    lineNumbering: (b & SUPPRESSED_LINE_NUMBERING) !== 0,
    pageBreaks: (b & SUPPRESSED_PAGE_BREAKS) !== 0,
    pageBackground: (b & SUPPRESSED_PAGE_BACKGROUND) !== 0,
    any: b !== 0
  };
}
var DECO_UNDERLINE = 1 << 0;
var DECO_STRIKETHROUGH = 1 << 1;
var DECO_SHADOW = 1 << 2;
var DECO_OUTLINE = 1 << 3;
var DECO_EMBOSS = 1 << 4;
var DECO_ENGRAVE = 1 << 5;
var DECO_GLOW = 1 << 6;
var DECO_REFLECTION = 1 << 7;
var HEADER_LEN = 68;
var PAGE_REC = 44;
var RUN_REC = 124;
var NO_CELL = 4294967295;
var NO_ORD = 4294967295;
var HEADER_ROW = 4294967294;
var PAGE_NOMINAL_W_OFF = 36;
var PAGE_NOMINAL_H_OFF = 40;
function requiredRunRec(version2) {
  if (version2 >= 14) return 124;
  if (version2 >= 12) return 116;
  if (version2 >= 7) return 100;
  if (version2 >= 5) return 96;
  if (version2 >= 3) return 88;
  if (version2 === 2) return 72;
  return 68;
}
var RUN_DECO_OFF = 68;
var RUN_EMPHASIS_OFF = 69;
var RUN_CB_STYLE_OFF = 70;
var RUN_DC_LINES_OFF = 71;
var RUN_DC_KIND_OFF = 72;
var RUN_CB_R_OFF = 73;
var RUN_GLOW_R_OFF = 76;
var RUN_BLANK_ORIGIN_OFF = 79;
var RUN_CB_WIDTH_OFF = 80;
var RUN_DC_DISTANCE_OFF = 84;
var RUN_CELL_ROW_OFF = 88;
var RUN_CELL_COL_OFF = 92;
var RUN_TABLE_ORD_OFF = 96;
var RUN_CELL_X_OFF = 100;
var RUN_CELL_Y_OFF = 104;
var RUN_CELL_W_OFF = 108;
var RUN_CELL_H_OFF = 112;
var RUN_FAMILY_OFF_OFF = 116;
var RUN_FAMILY_LEN_OFF = 120;
var GLYPH_REC = 4;
var RULE_REC = 32;
var IMAGE_REC = 48;
function requiredImageRec(version2) {
  if (version2 >= 6) return 48;
  return 32;
}
var IMAGE_BLOB_OFF_OFF = 32;
var IMAGE_BLOB_LEN_OFF = 36;
var IMAGE_ALT_OFF_OFF = 40;
var IMAGE_ALT_LEN_OFF = 44;
var FACE_REC = 8;
function runIsCell(run) {
  return run.cellCol !== NO_CELL && run.cellRow !== NO_CELL;
}
function runInTable(run) {
  return run.tableOrd !== NO_ORD;
}
var RunKind = {
  unknown: 0,
  paragraph: 1,
  heading: 2,
  listMarker: 3,
  listItem: 4,
  tableCell: 5,
  caption: 6,
  overlay: 7,
  footnote: 8,
  footnoteMarker: 9,
  /**
   * v8 — an EDITABLE code block (`<pre>`). Distinct from {@link RunKind.rawMarkup}
   * because a code block's `(byteStart, byteEnd)` index its own source text, so
   * a caret in it round-trips.
   */
  code: 10,
  /**
   * v8 — the READ-ONLY plain-text PROJECTION of an opaque `raw_html` /
   * `raw_ooxml` island. Its `(byteStart, byteEnd)` index the PROJECTED text, NOT
   * the island's markup source, so typing into it can never round-trip: it must
   * be rendered read-only and excluded from caret targeting.
   */
  rawMarkup: 11,
  /**
   * v9 — a DELIBERATELY BLANK LINE: the single, zero-glyph line box an EMPTY
   * (or whitespace-only) paragraph occupies.
   *
   * `glyphCount === 0`, `widthPt === 0`, `strLen === 0` and
   * `byteStart === byteEnd === 0` — but `xPt`, `baselineYPt` and `sizePt` are
   * REAL, measured by the same layout pass that positioned every other line.
   * That is the whole point: it is a POSITION with no glyphs, which is exactly
   * what a caret in an empty paragraph needs and what the wire could not
   * express before v9.
   *
   * It is a distinct kind rather than a `paragraph` run of zero length because
   * a zero-length run is ALSO the signature of a run that failed to paint (an
   * unshaped legacy-CID degrade). {@link isAddressable} rejects that shape
   * deliberately; `emptyLine` is the one value that says "empty on purpose".
   */
  emptyLine: 12
};
function namespaceKindOf(run) {
  if (run.runKind !== RunKind.emptyLine) return run.runKind;
  if (run.blankOriginKind !== RunKind.unknown) return run.blankOriginKind;
  return runInTable(run) ? RunKind.tableCell : RunKind.paragraph;
}
var LayoutView = class {
  dv;
  bytes;
  version;
  flags;
  pageCount;
  runCount;
  glyphCount;
  ruleCount;
  imageCount;
  faceCount;
  offPages;
  offRuns;
  offGlyphs;
  offRules;
  offImages;
  offFaces;
  offStrings;
  decoder = new TextDecoder();
  /**
   * Per-section record strides, DERIVED from the header's section offsets
   * rather than hard-coded — `soa.zig` lays every section out contiguously with
   * no alignment padding, so `(next_section - this_section) / count` is the
   * exact stride whatever version wrote the buffer. This is what lets a grown
   * record in a future version decode without touching this file.
   */
  runRec;
  pageRec;
  glyphRec;
  ruleRec;
  imageRec;
  faceRec;
  constructor(buffer) {
    const u8 = buffer instanceof Uint8Array ? buffer : new Uint8Array(buffer);
    this.bytes = u8;
    this.dv = new DataView(u8.buffer, u8.byteOffset, u8.byteLength);
    if (u8.byteLength < HEADER_LEN)
      throw new Error("web-page-layout: SoA buffer truncated (header)");
    if (this.dv.getUint32(0, true) !== MAGIC)
      throw new Error(
        "web-page-layout: SoA bad magic \u2014 the buffer is not a pdfz layout-geometry buffer. Check that pdfz-layout.wasm returned geometry rather than an error code."
      );
    this.version = this.dv.getUint16(4, true);
    if (this.version < MIN_VERSION)
      throw new Error(
        `web-page-layout: SoA version ${this.version} is below the minimum supported version ${MIN_VERSION} (this build reads up to v${VERSION}). The buffer was produced by a pre-release pdfz-layout.wasm \u2014 rebuild the wasm artifact.`
      );
    this.flags = this.dv.getUint16(6, true);
    this.pageCount = this.dv.getUint32(8, true);
    this.runCount = this.dv.getUint32(12, true);
    this.glyphCount = this.dv.getUint32(16, true);
    this.ruleCount = this.dv.getUint32(20, true);
    this.imageCount = this.dv.getUint32(24, true);
    this.faceCount = this.dv.getUint32(28, true);
    this.offPages = this.dv.getUint32(32, true);
    this.offRuns = this.dv.getUint32(36, true);
    this.offGlyphs = this.dv.getUint32(40, true);
    this.offRules = this.dv.getUint32(44, true);
    this.offImages = this.dv.getUint32(48, true);
    this.offFaces = this.dv.getUint32(52, true);
    this.offStrings = this.dv.getUint32(56, true);
    const total = this.dv.getUint32(64, true);
    if (u8.byteLength < total)
      throw new Error(
        `web-page-layout: SoA buffer truncated (body) \u2014 header declares ${total} bytes, got ${u8.byteLength}. The geometry buffer was copied out of wasm memory incompletely.`
      );
    this.pageRec = this.stride(this.offPages, this.offRuns, this.pageCount, PAGE_REC, "page");
    this.runRec = this.stride(this.offRuns, this.offGlyphs, this.runCount, RUN_REC, "run");
    this.glyphRec = this.stride(this.offGlyphs, this.offRules, this.glyphCount, GLYPH_REC, "glyph");
    this.ruleRec = this.stride(this.offRules, this.offImages, this.ruleCount, RULE_REC, "rule");
    this.imageRec = this.stride(this.offImages, this.offFaces, this.imageCount, IMAGE_REC, "image");
    this.faceRec = this.stride(this.offFaces, this.offStrings, this.faceCount, FACE_REC, "face");
    const need = requiredRunRec(Math.min(this.version, VERSION));
    if (this.runCount > 0 && this.runRec < need)
      throw new Error(
        `web-page-layout: SoA run record is ${this.runRec} bytes but wire version ${this.version} requires at least ${need}. The buffer's version header and its record layout disagree \u2014 regenerate it with a matching pdfz-layout.wasm.`
      );
    const needImage = requiredImageRec(Math.min(this.version, VERSION));
    if (this.imageCount > 0 && this.imageRec < needImage)
      throw new Error(
        `web-page-layout: SoA image record is ${this.imageRec} bytes but wire version ${this.version} requires at least ${needImage}. The buffer's version header and its record layout disagree \u2014 regenerate it with a matching pdfz-layout.wasm.`
      );
  }
  /**
   * `(next - start) / count`, validated. A section must be a uniform array with
   * no padding (which is exactly how `soa.zig` writes it), so a non-integral or
   * negative stride means the header offsets are corrupt — fail loudly rather
   * than paint misaligned geometry. `count === 0` → the known constant (the
   * section is never indexed).
   */
  stride(start, next, count, known, label) {
    if (count === 0) return known;
    const span = next - start;
    if (span <= 0 || span % count !== 0)
      throw new Error(
        `web-page-layout: SoA ${label} section is ${span} bytes for ${count} records \u2014 not a uniform array. The header offsets are corrupt.`
      );
    return span / count;
  }
  get glyphGranular() {
    return (this.flags & FLAG_GLYPH_GRANULAR) !== 0;
  }
  /**
   * v4 — true when this geometry came from a CONTINUOUS layout pass: one page,
   * `heightPt` = the measured content extent. False (the pre-v4 default, since
   * the flag bit is simply absent) ⇒ the paginated model.
   */
  get continuous() {
    return (this.flags & FLAG_CONTINUOUS) !== 0;
  }
  /**
   * v4 — the per-page frame features the engine suppressed. All-false for a
   * paginated buffer. Surface `any` in the UI rather than letting a watermark or
   * page border vanish without explanation.
   */
  get suppressed() {
    return decodeSuppressed(this.flags);
  }
  page(i) {
    const o = this.offPages + i * this.pageRec;
    const dv = this.dv;
    const widthPt = dv.getFloat32(o + 4, true);
    const heightPt = dv.getFloat32(o + 8, true);
    const hasTail = this.version >= 4 && this.pageRec >= PAGE_REC;
    return {
      index: dv.getUint32(o + 0, true),
      widthPt,
      heightPt,
      runFirst: dv.getUint32(o + 12, true),
      runCount: dv.getUint32(o + 16, true),
      ruleFirst: dv.getUint32(o + 20, true),
      ruleCount: dv.getUint32(o + 24, true),
      imageFirst: dv.getUint32(o + 28, true),
      imageCount: dv.getUint32(o + 32, true),
      nominalWidthPt: hasTail ? dv.getFloat32(o + PAGE_NOMINAL_W_OFF, true) : widthPt,
      nominalHeightPt: hasTail ? dv.getFloat32(o + PAGE_NOMINAL_H_OFF, true) : heightPt
    };
  }
  run(i) {
    const o = this.offRuns + i * this.runRec;
    const dv = this.dv;
    const deco = this.version >= 2 ? dv.getUint8(o + RUN_DECO_OFF) : 0;
    const v3 = this.version >= 3;
    const v5 = this.version >= 5;
    const v7 = this.version >= 7;
    const v12 = this.version >= 12 && this.runRec >= 116;
    const v13 = this.version >= 13;
    const v14 = this.version >= 14 && this.runRec >= RUN_REC;
    const familyLen = v14 ? dv.getUint32(o + RUN_FAMILY_LEN_OFF, true) : 0;
    return {
      blockOrd: dv.getUint32(o + 0, true),
      byteStart: dv.getUint32(o + 4, true),
      byteEnd: dv.getUint32(o + 8, true),
      lineOrd: dv.getUint32(o + 12, true),
      runKind: dv.getUint8(o + 16),
      shaped: dv.getUint8(o + 17) !== 0,
      cpGlyph1to1: dv.getUint8(o + 18) !== 0,
      hasColor: dv.getUint8(o + 19) !== 0,
      xPt: dv.getFloat32(o + 20, true),
      baselineYPt: dv.getFloat32(o + 24, true),
      sizePt: dv.getFloat32(o + 28, true),
      widthPt: dv.getFloat32(o + 32, true),
      color: [
        dv.getFloat32(o + 36, true),
        dv.getFloat32(o + 40, true),
        dv.getFloat32(o + 44, true)
      ],
      glyphFirst: dv.getUint32(o + 48, true),
      glyphCount: dv.getUint32(o + 52, true),
      strOff: dv.getUint32(o + 56, true),
      strLen: dv.getUint32(o + 60, true),
      fontId: dv.getUint32(o + 64, true),
      underline: (deco & DECO_UNDERLINE) !== 0,
      strikethrough: (deco & DECO_STRIKETHROUGH) !== 0,
      shadow: (deco & DECO_SHADOW) !== 0,
      outline: (deco & DECO_OUTLINE) !== 0,
      emboss: (deco & DECO_EMBOSS) !== 0,
      engrave: (deco & DECO_ENGRAVE) !== 0,
      glow: (deco & DECO_GLOW) !== 0,
      reflection: (deco & DECO_REFLECTION) !== 0,
      glowRgb: v3 ? [
        dv.getUint8(o + RUN_GLOW_R_OFF),
        dv.getUint8(o + RUN_GLOW_R_OFF + 1),
        dv.getUint8(o + RUN_GLOW_R_OFF + 2)
      ] : [0, 0, 0],
      cbWidthPt: v3 ? dv.getFloat32(o + RUN_CB_WIDTH_OFF, true) : 0,
      cbStyle: v3 ? dv.getUint8(o + RUN_CB_STYLE_OFF) : 0,
      cbRgb: v3 ? [
        dv.getUint8(o + RUN_CB_R_OFF),
        dv.getUint8(o + RUN_CB_R_OFF + 1),
        dv.getUint8(o + RUN_CB_R_OFF + 2)
      ] : [0, 0, 0],
      emphasis: v3 ? dv.getUint8(o + RUN_EMPHASIS_OFF) : 0,
      dcLines: v3 ? dv.getUint8(o + RUN_DC_LINES_OFF) : 0,
      dcKind: v3 ? dv.getUint8(o + RUN_DC_KIND_OFF) : 0,
      dcDistancePt: v3 ? dv.getFloat32(o + RUN_DC_DISTANCE_OFF, true) : 0,
      cellRow: v5 ? dv.getUint32(o + RUN_CELL_ROW_OFF, true) : NO_CELL,
      cellCol: v5 ? dv.getUint32(o + RUN_CELL_COL_OFF, true) : NO_CELL,
      tableOrd: v7 ? dv.getUint32(o + RUN_TABLE_ORD_OFF, true) : NO_ORD,
      cellBox: v12 && dv.getFloat32(o + RUN_CELL_W_OFF, true) > 0 ? {
        xPt: dv.getFloat32(o + RUN_CELL_X_OFF, true),
        yPt: dv.getFloat32(o + RUN_CELL_Y_OFF, true),
        wPt: dv.getFloat32(o + RUN_CELL_W_OFF, true),
        hPt: dv.getFloat32(o + RUN_CELL_H_OFF, true)
      } : null,
      blankOriginKind: v13 ? dv.getUint8(o + RUN_BLANK_ORIGIN_OFF) : RunKind.unknown,
      // A zero LENGTH is the wire's "no name declared", and it is what a pre-v14
      // buffer's absent bytes mean too — so both wires answer `null` and no
      // consumer has to know which one it is holding.
      fontFamilyName: familyLen === 0 ? null : this.blobText(dv.getUint32(o + RUN_FAMILY_OFF_OFF, true), familyLen)
    };
  }
  /**
   * Decode a UTF-8 string from a blob-section byte range. Shared by every
   * string-valued field on the wire (run text, image alt, the v14 font-family
   * name) so the `off_strings` base is applied in exactly ONE place.
   */
  blobText(off, len) {
    const start = this.offStrings + off;
    return this.decoder.decode(this.bytes.subarray(start, start + len));
  }
  /** Per-glyph advance in 1000-em units (multiply by size_pt / 1000 → points). */
  glyphAdvance(i) {
    return this.dv.getInt32(this.offGlyphs + i * this.glyphRec, true);
  }
  rule(i) {
    const o = this.offRules + i * this.ruleRec;
    const dv = this.dv;
    return {
      xPt: dv.getFloat32(o + 0, true),
      yPt: dv.getFloat32(o + 4, true),
      wPt: dv.getFloat32(o + 8, true),
      hPt: dv.getFloat32(o + 12, true),
      fillKind: dv.getUint8(o + 16),
      c: [dv.getFloat32(o + 20, true), dv.getFloat32(o + 24, true), dv.getFloat32(o + 28, true)]
    };
  }
  image(i) {
    const o = this.offImages + i * this.imageRec;
    const dv = this.dv;
    const hasTail = this.version >= 6 && this.imageRec >= IMAGE_REC;
    return {
      blockOrd: dv.getUint32(o + 0, true),
      byteStart: dv.getUint32(o + 4, true),
      byteEnd: dv.getUint32(o + 8, true),
      xPt: dv.getFloat32(o + 12, true),
      yPt: dv.getFloat32(o + 16, true),
      wPt: dv.getFloat32(o + 20, true),
      hPt: dv.getFloat32(o + 24, true),
      kind: dv.getUint8(o + 28),
      blobOff: hasTail ? dv.getUint32(o + IMAGE_BLOB_OFF_OFF, true) : 0,
      blobLen: hasTail ? dv.getUint32(o + IMAGE_BLOB_LEN_OFF, true) : 0,
      altOff: hasTail ? dv.getUint32(o + IMAGE_ALT_OFF_OFF, true) : 0,
      altLen: hasTail ? dv.getUint32(o + IMAGE_ALT_LEN_OFF, true) : 0
    };
  }
  /**
   * An image's ENCODED payload (PNG/JPEG container, or SVG source) as a
   * zero-copy view into the geometry buffer, or `null` when the wire carries
   * none. The slice is a VIEW — do not retain it past the buffer's lifetime;
   * pass it straight to `new Blob([...])` / `createImageBitmap`.
   */
  imageBytes(image) {
    if (image.blobLen === 0) return null;
    const start = this.offStrings + image.blobOff;
    return this.bytes.subarray(start, start + image.blobLen);
  }
  /** An image's ALT text (WCAG 1.1.1), or `''` when the author gave none. */
  imageAlt(image) {
    if (image.altLen === 0) return "";
    return this.blobText(image.altOff, image.altLen);
  }
  face(i) {
    const o = this.offFaces + i * this.faceRec;
    const dv = this.dv;
    return {
      fontId: dv.getUint32(o + 0, true),
      family: dv.getUint8(o + 4),
      weight: dv.getUint8(o + 5),
      italic: dv.getUint8(o + 6)
    };
  }
  faceForFontId(fontId) {
    for (let i = 0; i < this.faceCount; i++) {
      const f = this.face(i);
      if (f.fontId === fontId) return f;
    }
    return null;
  }
  /** A run's source text (decoded UTF-8). */
  runText(run) {
    return this.blobText(run.strOff, run.strLen);
  }
};

// src/geometry.ts
var BLOCK_ORD_NONE = 4294967295;
var BLOCK_ORD_AUX_BIT = 2147483648;
function isAuxBlockOrd(ord) {
  return ord !== BLOCK_ORD_NONE && (ord & BLOCK_ORD_AUX_BIT) !== 0;
}
function isAuxCaretEligible(run) {
  const kind = namespaceKindOf(run);
  if (kind === RunKind.tableCell) return runInTable(run);
  return kind === RunKind.heading || kind === RunKind.listItem;
}
var DEFAULT_METRICS = { ascentRatio: 0.8, descentRatio: 0.2 };
function isEmptyLineRun(run) {
  return run.runKind === RunKind.emptyLine;
}
function isAddressable(run) {
  if (run.blockOrd === BLOCK_ORD_NONE) return false;
  if (isEmptyLineRun(run)) return !isAuxBlockOrd(run.blockOrd) || isAuxCaretEligible(run);
  if (!run.shaped || run.strLen <= 0 || run.byteEnd <= run.byteStart) return false;
  if (isAuxBlockOrd(run.blockOrd)) return isAuxCaretEligible(run);
  return true;
}
function utf8Len(cp) {
  if (cp < 128) return 1;
  if (cp < 2048) return 2;
  if (cp < 65536) return 3;
  return 4;
}
function buildGeometryIndex(view, opts = {}) {
  const metrics = opts.metrics ?? DEFAULT_METRICS;
  const remap = opts.remapBlockOrd;
  const groups = /* @__PURE__ */ new Map();
  const dead = /* @__PURE__ */ new Map();
  const markDead = (key, pageIndex, top, bottom) => {
    const cur = dead.get(key);
    if (cur) {
      cur.top = Math.min(cur.top, top);
      cur.bottom = Math.max(cur.bottom, bottom);
      return;
    }
    dead.set(key, { pageIndex, top, bottom });
  };
  for (let p = 0; p < view.pageCount; p++) {
    const pg = view.page(p);
    for (let i = 0; i < pg.runCount; i++) {
      const gi = pg.runFirst + i;
      const run = view.run(gi);
      const runTop = pg.heightPt - run.baselineYPt - run.sizePt * metrics.ascentRatio;
      const runBottom = pg.heightPt - run.baselineYPt + run.sizePt * metrics.descentRatio;
      if (!isAddressable(run)) {
        if (run.blockOrd !== BLOCK_ORD_NONE && run.runKind !== RunKind.listMarker) {
          markDead(`${p}:unaddressable:${run.blockOrd}:${run.lineOrd}`, p, runTop, runBottom);
        }
        continue;
      }
      if (!remap && isAuxBlockOrd(run.blockOrd)) {
        markDead(`${p}:aux:${run.blockOrd}:${run.lineOrd}`, p, runTop, runBottom);
        continue;
      }
      const blockOrd = remap ? remap(run.blockOrd) : run.blockOrd;
      if (blockOrd === void 0) {
        markDead(`${p}:${run.blockOrd}:${run.lineOrd}`, p, runTop, runBottom);
        continue;
      }
      const key = `${p}:${blockOrd}:${run.lineOrd}`;
      let acc = groups.get(key);
      if (!acc) {
        acc = {
          pageIndex: p,
          blockOrd,
          lineOrd: run.lineOrd,
          runIdxs: [],
          xMin: run.xPt,
          xMax: run.xPt + run.widthPt,
          baseline: run.baselineYPt,
          size: run.sizePt,
          byteStart: run.byteStart,
          byteEnd: run.byteEnd,
          heightPt: pg.heightPt,
          // PDF bottom-left → canvas top-down, the same flip every other
          // vertical coordinate in this index takes.
          cellBox: run.cellBox != null ? {
            pageIndex: p,
            xPt: run.cellBox.xPt,
            yPt: pg.heightPt - (run.cellBox.yPt + run.cellBox.hPt),
            wPt: run.cellBox.wPt,
            hPt: run.cellBox.hPt
          } : null
        };
        groups.set(key, acc);
      }
      acc.runIdxs.push(gi);
      acc.xMin = Math.min(acc.xMin, run.xPt);
      acc.xMax = Math.max(acc.xMax, run.xPt + run.widthPt);
      acc.baseline = Math.max(acc.baseline, run.baselineYPt);
      acc.size = Math.max(acc.size, run.sizePt);
      acc.byteStart = Math.min(acc.byteStart, run.byteStart);
      acc.byteEnd = Math.max(acc.byteEnd, run.byteEnd);
    }
  }
  const lines = [];
  for (const acc of groups.values()) {
    acc.runIdxs.sort((a, b) => view.run(a).xPt - view.run(b).xPt);
    const baselineCanvas = acc.heightPt - acc.baseline;
    lines.push({
      pageIndex: acc.pageIndex,
      blockOrd: acc.blockOrd,
      lineOrd: acc.lineOrd,
      runIdxs: acc.runIdxs,
      xMinPt: acc.xMin,
      xMaxPt: acc.xMax,
      baselineYPt: acc.baseline,
      topPt: baselineCanvas - acc.size * metrics.ascentRatio,
      bottomPt: baselineCanvas + acc.size * metrics.descentRatio,
      sizePt: acc.size,
      byteStart: acc.byteStart,
      byteEnd: acc.byteEnd,
      cellBox: acc.cellBox
    });
  }
  lines.sort((a, b) => a.pageIndex - b.pageIndex || a.topPt - b.topPt || a.xMinPt - b.xMinPt);
  const byBlock = /* @__PURE__ */ new Map();
  for (const ln of lines) {
    const arr = byBlock.get(ln.blockOrd);
    if (arr) arr.push(ln);
    else byBlock.set(ln.blockOrd, [ln]);
  }
  for (const arr of byBlock.values()) arr.sort((a, b) => a.lineOrd - b.lineOrd);
  const deadZones = [];
  for (const d of dead.values()) {
    deadZones.push({ pageIndex: d.pageIndex, topPt: d.top, bottomPt: d.bottom });
  }
  return { lines, byBlock, deadZones };
}
function runCaretXPt(view, run, bo) {
  const localByte = Math.max(0, Math.min(bo - run.byteStart, run.strLen));
  const unit = run.sizePt / 1e3;
  if (!run.cpGlyph1to1) {
    return localByte * 2 <= run.strLen ? run.xPt : run.xPt + run.widthPt;
  }
  const text = view.runText(run);
  let byteAcc = 0;
  let prefix1000 = 0;
  let g = 0;
  for (const ch of text) {
    if (byteAcc >= localByte) break;
    byteAcc += utf8Len(ch.codePointAt(0) ?? 0);
    if (g < run.glyphCount) prefix1000 += view.glyphAdvance(run.glyphFirst + g);
    g++;
  }
  return run.xPt + prefix1000 * unit;
}
function runOffsetAtXPt(view, run, targetXPt) {
  if (!run.cpGlyph1to1) {
    return targetXPt < run.xPt + run.widthPt / 2 ? run.byteStart : run.byteEnd;
  }
  const text = view.runText(run);
  const unit = run.sizePt / 1e3;
  let byteAcc = 0;
  let prevX = run.xPt;
  let prefix1000 = 0;
  let g = 0;
  for (const ch of text) {
    const adv = g < run.glyphCount ? view.glyphAdvance(run.glyphFirst + g) : 0;
    const nextX = run.xPt + (prefix1000 + adv) * unit;
    const mid = (prevX + nextX) / 2;
    if (targetXPt < mid) return run.byteStart + byteAcc;
    byteAcc += utf8Len(ch.codePointAt(0) ?? 0);
    prefix1000 += adv;
    prevX = nextX;
    g++;
  }
  return run.byteEnd;
}
function xDistanceTo(line, xPt) {
  if (xPt < line.xMinPt) return line.xMinPt - xPt;
  if (xPt > line.xMaxPt) return xPt - line.xMaxPt;
  return 0;
}
function lineForPoint(index, pageIndex, xPt, yPt) {
  let inBand = null;
  let inBandX = Number.POSITIVE_INFINITY;
  let nearest = null;
  let nearestY = Number.POSITIVE_INFINITY;
  let nearestX = Number.POSITIVE_INFINITY;
  for (const ln of index.lines) {
    if (ln.pageIndex !== pageIndex) continue;
    const dx = xDistanceTo(ln, xPt);
    if (yPt >= ln.topPt && yPt <= ln.bottomPt) {
      if (dx < inBandX) {
        inBandX = dx;
        inBand = ln;
      }
      continue;
    }
    if (inBand) continue;
    const dy = Math.abs(yPt - (ln.topPt + ln.bottomPt) / 2);
    if (dy < nearestY || dy === nearestY && dx < nearestX) {
      nearestY = dy;
      nearestX = dx;
      nearest = ln;
    }
  }
  if (inBand) return inBand;
  if (deadZoneAt(index, pageIndex, yPt)) return null;
  return nearest;
}
function deadZoneAt(index, pageIndex, yPt) {
  for (const dz of index.deadZones ?? []) {
    if (dz.pageIndex !== pageIndex) continue;
    if (yPt >= dz.topPt && yPt <= dz.bottomPt) return dz;
  }
  return null;
}
function runOnLineAtX(view, line, xPt) {
  let best = -1;
  let bestDist = Number.POSITIVE_INFINITY;
  for (const gi of line.runIdxs) {
    const run = view.run(gi);
    const lo = run.xPt;
    const hi = run.xPt + run.widthPt;
    if (xPt >= lo && xPt <= hi) return gi;
    const d = xPt < lo ? lo - xPt : xPt - hi;
    if (d < bestDist) {
      bestDist = d;
      best = gi;
    }
  }
  return best;
}
function hitTest(view, index, pageIndex, xPt, yPt) {
  const line = lineForPoint(index, pageIndex, xPt, yPt);
  if (!line) return null;
  if (xPt <= line.xMinPt) {
    return { blockOrd: line.blockOrd, byteOffset: line.byteStart, affinity: "downstream" };
  }
  if (xPt >= line.xMaxPt) {
    return { blockOrd: line.blockOrd, byteOffset: line.byteEnd, affinity: "upstream" };
  }
  const gi = runOnLineAtX(view, line, xPt);
  if (gi < 0) {
    return { blockOrd: line.blockOrd, byteOffset: line.byteStart, affinity: "downstream" };
  }
  const run = view.run(gi);
  return { blockOrd: line.blockOrd, byteOffset: runOffsetAtXPt(view, run, xPt) };
}
function blockLines(index, blockOrd) {
  return index.byBlock.get(blockOrd) ?? [];
}
function lineForOffset(index, pos) {
  const lines = blockLines(index, pos.blockOrd);
  if (lines.length === 0) return null;
  const bo = pos.byteOffset;
  for (let i = 0; i < lines.length; i++) {
    const ln = lines[i];
    if (bo >= ln.byteStart && bo < ln.byteEnd) return ln;
    if (bo === ln.byteEnd) {
      const next = lines[i + 1];
      if (next && bo >= next.byteStart && pos.affinity !== "upstream") return next;
      return ln;
    }
    if (bo < ln.byteStart) return i === 0 ? ln : lines[i - 1];
  }
  return lines[lines.length - 1];
}
function caretRect(view, index, pos, metrics = DEFAULT_METRICS, caretWidthPt = 1) {
  const line = lineForOffset(index, pos);
  if (!line) return null;
  const clamped = Math.max(line.byteStart, Math.min(pos.byteOffset, line.byteEnd));
  let x = line.xMinPt;
  let placed = false;
  for (const gi of line.runIdxs) {
    const run = view.run(gi);
    if (clamped >= run.byteStart && clamped <= run.byteEnd) {
      x = runCaretXPt(view, run, clamped);
      placed = true;
      break;
    }
  }
  if (!placed) {
    x = clamped >= line.byteEnd ? line.xMaxPt : line.xMinPt;
  }
  const height = line.sizePt * (metrics.ascentRatio + metrics.descentRatio);
  return {
    pageIndex: line.pageIndex,
    xPt: x,
    yPt: line.topPt,
    wPt: caretWidthPt,
    hPt: height
  };
}
function comparePos(a, b) {
  return a.blockOrd - b.blockOrd || a.byteOffset - b.byteOffset;
}
function selectionRects(view, index, from, to, metrics = DEFAULT_METRICS, opts = {}) {
  const [start, end] = comparePos(from, to) <= 0 ? [from, to] : [to, from];
  if (comparePos(start, end) === 0) return [];
  const rects = [];
  for (const [blockOrd, lines] of index.byBlock) {
    if (blockOrd < start.blockOrd || blockOrd > end.blockOrd) continue;
    if (opts.cellBoxes === true) {
      const box = wholeCellBox(lines, blockOrd, start, end);
      if (box) {
        rects.push(box);
        continue;
      }
    }
    for (const line of lines) {
      const lineLo = blockOrd === start.blockOrd ? Math.max(line.byteStart, start.byteOffset) : line.byteStart;
      const lineHi = blockOrd === end.blockOrd ? Math.min(line.byteEnd, end.byteOffset) : line.byteEnd;
      if (lineHi <= lineLo) continue;
      const xLeft = caretXOnLine(view, line, lineLo);
      const xRight = caretXOnLine(view, line, lineHi);
      const height = line.sizePt * (metrics.ascentRatio + metrics.descentRatio);
      rects.push({
        pageIndex: line.pageIndex,
        xPt: Math.min(xLeft, xRight),
        yPt: line.topPt,
        wPt: Math.abs(xRight - xLeft),
        hPt: height
      });
    }
  }
  return rects;
}
function wholeCellBox(lines, blockOrd, start, end) {
  const box = lines[0]?.cellBox;
  if (box == null) return null;
  let lo = Number.POSITIVE_INFINITY;
  let hi = Number.NEGATIVE_INFINITY;
  for (const line of lines) {
    if (line.cellBox == null || line.cellBox.xPt !== box.xPt || line.cellBox.yPt !== box.yPt) {
      return null;
    }
    lo = Math.min(lo, line.byteStart);
    hi = Math.max(hi, line.byteEnd);
  }
  if (blockOrd === start.blockOrd && start.byteOffset > lo) return null;
  if (blockOrd === end.blockOrd && end.byteOffset < hi) return null;
  return box;
}
function caretXOnLine(view, line, bo) {
  const clamped = Math.max(line.byteStart, Math.min(bo, line.byteEnd));
  for (const gi of line.runIdxs) {
    const run = view.run(gi);
    if (clamped >= run.byteStart && clamped <= run.byteEnd) return runCaretXPt(view, run, clamped);
  }
  return clamped >= line.byteEnd ? line.xMaxPt : line.xMinPt;
}
function lineStart(index, pos) {
  const line = lineForOffset(index, pos);
  if (!line) return pos;
  return { blockOrd: line.blockOrd, byteOffset: line.byteStart, affinity: "downstream" };
}
function lineEnd(index, pos) {
  const line = lineForOffset(index, pos);
  if (!line) return pos;
  return { blockOrd: line.blockOrd, byteOffset: line.byteEnd, affinity: "upstream" };
}
function docStart(index) {
  const ln = index.lines[0];
  return ln ? { blockOrd: ln.blockOrd, byteOffset: ln.byteStart, affinity: "downstream" } : null;
}
function docEnd(index) {
  const ln = index.lines[index.lines.length - 1];
  return ln ? { blockOrd: ln.blockOrd, byteOffset: ln.byteEnd, affinity: "upstream" } : null;
}
function moveVertical(view, index, pos, dir, goalXPt) {
  const cur = lineForOffset(index, pos);
  if (!cur) return null;
  const ordered2 = index.lines;
  const curIdx = ordered2.indexOf(cur);
  if (curIdx < 0) return null;
  const targetIdx = curIdx + dir;
  const target = ordered2[targetIdx];
  if (!target) return null;
  if (goalXPt <= target.xMinPt) {
    return { blockOrd: target.blockOrd, byteOffset: target.byteStart, affinity: "downstream" };
  }
  if (goalXPt >= target.xMaxPt) {
    return { blockOrd: target.blockOrd, byteOffset: target.byteEnd, affinity: "upstream" };
  }
  const gi = runOnLineAtX(view, target, goalXPt);
  if (gi < 0) {
    return { blockOrd: target.blockOrd, byteOffset: target.byteStart, affinity: "downstream" };
  }
  return { blockOrd: target.blockOrd, byteOffset: runOffsetAtXPt(view, view.run(gi), goalXPt) };
}
function lineRunKind(view, index, pos) {
  const line = lineForOffset(index, pos);
  if (!line) return RunKind.unknown;
  const first = line.runIdxs[0];
  return first === void 0 ? RunKind.unknown : namespaceKindOf(view.run(first));
}

// src/edit-model.ts
var UTF8 = new TextEncoder();
var UTF8_DEC = new TextDecoder();
function collapsed(pos) {
  return { anchor: pos, focus: pos };
}
function isCollapsed(sel) {
  return sel.anchor.blockOrd === sel.focus.blockOrd && sel.anchor.byteOffset === sel.focus.byteOffset;
}
function rangeSlices(blocks, from, to) {
  const [lo, hi] = comparePos(from, to) <= 0 ? [from, to] : [to, from];
  if (comparePos(lo, hi) === 0) return [];
  const out = [];
  for (let ord = lo.blockOrd; ord <= hi.blockOrd; ord++) {
    const text = blocks[ord]?.text ?? "";
    const start = ord === lo.blockOrd ? Math.min(lo.byteOffset, byteLen(text)) : 0;
    const end = ord === hi.blockOrd ? Math.min(hi.byteOffset, byteLen(text)) : byteLen(text);
    if (end < start) continue;
    out.push({ blockOrd: ord, start, end, text: sliceBytes(text, start, end) });
  }
  return out;
}
function rangeText(blocks, from, to) {
  return rangeSlices(blocks, from, to).map((s) => s.text).join("\n");
}
function byteLen(s) {
  return UTF8.encode(s).length;
}
function sliceBytes(text, from, to) {
  const bytes = UTF8.encode(text);
  const a = Math.max(0, Math.min(from, bytes.length));
  const b = Math.max(a, Math.min(to, bytes.length));
  return new TextDecoder().decode(bytes.subarray(a, b));
}
function spliceBytes(text, at, deleteBytes, insert) {
  const bytes = UTF8.encode(text);
  const a = Math.max(0, Math.min(at, bytes.length));
  const b = Math.max(a, Math.min(a + deleteBytes, bytes.length));
  const ins = UTF8.encode(insert);
  const out = new Uint8Array(a + ins.length + (bytes.length - b));
  out.set(bytes.subarray(0, a), 0);
  out.set(ins, a);
  out.set(bytes.subarray(b), a + ins.length);
  return UTF8_DEC.decode(out);
}
var segmenter = typeof Intl !== "undefined" && "Segmenter" in Intl ? new Intl.Segmenter(void 0, { granularity: "grapheme" }) : null;
function prevGraphemeBytes(text, at) {
  if (at <= 0) return 0;
  const bytes = UTF8.encode(text);
  const head = UTF8_DEC.decode(bytes.subarray(0, Math.min(at, bytes.length)));
  if (segmenter) {
    let last = "";
    for (const s of segmenter.segment(head)) last = s.segment;
    return byteLen(last);
  }
  const cps = Array.from(head);
  const lastCp = cps[cps.length - 1];
  return lastCp ? byteLen(lastCp) : 0;
}
function nextGraphemeBytes(text, at) {
  const bytes = UTF8.encode(text);
  if (at >= bytes.length) return 0;
  const tail = UTF8_DEC.decode(bytes.subarray(Math.max(0, at)));
  if (segmenter) {
    for (const s of segmenter.segment(tail)) return byteLen(s.segment);
    return 0;
  }
  const first = Array.from(tail)[0];
  return first ? byteLen(first) : 0;
}
function prevWordBoundary(text, at) {
  const bytes = UTF8.encode(text);
  let i = Math.max(0, Math.min(at, bytes.length));
  const head = () => UTF8_DEC.decode(bytes.subarray(0, i));
  const lastChar = () => {
    const cps = Array.from(head());
    return cps[cps.length - 1] ?? "";
  };
  while (i > 0 && /\s/.test(lastChar())) i -= prevGraphemeBytes(text, i);
  while (i > 0 && !/\s/.test(lastChar())) i -= prevGraphemeBytes(text, i);
  return i;
}
function nextWordBoundary(text, at) {
  const bytes = UTF8.encode(text);
  let i = Math.max(0, Math.min(at, bytes.length));
  const firstChar = () => Array.from(UTF8_DEC.decode(bytes.subarray(i)))[0] ?? "";
  while (i < bytes.length && /\s/.test(firstChar())) i += nextGraphemeBytes(text, i);
  while (i < bytes.length && firstChar() !== "" && !/\s/.test(firstChar()))
    i += nextGraphemeBytes(text, i);
  return i;
}
function ordered(sel) {
  const a = sel.anchor;
  const f = sel.focus;
  const cmp = a.blockOrd - f.blockOrd || a.byteOffset - f.byteOffset;
  return cmp <= 0 ? [a, f] : [f, a];
}
function blockText(bridge, blockOrd) {
  return bridge.blocks[blockOrd]?.text ?? "";
}
function deleteRange(bridge, start, end) {
  if (start.blockOrd === end.blockOrd) {
    const next = bridge.editText(
      start.blockOrd,
      start.byteOffset,
      end.byteOffset - start.byteOffset,
      ""
    );
    return { bridge: next, caret: { blockOrd: start.blockOrd, byteOffset: start.byteOffset } };
  }
  let b = bridge;
  b = b.editText(end.blockOrd, 0, end.byteOffset, "");
  const startLen = byteLen(blockText(b, start.blockOrd));
  b = b.editText(start.blockOrd, start.byteOffset, startLen - start.byteOffset, "");
  for (let m = start.blockOrd + 1; m < end.blockOrd; m++) {
    b = b.editText(m, 0, byteLen(blockText(b, m)), "");
  }
  const merges = end.blockOrd - start.blockOrd;
  for (let i = 0; i < merges; i++) b = b.merge(start.blockOrd + 1);
  return { bridge: b, caret: { blockOrd: start.blockOrd, byteOffset: start.byteOffset } };
}
function isDeleteIntent(intent) {
  return intent.type === "deleteBackward" || intent.type === "deleteForward" || intent.type === "deleteWordBackward" || intent.type === "deleteWordForward" || intent.type === "deleteToLineStart" || intent.type === "deleteToLineEnd";
}
function applyEdit(bridge, sel, intent, resolveLineEdge) {
  const [start, end] = ordered(sel);
  const ranged = !isCollapsed(sel);
  const noop = { bridge, caret: sel.focus, changed: false, declined: null };
  const refused = (verb, blockOrd, byteOffset) => ({
    bridge,
    caret: sel.focus,
    changed: false,
    declined: {
      verb,
      blockOrd,
      byteOffset,
      intent: intent.type,
      reason: bridge.routingRefusal?.(blockOrd) ?? "handle-declined"
    }
  });
  let b = bridge;
  let caret = start;
  if (ranged) {
    const del = deleteRange(bridge, start, end);
    b = del.bridge;
    caret = del.caret;
    if (isDeleteIntent(intent)) {
      return b === bridge ? refused("editText", start.blockOrd, start.byteOffset) : { bridge: b, caret, changed: true, declined: null };
    }
  }
  const rangeDeleted = ranged && b !== bridge ? { bridge: b, caret, changed: true, declined: null } : noop;
  const at = caret.byteOffset;
  const bo = caret.blockOrd;
  const text = blockText(b, bo);
  const len = byteLen(text);
  switch (intent.type) {
    case "insertText": {
      if (intent.text.length === 0) return rangeDeleted;
      const next = b.editText(bo, at, 0, intent.text);
      if (next === b) return ranged && b !== bridge ? rangeDeleted : refused("editText", bo, at);
      return {
        bridge: next,
        caret: { blockOrd: bo, byteOffset: at + byteLen(intent.text) },
        changed: true,
        declined: null
      };
    }
    case "insertLineBreak": {
      const next = b.editText(bo, at, 0, "\n");
      if (next === b) return ranged && b !== bridge ? rangeDeleted : refused("editText", bo, at);
      return {
        bridge: next,
        caret: { blockOrd: bo, byteOffset: at + 1 },
        changed: true,
        declined: null
      };
    }
    case "splitBlock": {
      const next = b.split(bo, at);
      if (next === b) return ranged && b !== bridge ? rangeDeleted : refused("split", bo, at);
      return {
        bridge: next,
        caret: { blockOrd: bo + 1, byteOffset: 0, affinity: "downstream" },
        changed: true,
        declined: null
      };
    }
    case "deleteBackward":
    case "deleteWordBackward": {
      if (at <= 0) return backwardBoundary();
      const del = intent.type === "deleteWordBackward" ? at - prevWordBoundary(text, at) : prevGraphemeBytes(text, at);
      if (del <= 0) return noop;
      const next = b.editText(bo, at - del, del, "");
      if (next === b) return refused("editText", bo, at);
      return {
        bridge: next,
        caret: { blockOrd: bo, byteOffset: at - del },
        changed: true,
        declined: null
      };
    }
    case "deleteForward":
    case "deleteWordForward": {
      if (at >= len) return forwardBoundary();
      const del = intent.type === "deleteWordForward" ? nextWordBoundary(text, at) - at : nextGraphemeBytes(text, at);
      if (del <= 0) return noop;
      const next = b.editText(bo, at, del, "");
      if (next === b) return refused("editText", bo, at);
      return {
        bridge: next,
        caret: { blockOrd: bo, byteOffset: at },
        changed: true,
        declined: null
      };
    }
    case "deleteToLineStart": {
      const edge = resolveLineEdge?.(caret, -1) ?? null;
      const from = edge != null && edge.blockOrd === bo ? Math.min(edge.byteOffset, at) : 0;
      if (from >= at) {
        return at <= 0 ? backwardBoundary() : noop;
      }
      const next = b.editText(bo, from, at - from, "");
      if (next === b) return refused("editText", bo, at);
      return {
        bridge: next,
        caret: { blockOrd: bo, byteOffset: from },
        changed: true,
        declined: null
      };
    }
    case "deleteToLineEnd": {
      const edge = resolveLineEdge?.(caret, 1) ?? null;
      const to = edge != null && edge.blockOrd === bo ? Math.max(edge.byteOffset, at) : len;
      if (to <= at) return at >= len ? forwardBoundary() : noop;
      const next = b.editText(bo, at, to - at, "");
      if (next === b) return refused("editText", bo, at);
      return {
        bridge: next,
        caret: { blockOrd: bo, byteOffset: at },
        changed: true,
        declined: null
      };
    }
  }
  function backwardBoundary() {
    if (bo > 0) {
      const joinByte = byteLen(blockText(b, bo - 1));
      const merged = b.merge(bo);
      if (merged !== b) {
        return {
          bridge: merged,
          caret: { blockOrd: bo - 1, byteOffset: joinByte },
          changed: true,
          declined: null
        };
      }
    }
    if (len === 0 && b.blocks.length > 1) {
      const gone = b.deleteBlock?.(bo) ?? b;
      if (gone !== b) {
        const caretPos = bo > 0 ? { blockOrd: bo - 1, byteOffset: byteLen(blockText(b, bo - 1)), affinity: "upstream" } : { blockOrd: 0, byteOffset: 0 };
        return { bridge: gone, caret: caretPos, changed: true, declined: null };
      }
    }
    if (bo > 0 && isAtomic(b, bo - 1)) {
      const gone = b.deleteBlock?.(bo - 1) ?? b;
      if (gone !== b) {
        return {
          bridge: gone,
          caret: { blockOrd: bo - 1, byteOffset: 0, affinity: "downstream" },
          changed: true,
          declined: null
        };
      }
    }
    if (bo > 0) return refused("merge", bo, at);
    return noop;
  }
  function forwardBoundary() {
    if (bo + 1 >= b.blocks.length) return noop;
    const stay = { blockOrd: bo, byteOffset: at };
    const merged = b.merge(bo + 1);
    if (merged !== b) return { bridge: merged, caret: stay, changed: true, declined: null };
    if (isAtomic(b, bo + 1)) {
      const gone = b.deleteBlock?.(bo + 1) ?? b;
      if (gone !== b) return { bridge: gone, caret: stay, changed: true, declined: null };
    }
    return refused("merge", bo + 1, 0);
  }
}
function isAtomic(bridge, blockOrd) {
  return bridge.routingRefusal?.(blockOrd) === "not-editable";
}
function moveCaretHorizontal(bridge, pos, dir) {
  const text = blockText(bridge, pos.blockOrd);
  const len = byteLen(text);
  if (dir < 0) {
    if (pos.byteOffset > 0) {
      const step = prevGraphemeBytes(text, pos.byteOffset);
      return { blockOrd: pos.blockOrd, byteOffset: pos.byteOffset - step, affinity: "downstream" };
    }
    if (pos.blockOrd > 0) {
      const prevLen = byteLen(blockText(bridge, pos.blockOrd - 1));
      return { blockOrd: pos.blockOrd - 1, byteOffset: prevLen, affinity: "upstream" };
    }
    return pos;
  }
  if (pos.byteOffset < len) {
    const step = nextGraphemeBytes(text, pos.byteOffset);
    return { blockOrd: pos.blockOrd, byteOffset: pos.byteOffset + step, affinity: "downstream" };
  }
  if (pos.blockOrd + 1 < bridge.blocks.length) {
    return { blockOrd: pos.blockOrd + 1, byteOffset: 0, affinity: "downstream" };
  }
  return pos;
}
function moveCaretWord(bridge, pos, dir) {
  const text = blockText(bridge, pos.blockOrd);
  if (dir < 0) {
    if (pos.byteOffset > 0) {
      return {
        blockOrd: pos.blockOrd,
        byteOffset: prevWordBoundary(text, pos.byteOffset),
        affinity: "downstream"
      };
    }
    return moveCaretHorizontal(bridge, pos, -1);
  }
  if (pos.byteOffset < byteLen(text)) {
    return {
      blockOrd: pos.blockOrd,
      byteOffset: nextWordBoundary(text, pos.byteOffset),
      affinity: "downstream"
    };
  }
  return moveCaretHorizontal(bridge, pos, 1);
}
var idCounter = 0;
function freshId() {
  idCounter += 1;
  return `pl-block-${idCounter}`;
}
var PlainTextEditBridge = class _PlainTextEditBridge {
  constructor(blocks) {
    this.blocks = blocks;
  }
  blocks;
  with(blocks) {
    return new _PlainTextEditBridge(blocks);
  }
  editText(blockOrd, at, deleteBytes, insert) {
    const block = this.blocks[blockOrd];
    if (!block) return this;
    const next = { ...block, text: spliceBytes(block.text, at, deleteBytes, insert) };
    const arr = this.blocks.slice();
    arr[blockOrd] = next;
    return this.with(arr);
  }
  split(blockOrd, atByte) {
    const block = this.blocks[blockOrd];
    if (!block || block.kind === "html") return this;
    const bytes = UTF8.encode(block.text);
    const a = Math.max(0, Math.min(atByte, bytes.length));
    const head = { ...block, text: UTF8_DEC.decode(bytes.subarray(0, a)) };
    const tail = {
      id: freshId(),
      text: UTF8_DEC.decode(bytes.subarray(a)),
      kind: "paragraph"
    };
    const arr = this.blocks.slice();
    arr.splice(blockOrd, 1, head, tail);
    return this.with(arr);
  }
  merge(blockOrd) {
    if (blockOrd <= 0 || blockOrd >= this.blocks.length) return this;
    const prev = this.blocks[blockOrd - 1];
    const cur = this.blocks[blockOrd];
    if (!prev || !cur || prev.kind === "html" || cur.kind === "html") return this;
    const merged = { ...prev, text: prev.text + cur.text };
    const arr = this.blocks.slice();
    arr.splice(blockOrd - 1, 2, merged);
    return this.with(arr);
  }
  /** Remove the block wholesale. Mirrors `DoczHandle.deleteBlock`'s refusals so
   *  the reference bridge and the production one behave the same at the two
   *  boundaries that matter: the document's LAST block survives (a document with
   *  no block has no caret), and an opaque `html` island is left alone (the same
   *  markup protection `editText`/`split`/`merge` give it). */
  deleteBlock(blockOrd) {
    if (blockOrd < 0 || blockOrd >= this.blocks.length) return this;
    if (this.blocks.length <= 1) return this;
    if (this.blocks[blockOrd]?.kind === "html") return this;
    const arr = this.blocks.slice();
    arr.splice(blockOrd, 1);
    return this.with(arr);
  }
};

// src/ord-pairing.ts
var UTF82 = new TextEncoder();
function blankLineAt(text, at) {
  const bytes = UTF82.encode(text);
  const i = Math.max(0, Math.min(at, bytes.length));
  const NL = 10;
  let from = i;
  while (from > 0 && bytes[from - 1] !== NL) from--;
  let to = i;
  while (to < bytes.length && bytes[to] !== NL) to++;
  return sliceBytes(text, from, to).trim().length === 0;
}
function collapseWhitespace(s) {
  return s.replace(/\s+/gu, " ");
}
function runMatchesBlockText(view, runIdx, text) {
  const run = view.run(runIdx);
  if (run.runKind === RunKind.emptyLine) return blankLineAt(text, run.byteStart);
  const painted = view.runText(run);
  const slice = sliceBytes(text, run.byteStart, run.byteEnd);
  if (slice === painted) return true;
  const trimmed = painted.trimEnd();
  if (trimmed.length === 0) return false;
  if (slice.trimEnd() === trimmed) return true;
  const paintedNorm = collapseWhitespace(painted).trimEnd();
  if (paintedNorm.length === 0) return false;
  const tailNorm = collapseWhitespace(sliceBytes(text, run.byteStart, byteLen(text)));
  return tailNorm.startsWith(paintedNorm);
}
function linkVerifies(view, runsByOrd, ord, blocks, docIndex) {
  if (docIndex === void 0) return false;
  const block = blocks[docIndex];
  if (!block) return false;
  const runs = runsByOrd.get(ord);
  if (!runs || runs.length === 0) return false;
  for (const r of runs) if (!runMatchesBlockText(view, r, block.text)) return false;
  return true;
}
function proposeTargets(view, runsByOrd, ords, blocks, candidates) {
  for (const targets of candidates) {
    if (targets.length !== ords.length) continue;
    let ok = true;
    for (let k = 0; k < ords.length; k++) {
      if (!linkVerifies(view, runsByOrd, ords[k], blocks, targets[k])) {
        ok = false;
        break;
      }
    }
    if (ok) return targets;
  }
  return null;
}
function alignVerified(view, runsByOrd, ords, blocks, candidates) {
  const out = [];
  let cursor = 0;
  for (const ord of ords) {
    let hit;
    for (let j = cursor; j < candidates.length; j++) {
      if (linkVerifies(view, runsByOrd, ord, blocks, candidates[j])) {
        hit = candidates[j];
        cursor = j + 1;
        break;
      }
    }
    out.push(hit);
  }
  return out;
}

// src/block-map.ts
function isSingleParagraphQuote(block) {
  return block.kind === "blockquote" && !block.text.includes("\n");
}
var NON_PARAGRAPH_SOURCE_KINDS = /* @__PURE__ */ new Set([
  "table",
  "image",
  "imageSvg",
  "horizontalRule",
  "code",
  "rawHtml",
  "rawOoxml",
  "rawSvg",
  "unknown"
]);
function isParagraphOrdKind(block) {
  if (block.sourceKind !== void 0 && NON_PARAGRAPH_SOURCE_KINDS.has(block.sourceKind)) {
    return false;
  }
  return block.kind === void 0 || block.kind === "paragraph" || isSingleParagraphQuote(block);
}
function isHeadingOrdKind(block) {
  return block.kind === "heading";
}
function isListItemOrdKind(block) {
  return block.kind === "list_item";
}
function isTableCellOrdKind(block) {
  return block.kind === "table_cell";
}
function isCanvasAddressableKind(block) {
  return isParagraphOrdKind(block) || isHeadingOrdKind(block) || isListItemOrdKind(block) || isTableCellOrdKind(block);
}
var AUX_NAMESPACES = [
  { runKind: RunKind.heading, label: "heading", matches: isHeadingOrdKind },
  { runKind: RunKind.listItem, label: "list_item", matches: isListItemOrdKind },
  { runKind: RunKind.tableCell, label: "table_cell", matches: isTableCellOrdKind }
];
function stampsGeometry(block) {
  if (isCanvasAddressableKind(block)) return true;
  return /\S/u.test(block.text);
}
function docSides(blocks, pred) {
  const all = [];
  const nonEmpty = [];
  const inked = [];
  for (let i = 0; i < blocks.length; i++) {
    const b = blocks[i];
    if (!b || !pred(b)) continue;
    all.push(i);
    if (b.text.length > 0) nonEmpty.push(i);
    if (stampsGeometry(b)) inked.push(i);
  }
  return { all, nonEmpty, inked };
}
function collectOrds(view) {
  const para = /* @__PURE__ */ new Set();
  const aux = /* @__PURE__ */ new Map();
  const runsByOrd = /* @__PURE__ */ new Map();
  for (let i = 0; i < view.runCount; i++) {
    const run = view.run(i);
    if (run.blockOrd === BLOCK_ORD_NONE || !isAddressable(run)) continue;
    const evidence = runsByOrd.get(run.blockOrd);
    if (evidence) evidence.push(i);
    else runsByOrd.set(run.blockOrd, [i]);
    if (!isAuxBlockOrd(run.blockOrd)) {
      para.add(run.blockOrd);
      continue;
    }
    const namespaceKind = namespaceKindOf(run);
    let bucket = aux.get(namespaceKind);
    if (!bucket) {
      bucket = /* @__PURE__ */ new Set();
      aux.set(namespaceKind, bucket);
    }
    bucket.add(run.blockOrd);
  }
  const auxSorted = /* @__PURE__ */ new Map();
  for (const [kind, set] of aux)
    auxSorted.set(
      kind,
      [...set].sort((a, b) => (a >>> 0) - (b >>> 0))
    );
  return { para: [...para].sort((a, b) => a - b), aux: auxSorted, runsByOrd };
}
function buildBlockMap(view, blocks) {
  const ords = collectOrds(view);
  const geomToDoc = /* @__PURE__ */ new Map();
  const docToGeom = /* @__PURE__ */ new Map();
  const link = (g, d) => {
    geomToDoc.set(g, d);
    docToGeom.set(d, g);
  };
  const para = docSides(blocks, isParagraphOrdKind);
  const paraTargets = proposeTargets(view, ords.runsByOrd, ords.para, blocks, [
    para.inked,
    para.nonEmpty,
    para.all
  ]);
  if (paraTargets !== null) {
    for (let k = 0; k < ords.para.length; k++)
      link(ords.para[k], paraTargets[k]);
  } else {
    const aligned = alignVerified(view, ords.runsByOrd, ords.para, blocks, para.all);
    for (let k = 0; k < ords.para.length; k++) {
      const d = aligned[k];
      if (d === void 0) continue;
      link(ords.para[k], d);
    }
  }
  const degraded = ords.para.some((g) => !geomToDoc.has(g));
  const unmappedKinds = [];
  const auxPaired = /* @__PURE__ */ new Set();
  for (const ns of AUX_NAMESPACES) {
    const sorted = ords.aux.get(ns.runKind) ?? [];
    if (sorted.length === 0) continue;
    const doc = docSides(blocks, ns.matches);
    const targets = proposeTargets(view, ords.runsByOrd, sorted, blocks, [
      doc.inked,
      doc.nonEmpty,
      doc.all
    ]);
    const resolved = targets ?? alignVerified(view, ords.runsByOrd, sorted, blocks, doc.all);
    if (targets === null) unmappedKinds.push(ns.label);
    for (let k = 0; k < sorted.length; k++) {
      const d = resolved[k];
      if (d === void 0) continue;
      link(sorted[k], d);
      auxPaired.add(d);
    }
  }
  const addressableDocIndices = [];
  for (let i = 0; i < blocks.length; i++) {
    const b = blocks[i];
    if (!b) continue;
    if (degraded && !docToGeom.has(i)) continue;
    if (isParagraphOrdKind(b) || auxPaired.has(i)) addressableDocIndices.push(i);
  }
  return {
    geomToDoc: (g) => geomToDoc.get(g),
    docToGeom: (d) => docToGeom.get(d),
    addressableDocIndices,
    degraded,
    unmappedKinds,
    headingsUnmapped: unmappedKinds.includes("heading")
  };
}

// src/caret-overlay.ts
var PT_PER_IN = 72;
var CSS_PX_PER_IN = 96;
var DEFAULT_OVERLAY_COLORS = {
  caret: "var(--pl-caret-color, currentColor)",
  selection: "var(--pl-selection-color, rgba(37, 99, 235, 0.28))",
  highlight: "var(--pl-highlight-color, rgba(245, 158, 11, 0.30))",
  preedit: "var(--pl-preedit-color, currentColor)",
  focusRing: "var(--pl-focus-ring-color, #2563eb)"
};
var DEFAULT_HIGHLIGHT = "rgba(245, 158, 11, 0.30)";
var DEFAULT_SELECTION = "rgba(37, 99, 235, 0.28)";
var DEFAULT_FOCUS_RING = "#2563eb";
var DEFAULT_INK = "#000000";
function isCanvasPaintable(color) {
  const s = (color ?? "").trim();
  if (s.length === 0) return false;
  if (/^(var|color-mix)\s*\(/i.test(s)) return false;
  return s.toLowerCase() !== "currentcolor";
}
function varParts(s) {
  if (!/^var\s*\(/i.test(s.trim())) return null;
  const open = s.indexOf("(");
  const inner = s.slice(open + 1, s.lastIndexOf(")"));
  let depth = 0;
  for (let i = 0; i < inner.length; i++) {
    const c = inner[i];
    if (c === "(") depth++;
    else if (c === ")") depth--;
    else if (c === "," && depth === 0) {
      return { name: inner.slice(0, i).trim(), fallback: inner.slice(i + 1).trim() };
    }
  }
  return { name: inner.trim(), fallback: null };
}
function resolveOverlayColor(canvas, color, fallback) {
  if (isCanvasPaintable(color)) return color;
  const parts = varParts(color ?? "");
  if (parts) {
    const fromTheme = computedVar(canvas, parts.name);
    if (isCanvasPaintable(fromTheme)) return fromTheme;
    if (parts.fallback !== null) return resolveOverlayColor(canvas, parts.fallback, fallback);
  }
  return fallback;
}
function computedVar(canvas, name) {
  if (!name.startsWith("--")) return void 0;
  const view = canvas.ownerDocument?.defaultView;
  if (typeof view?.getComputedStyle !== "function") return void 0;
  try {
    return view.getComputedStyle(canvas).getPropertyValue?.(name)?.trim() || void 0;
  } catch {
    return void 0;
  }
}
var HIGHLIGHT_INFLATE_PT = 1.5;
var HIGHLIGHT_RADIUS_PT = 1.5;
var HIGHLIGHT_BLEND = "var(--pl-highlight-blend, multiply)";
var HIGHLIGHT_BLEND_VAR = "--pl-highlight-blend";
var PREEDIT_UNDERLINE_PT = 1;
function resolve(opts) {
  const zoom = opts?.zoom ?? 1;
  const dpr = opts?.dpr ?? (globalThis.devicePixelRatio || 1);
  return {
    backing: Math.min(dpr, opts?.maxDpr ?? 2),
    cssScale: CSS_PX_PER_IN / PT_PER_IN * zoom,
    colors: opts?.colors ?? DEFAULT_OVERLAY_COLORS
  };
}
function rectsForPage(rects, pageIndex) {
  return rects.filter((r) => r.pageIndex === pageIndex);
}
function caretRectToCss(rect, pageOriginCss, opts) {
  const { cssScale } = resolve(opts);
  return {
    leftCss: pageOriginCss.leftCss + rect.xPt * cssScale,
    topCss: pageOriginCss.topCss + rect.yPt * cssScale,
    heightCss: rect.hPt * cssScale
  };
}
function sizeOverlay(canvas, widthPt, heightPt, opts) {
  const r = resolve(opts);
  const { yFromPt, spanPt, full } = bandOf(opts, heightPt);
  const cssW = widthPt * r.cssScale;
  const cssH = spanPt * r.cssScale;
  canvas.style.width = `${cssW}px`;
  canvas.style.height = `${cssH}px`;
  canvas.width = Math.round(cssW * r.backing);
  canvas.height = Math.round(cssH * r.backing);
  const ctx = canvas.getContext("2d");
  if (!ctx) return null;
  const k = r.backing * r.cssScale;
  ctx.setTransform(k, 0, 0, k, 0, full ? 0 : -yFromPt * k);
  return ctx;
}
function bandOf(opts, heightPt) {
  const b = opts?.band;
  if (!b) return { yFromPt: 0, spanPt: heightPt, full: true };
  const yFromPt = Math.max(0, Math.min(b.yFromPt, heightPt));
  const yToPt = Math.max(yFromPt, Math.min(b.yToPt, heightPt));
  return { yFromPt, spanPt: yToPt - yFromPt, full: yFromPt <= 0 && yToPt >= heightPt };
}
function paintSelection(canvas, pageIndex, widthPt, heightPt, rects, opts) {
  const ctx = sizeOverlay(canvas, widthPt, heightPt, opts);
  if (!ctx) return;
  const r = resolve(opts);
  const strip = bandOf(opts, heightPt);
  ctx.clearRect(0, strip.yFromPt, widthPt, strip.spanPt);
  ctx.fillStyle = resolveOverlayColor(canvas, r.colors.selection, DEFAULT_SELECTION);
  for (const rect of rectsForPage(rects, pageIndex)) {
    ctx.fillRect(rect.xPt, rect.yPt, Math.max(rect.wPt, 1), rect.hPt);
  }
}
function inflatedBand(rect, byPt) {
  return {
    x: rect.xPt - byPt,
    y: rect.yPt - byPt,
    w: Math.max(rect.wPt, 1) + byPt * 2,
    h: rect.hPt + byPt * 2
  };
}
function fillBandsOnce(ctx, rects, inflatePt = HIGHLIGHT_INFLATE_PT, radiusPt = HIGHLIGHT_RADIUS_PT) {
  if (rects.length === 0) return;
  const p = ctx;
  const canRound = typeof p.roundRect === "function";
  const canRect = typeof p.rect === "function";
  if (typeof p.beginPath !== "function" || typeof p.fill !== "function" || !(canRound || canRect)) {
    for (const rect of rects) {
      const b = inflatedBand(rect, inflatePt);
      ctx.fillRect(b.x, b.y, b.w, b.h);
    }
    return;
  }
  p.beginPath();
  for (const rect of rects) {
    const b = inflatedBand(rect, inflatePt);
    if (canRound) {
      p.roundRect?.(b.x, b.y, b.w, b.h, Math.min(radiusPt, b.w / 2, b.h / 2));
    } else {
      p.rect?.(b.x, b.y, b.w, b.h);
    }
  }
  p.fill();
}
function paintHighlights(canvas, pageIndex, widthPt, heightPt, rects, opts, flash) {
  const ctx = sizeOverlay(canvas, widthPt, heightPt, opts);
  if (!ctx) return;
  const r = resolve(opts);
  const strip = bandOf(opts, heightPt);
  ctx.clearRect(0, strip.yFromPt, widthPt, strip.spanPt);
  const fallback = resolveOverlayColor(canvas, r.colors.highlight, DEFAULT_HIGHLIGHT);
  const groups = /* @__PURE__ */ new Map();
  for (const rect of rectsForPage(rects, pageIndex)) {
    const color = resolveOverlayColor(canvas, rect.color, fallback);
    const key = rect.group !== void 0 ? `g:${rect.group}` : `c:${color}`;
    const existing = groups.get(key);
    if (existing) existing.rects.push(rect);
    else groups.set(key, { color, rects: [rect] });
  }
  for (const group of groups.values()) {
    ctx.fillStyle = group.color;
    fillBandsOnce(ctx, group.rects);
  }
  if (!flash) return;
  const alpha = Math.max(0, Math.min(1, flash.alpha));
  if (alpha <= 0) return;
  const prevAlpha = ctx.globalAlpha;
  ctx.globalAlpha = alpha;
  ctx.fillStyle = resolveOverlayColor(canvas, flash.color, fallback);
  fillBandsOnce(ctx, rectsForPage(flash.rects, pageIndex));
  ctx.globalAlpha = prevAlpha;
}
var FOCUS_RING_WIDTH_PT = 1.5;
function focusRingEdges(band, widthPt = FOCUS_RING_WIDTH_PT) {
  const w = Math.max(widthPt, 0.25);
  const x = band.xPt - w;
  const y = band.yPt - w;
  const outerW = Math.max(band.wPt, 1) + w * 2;
  const outerH = band.hPt + w * 2;
  const p = band.pageIndex;
  return [
    { pageIndex: p, xPt: x, yPt: y, wPt: outerW, hPt: w },
    // top
    { pageIndex: p, xPt: x, yPt: y + outerH - w, wPt: outerW, hPt: w },
    // bottom
    { pageIndex: p, xPt: x, yPt: y + w, wPt: w, hPt: outerH - w * 2 },
    // left
    {
      pageIndex: p,
      xPt: x + outerW - w,
      yPt: y + w,
      wPt: w,
      hPt: outerH - w * 2
    }
    // right
  ];
}
function paintFocusRing(canvas, pageIndex, widthPt, heightPt, rects, opts) {
  const ctx = sizeOverlay(canvas, widthPt, heightPt, opts);
  if (!ctx) return;
  const r = resolve(opts);
  const strip = bandOf(opts, heightPt);
  ctx.clearRect(0, strip.yFromPt, widthPt, strip.spanPt);
  ctx.fillStyle = resolveOverlayColor(canvas, r.colors.focusRing, DEFAULT_FOCUS_RING);
  for (const band of rectsForPage(rects, pageIndex)) {
    for (const edge of focusRingEdges(band)) {
      ctx.fillRect(edge.xPt, edge.yPt, edge.wPt, edge.hPt);
    }
  }
}
function paintPreedit(canvas, pageIndex, widthPt, heightPt, rects, opts) {
  const ctx = sizeOverlay(canvas, widthPt, heightPt, opts);
  if (!ctx) return;
  const r = resolve(opts);
  const strip = bandOf(opts, heightPt);
  ctx.clearRect(0, strip.yFromPt, widthPt, strip.spanPt);
  ctx.fillStyle = resolveOverlayColor(canvas, r.colors.preedit, DEFAULT_INK);
  for (const rect of rectsForPage(rects, pageIndex)) {
    ctx.fillRect(
      rect.xPt,
      rect.yPt + rect.hPt - PREEDIT_UNDERLINE_PT,
      Math.max(rect.wPt, 1),
      PREEDIT_UNDERLINE_PT
    );
  }
}
function paintCaret(canvas, pageIndex, widthPt, heightPt, caret, visible, opts) {
  const ctx = sizeOverlay(canvas, widthPt, heightPt, opts);
  if (!ctx) return;
  const r = resolve(opts);
  const strip = bandOf(opts, heightPt);
  ctx.clearRect(0, strip.yFromPt, widthPt, strip.spanPt);
  if (!visible || !caret || caret.pageIndex !== pageIndex) return;
  ctx.fillStyle = resolveOverlayColor(canvas, r.colors.caret, DEFAULT_INK);
  ctx.fillRect(caret.xPt, caret.yPt, Math.max(caret.wPt, 1), caret.hPt);
}
var CaretBlink = class {
  constructor(onToggle, intervalMs = 530) {
    this.onToggle = onToggle;
    this.intervalMs = intervalMs;
  }
  onToggle;
  intervalMs;
  timer = null;
  visible = true;
  get isVisible() {
    return this.visible;
  }
  restart() {
    this.stop();
    this.visible = true;
    this.onToggle(true);
    if (typeof setInterval !== "function") return;
    this.timer = setInterval(() => {
      this.visible = !this.visible;
      this.onToggle(this.visible);
    }, this.intervalMs);
  }
  stop() {
    if (this.timer != null) {
      clearInterval(this.timer);
      this.timer = null;
    }
  }
};
var TypingRun = class {
  anchor = null;
  end = null;
  /** The stable coalesce key for `intent` at `selection`, or `null` when this
   *  edit must start a fresh undo step (which also ends the run). */
  keyFor(intent, selection) {
    if (intent.type !== "insertText" || !isCollapsed(selection) || !isCoalescibleText(intent.text)) {
      this.reset();
      return null;
    }
    const focus = selection.focus;
    const continues = this.anchor !== null && this.end !== null && this.end.blockOrd === focus.blockOrd && this.end.byteOffset === focus.byteOffset;
    if (!continues) this.anchor = focus;
    const anchor = this.anchor;
    return `insertText:${anchor.blockOrd}:${anchor.byteOffset}`;
  }
  /** Record where the caret landed after a coalescible keystroke, so the NEXT
   *  keystroke can tell whether it continues the run. */
  advance(caret) {
    this.end = caret;
  }
  /** End the run (caret motion, selection change, undo/redo, a format op). */
  reset() {
    this.anchor = null;
    this.end = null;
  }
};

// src/editor-core.ts
var EditorCore = class {
  bridge;
  selection;
  layout;
  goalXPt = null;
  /** The live IME PRE-EDIT overlay, or `null`. `baseline` is the COMMITTED state
   *  the provisional text is laid over; every update rebuilds from it, and
   *  dropping the pre-edit restores it by identity. */
  preedit = null;
  /** THE undo/redo ring — the shared upstream {@link SnapshotHistory} over this
   *  surface's snapshot shape, NEVER a second local ring (RULE ZERO). */
  history;
  /** The Word-parity typing-run policy that produces the coalesce keys. */
  run = new TypingRun();
  relayout;
  metrics;
  onChange;
  onDecline;
  onPointRefused;
  constructor(opts) {
    this.bridge = opts.bridge;
    this.relayout = opts.relayout;
    this.metrics = opts.metrics;
    this.history = new SnapshotHistory({
      limit: opts.historyLimit ?? DEFAULT_HISTORY_LIMIT,
      ...opts.coalesceWindowMs !== void 0 ? { coalesceWindowMs: opts.coalesceWindowMs } : {},
      ...opts.now !== void 0 ? { now: opts.now } : {}
    });
    this.onChange = opts.onChange;
    this.onDecline = opts.onDecline;
    this.onPointRefused = opts.onPointRefused;
    this.layout = this.relayout(opts.bridge.blocks, opts.bridge);
    const start = docStart(this.layout.index) ?? { blockOrd: 0, byteOffset: 0 };
    this.selection = collapsed(start);
  }
  getState() {
    return { bridge: this.bridge, selection: this.selection, layout: this.layout };
  }
  get view() {
    return this.layout.view;
  }
  get index() {
    return this.layout.index;
  }
  get focus() {
    return this.selection.focus;
  }
  /** The caret rectangle for the current focus (or `null` if unaddressable, e.g.
   *  an empty block — the host falls back). */
  caretRect() {
    return caretRect(this.layout.view, this.layout.index, this.selection.focus, this.metrics);
  }
  /**
   * Selection rectangles for the current range (empty when collapsed).
   *
   * A TABLE CELL the selection covers entirely paints as its whole CELL BOX
   * (`SelectionRectOptions.cellBoxes`), not as its text lines. Without that an
   * ALL-EMPTY table — every cell a zero-width line box, which is exactly what
   * `insert.table` produces — selects to ZERO painted rects, and the reader
   * cannot tell their click did anything. The comment-anchor seam
   * ({@link rangeRects}) deliberately does NOT opt in.
   */
  selectionRects() {
    return selectionRects(
      this.layout.view,
      this.layout.index,
      this.selection.anchor,
      this.selection.focus,
      this.metrics,
      { cellBoxes: true }
    );
  }
  /**
   * Selection rectangles for an ARBITRARY byte-offset range (not the live
   * caret) — the comment-anchor highlight seam. A host resolves a stored anchor
   * selector to `(blockOrd, byteStart..byteEnd)` and asks the CURRENT geometry
   * for its page-space rects, then paints them on the highlight overlay. Empty
   * when the range is collapsed or unaddressable in the current layout. Byte
   * offsets are stable across relayout, so the host recomputes the rects each
   * repaint and the highlight tracks reflow verbatim.
   */
  rangeRects(from, to) {
    return selectionRects(this.layout.view, this.layout.index, from, to, this.metrics);
  }
  /**
   * The live selection's PER-BLOCK slices, in document order — the coordinate a
   * cross-block act needs. `[]` when collapsed.
   *
   * A comment placed over several paragraphs applies its Mark to each block's
   * OWN sub-range; a joined string cannot say where those boundaries fall, which
   * is why this and not {@link selectedText} is the anchoring seam.
   */
  selectionSlices() {
    return rangeSlices(this.bridge.blocks, this.selection.anchor, this.selection.focus);
  }
  /** The plain text currently selected (for clipboard copy/cut). Empty when
   *  collapsed. Cross-block ranges join block texts with `\n`. */
  selectedText() {
    return rangeText(this.bridge.blocks, this.selection.anchor, this.selection.focus);
  }
  // ─── IME pre-edit (provisional, NEVER committed) ──────────────────────────────
  /**
   * Show the IME's PRE-EDIT (provisional) text at the caret without committing
   * anything.
   *
   * A `<canvas>` paints nothing on its own, so before this a CJK/Indic/dead-key
   * user typed into a document that showed them absolutely nothing until the
   * composition committed. The provisional text is laid out through the SAME
   * `applyEdit` reducer and the SAME `relayout` the commit will use, so what the
   * user sees while composing is byte-for-byte what they will get — including the
   * reflow, the page break it may push, and the caret sitting after it.
   *
   * It is an OVERLAY, not an edit: the committed `(bridge, selection)` are stashed
   * on the first call and restored by identity on {@link clearPreedit}, no undo
   * snapshot is pushed, and each update rebuilds from the stash (an IME rewrites
   * its whole buffer every keystroke, so appending would double the text). Passing
   * `''` drops the overlay.
   */
  setPreedit(text) {
    const baseline = this.preedit?.baseline ?? this.snapshot();
    if (text.length === 0) {
      this.clearPreedit();
      return;
    }
    const res = applyEdit(baseline.bridge, baseline.selection, { type: "insertText", text });
    if (!res.changed) {
      this.clearPreedit();
      return;
    }
    this.preedit = { baseline, text };
    this.bridge = res.bridge;
    this.layout = this.relayout(res.bridge.blocks, res.bridge);
    this.selection = collapsed(res.caret);
    this.goalXPt = null;
    this.emit();
  }
  /** Drop the provisional text and restore the committed document verbatim. */
  clearPreedit() {
    if (this.discardPreedit()) this.emit();
  }
  /** The provisional text currently painted (`''` when not composing). */
  get preeditText() {
    return this.preedit?.text ?? "";
  }
  /**
   * Page-space rects covering the provisional text — what a host underlines so the
   * user can tell an un-committed IME buffer from committed document text. Empty
   * when nothing is composing.
   */
  preeditRects() {
    const p = this.preedit;
    if (!p) return [];
    const { anchor, focus } = p.baseline.selection;
    const start = comparePos(anchor, focus) <= 0 ? anchor : focus;
    return selectionRects(
      this.layout.view,
      this.layout.index,
      start,
      this.selection.focus,
      this.metrics
    );
  }
  /** Restore the committed baseline WITHOUT emitting. `true` when it did anything.
   *  Every mutating entry point calls this first, so a stray edit, undo or bridge
   *  swap arriving mid-composition can never bake the provisional text in. */
  discardPreedit() {
    const p = this.preedit;
    if (!p) return false;
    this.preedit = null;
    this.bridge = p.baseline.bridge;
    this.layout = this.relayout(p.baseline.bridge.blocks, p.baseline.bridge);
    this.selection = p.baseline.selection;
    this.goalXPt = null;
    return true;
  }
  // ─── edits ───────────────────────────────────────────────────────────────────
  /**
   * The VISUAL line edge of a caret position — the geometry answer the pure
   * reducer cannot produce and must not guess.
   *
   * ⌘⌫ / ⌃K delete to the edge of the line the reader SEES, and a soft wrap is a
   * property of the layout: the same paragraph wraps in three places at one
   * column width and five at another. `edit-model.ts` holds no geometry, so this
   * core — which does — passes the answer down ({@link LineEdgeResolver}).
   *
   * It resolves through `geometry.lineStart`/`lineEnd`, which is the SAME
   * resolution `NavCommand`'s `unit: 'lineEdge'` (Home / End) already takes, so
   * the two keys agree on where a line begins by construction rather than by
   * coincidence. Re-deriving it here — a `\n` scan, "the block" — would give ⌘⌫ a
   * meaning that disagrees with the pixels and with Home in the same paragraph.
   */
  lineEdge = (pos, dir) => dir < 0 ? lineStart(this.layout.index, pos) : lineEnd(this.layout.index, pos);
  applyIntent(intent) {
    this.discardPreedit();
    const res = applyEdit(this.bridge, this.selection, intent, this.lineEdge);
    if (!res.changed) {
      if (res.declined) this.onDecline?.(res.declined, this);
      return;
    }
    const key = this.run.keyFor(intent, this.selection);
    this.pushUndo(key);
    this.bridge = res.bridge;
    this.layout = this.relayout(res.bridge.blocks, res.bridge);
    this.selection = collapsed(res.caret);
    if (key !== null) this.run.advance(res.caret);
    this.goalXPt = null;
    this.emit();
  }
  /**
   * Re-run layout for the CURRENT blocks (NO text change) and repaint — the
   * formatting seam. A mark toggle (bold/italic/color) does not change any block's
   * plain text, so it is invisible to {@link applyIntent}; the marks it changes
   * live in the injected {@link RelayoutFn}'s fragment provider. Calling
   * `refresh()` re-encodes the blocks THROUGH that provider, so the new marks
   * reach `pl_layout` and the painted weight/colour update. Byte offsets are
   * stable across relayout, so the selection is preserved verbatim.
   *
   * DOES NOT push an undo snapshot. It snapshots the UNCHANGED bridge, so the
   * entry could restore nothing — undoing a format op through `refresh()` was a
   * guaranteed silent no-op that still CONSUMED an undo step. The real undoable
   * formatting seam is {@link applyBridge}, which installs a new (mark-carrying)
   * document as one restore point.
   */
  refresh() {
    this.discardPreedit();
    this.layout = this.relayout(this.bridge.blocks, this.bridge);
    this.goalXPt = null;
    this.emit();
  }
  /**
   * Install a NEW document ({@link EditBridge}) as ONE undoable step — the real
   * formatting / paste / structural-replace seam. A consumer that produced a new
   * mark-carrying `DoczHandle` (e.g. via `DoczHandle.withBlockRuns`) wraps it in a
   * bridge and installs it here; undo restores both the previous bridge AND the
   * previous selection. `opts.selection` places the caret after the swap.
   */
  applyBridge(next, opts = {}) {
    this.discardPreedit();
    this.pushUndo(null);
    this.bridge = next;
    this.layout = this.relayout(next.blocks, next);
    if (opts.selection) this.selection = opts.selection;
    this.goalXPt = null;
    this.emit();
  }
  /**
   * Apply a byte-offset text edit to block `blockOrd` OUT-OF-BAND (not at the
   * caret) — the programmatic edit seam (`applyTextEdit` / an agent proposal /
   * paste-into-block). Splices `[at, at+deleteBytes)` → `insert` through the
   * {@link EditBridge} (so a docz bridge preserves comment marks in `docz.wasm`),
   * relayouts, and repaints. The caret is left where it is (byte offsets are
   * stable across the relayout). No-op when the bridge declines (unknown block).
   */
  editBlockAt(blockOrd, at, deleteBytes, insert) {
    this.discardPreedit();
    const next = this.bridge.editText(blockOrd, at, deleteBytes, insert);
    if (next === this.bridge) return;
    this.run.reset();
    this.pushUndo(null);
    this.bridge = next;
    this.layout = this.relayout(next.blocks, next);
    this.goalXPt = null;
    this.emit();
  }
  // ─── navigation ────────────────────────────────────────────────────────────────
  applyNav(cmd) {
    this.discardPreedit();
    this.breakRun();
    if (cmd.type === "selectAll") {
      this.selectAll();
      return;
    }
    if (cmd.unit === "line") {
      const goal = this.goalXPt ?? this.caretRect()?.xPt ?? 0;
      const next2 = moveVertical(
        this.layout.view,
        this.layout.index,
        this.selection.focus,
        cmd.dir,
        goal
      );
      if (!next2) return;
      this.selection = { anchor: cmd.extend ? this.selection.anchor : next2, focus: next2 };
      this.goalXPt = goal;
      this.emit();
      return;
    }
    const next = this.resolveNav(cmd);
    if (!next) return;
    this.setFocus(next, cmd.extend);
  }
  resolveNav(cmd) {
    const focus = this.selection.focus;
    switch (cmd.unit) {
      case "char":
        return moveCaretHorizontal(this.bridge, focus, cmd.dir);
      case "word":
        return moveCaretWord(this.bridge, focus, cmd.dir);
      case "lineEdge":
        return cmd.dir < 0 ? lineStart(this.layout.index, focus) : lineEnd(this.layout.index, focus);
      case "doc":
        return cmd.dir < 0 ? docStart(this.layout.index) : docEnd(this.layout.index);
      case "line":
        return null;
    }
  }
  /** Move the focus (caret). `extend` keeps the anchor (grow selection); else
   *  collapse. Resets the vertical goal column (horizontal / edge / click moves). */
  setFocus(focus, extend) {
    this.discardPreedit();
    this.breakRun();
    const anchor = extend ? this.selection.anchor : focus;
    this.selection = { anchor, focus };
    this.goalXPt = null;
    this.emit();
  }
  // ─── pointer ───────────────────────────────────────────────────────────────────
  /** Click-to-place / drag: hit-test a canvas point (page-relative points) and
   *  set the caret. `extend` (shift-click / drag) keeps the anchor. Returns the
   *  resolved position, or `null` if the point hit no addressable geometry. */
  pointAt(pageIndex, xPt, yPt, extend = false) {
    const pos = hitTest(this.layout.view, this.layout.index, pageIndex, xPt, yPt);
    if (!pos) {
      const dz = this.deadZoneAt(pageIndex, yPt);
      this.onPointRefused?.({ pageIndex, xPt, yPt, painted: dz != null, deadZone: dz }, this);
      return null;
    }
    this.setFocus(pos, extend);
    return pos;
  }
  /** The PAINTED-but-unaddressable band under a page point, or `null`. The reason
   *  {@link pointAt} refused, for a caller that must explain it. */
  deadZoneAt(pageIndex, yPt) {
    return deadZoneAt(this.layout.index, pageIndex, yPt);
  }
  /**
   * Select the WHOLE DOCUMENT — every block, across every page.
   *
   * The range is taken from the DOCUMENT (`bridge.blocks`), not from the geometry
   * index. `docStart`/`docEnd` answer with the first and last ADDRESSABLE LINE,
   * so a document whose first or last block has no caret geometry — a leading
   * table, a trailing image, any block pdfz lays out but stamps no addressable
   * run for — would silently have its ends cropped out of "select all", and the
   * copy that followed would be missing text the user can see. The owner asked
   * for "ALL text from all pages", and the document is the only thing that knows
   * where it ends.
   *
   * Cross-PAGE needs nothing special: `blockOrd` is a document coordinate and
   * pagination is a property of the geometry, so a range from the first block to
   * the last spans every page by construction. The painted highlight simply skips
   * whatever has no line box, which is honest rather than wrong.
   */
  selectAll() {
    this.discardPreedit();
    this.breakRun();
    const blocks = this.bridge.blocks;
    const last = blocks.length - 1;
    if (last < 0) return;
    const anchor = { blockOrd: 0, byteOffset: 0, affinity: "downstream" };
    const focus = {
      blockOrd: last,
      byteOffset: byteLen(blocks[last]?.text ?? ""),
      affinity: "upstream"
    };
    this.selection = { anchor, focus };
    this.goalXPt = null;
    this.emit();
  }
  /** Programmatically set the selection (e.g. to a comment anchor range). */
  setSelection(sel) {
    this.discardPreedit();
    this.breakRun();
    this.selection = sel;
    this.goalXPt = null;
    this.emit();
  }
  // ─── history ───────────────────────────────────────────────────────────────────
  canUndo() {
    return this.history.canUndo();
  }
  canRedo() {
    return this.history.canRedo();
  }
  undo() {
    this.discardPreedit();
    this.run.reset();
    const snap = this.history.undo(this.snapshot());
    if (!snap) return;
    this.restore(snap);
  }
  redo() {
    this.discardPreedit();
    this.run.reset();
    const snap = this.history.redo(this.snapshot());
    if (!snap) return;
    this.restore(snap);
  }
  /** Drop the whole history — the BARRIER for an act that must not be undone
   *  past (in elaborate: placing a review comment, whose server-side anchor
   *  rebase no client redo can repair). */
  historyBarrier() {
    this.run.reset();
    this.history.barrier();
  }
  /** End the typing run AND tell the ring the next push starts a fresh entry. */
  breakRun() {
    this.run.reset();
    this.history.breakCoalesce();
  }
  snapshot() {
    return { bridge: this.bridge, selection: this.selection };
  }
  restore(snap) {
    this.bridge = snap.bridge;
    this.layout = this.relayout(snap.bridge.blocks, snap.bridge);
    this.selection = snap.selection;
    this.goalXPt = null;
    this.emit();
  }
  pushUndo(coalesceKey) {
    this.history.push(this.snapshot(), coalesceKey);
  }
  emit() {
    this.onChange?.(this);
  }
};
var INSERT_TEXT_TYPES = /* @__PURE__ */ new Set([
  "insertText",
  "insertReplacementText",
  "insertFromPaste",
  "insertFromDrop",
  "insertFromComposition",
  "insertCompositionText"
]);
function intentFromBeforeInput(ev, composing = false, enterNamedByKeydown = true) {
  const { inputType } = ev;
  if (composing) return null;
  if (INSERT_TEXT_TYPES.has(inputType)) {
    const text = ev.data ?? ev.dataTransfer?.getData("text/plain") ?? "";
    return text.length > 0 ? { type: "insertText", text } : null;
  }
  switch (inputType) {
    // NOT the canvas surface's Enter route — see {@link editFromKeydown}. A
    // `<textarea>` proxy never emits this, so relying on it here is what made
    // Enter a no-op. Kept because it is the CORRECT reading of the event when it
    // does arrive (a `contenteditable`-hosted consumer, a browser that delivers
    // it unpaired), and because the sink's one-shot guard swallows the copy that
    // pairs with a keydown this surface already handled.
    case "insertParagraph":
      return { type: "splitBlock" };
    // Enter on a `<textarea>` AND Shift+Enter on everything arrive with THIS
    // SAME inputType, so the element cannot tell them apart — which is why the
    // physical-keyboard cases are decided at `keydown` ({@link editFromKeydown})
    // and their echo here is swallowed by the one-shot guard.
    //
    // What is left is a break that NO key named. A physical keyboard cannot
    // produce one (measured, Chrome 131, real `<textarea>`: plain Enter and
    // Shift+Enter both arrive behind a `key: 'Enter'` keydown, and Ctrl / Meta /
    // Alt + Enter emit no `beforeinput` at all), so it came from a source that
    // does not report its key — the soft-keyboard / IME path. On Android that is
    // the ONLY way Enter arrives, and a soft keyboard has no Shift+Enter
    // affordance whose soft break could be lost.
    case "insertLineBreak":
      return enterNamedByKeydown ? { type: "insertLineBreak" } : { type: "splitBlock" };
    case "deleteContentBackward":
    case "deleteContent":
    case "deleteByCut":
      return { type: "deleteBackward" };
    case "deleteContentForward":
      return { type: "deleteForward" };
    case "deleteWordBackward":
      return { type: "deleteWordBackward" };
    case "deleteWordForward":
      return { type: "deleteWordForward" };
    // THE LINE DELETES. This surface's `<textarea>` proxy never emits these —
    // MEASURED (real Chrome, real CDP): ⌘⌫ and ⌃K produce keydown and NO
    // `beforeinput` at all, which is why {@link deleteFromKeydown} is their only
    // real route and why the verbs were missing entirely. They are read here for
    // the same reason `insertParagraph` is: it is the CORRECT reading of the
    // event where a browser or a `contenteditable`-hosted consumer does deliver
    // it, and the sink's one-shot paired guard swallows the copy that pairs with
    // a keydown this surface already answered.
    //
    // SOFT vs HARD is a distinction about the wrapped line vs the paragraph. The
    // engine's line verbs are visual-line-native (`LineEdgeResolver`), and a HARD
    // line delete on an unwrapped block is the same range, so both map here
    // rather than inventing a third verb no key can produce.
    case "deleteSoftLineBackward":
    case "deleteHardLineBackward":
      return { type: "deleteToLineStart" };
    case "deleteSoftLineForward":
    case "deleteHardLineForward":
      return { type: "deleteToLineEnd" };
    default:
      return null;
  }
}
var DELETE_PAIRED_INPUT_TYPES = /* @__PURE__ */ new Map([
  ["deleteBackward", /* @__PURE__ */ new Set(["deleteContentBackward", "deleteContent"])],
  ["deleteForward", /* @__PURE__ */ new Set(["deleteContentForward", "deleteContent"])],
  ["deleteWordBackward", /* @__PURE__ */ new Set(["deleteWordBackward"])],
  ["deleteWordForward", /* @__PURE__ */ new Set(["deleteWordForward"])],
  ["deleteToLineStart", /* @__PURE__ */ new Set(["deleteSoftLineBackward", "deleteHardLineBackward"])],
  ["deleteToLineEnd", /* @__PURE__ */ new Set(["deleteSoftLineForward", "deleteHardLineForward"])]
]);
var EMACS_DELETE_CHORDS = /* @__PURE__ */ new Map([
  ["k", { type: "deleteToLineEnd" }],
  ["u", { type: "deleteToLineStart" }],
  ["h", { type: "deleteBackward" }],
  ["d", { type: "deleteForward" }],
  ["w", { type: "deleteWordBackward" }]
]);
function deleteFromKeydown(ev, opts = {}) {
  if (ev.isComposing === true) return null;
  const ctrl = ev.ctrlKey === true;
  const meta = ev.metaKey === true;
  const alt = ev.altKey === true;
  if (ev.key === "Backspace" || ev.key === "Delete") {
    const forward = ev.key === "Delete";
    if (meta) return { type: forward ? "deleteToLineEnd" : "deleteToLineStart" };
    if (alt || ctrl) return { type: forward ? "deleteWordForward" : "deleteWordBackward" };
    return { type: forward ? "deleteForward" : "deleteBackward" };
  }
  if (!ctrl || meta || alt || opts.emacs !== true) return null;
  return EMACS_DELETE_CHORDS.get(ev.key.toLowerCase()) ?? null;
}
function emacsDeleteBindingsForPlatform(platform) {
  return /mac|iphone|ipad|ipod|darwin/i.test(platform);
}
function detectEmacsDeleteBindings() {
  const nav = globalThis.navigator;
  if (!nav) return false;
  return emacsDeleteBindingsForPlatform(nav.platform ?? nav.userAgent ?? "");
}
var ENTER_PAIRED_INPUT_TYPES = /* @__PURE__ */ new Set([
  "insertParagraph",
  "insertLineBreak"
]);
function editFromKeydown(ev) {
  if (ev.isComposing === true) return null;
  if (ev.key !== "Enter") return null;
  if (ev.ctrlKey === true || ev.metaKey === true || ev.altKey === true) return null;
  return ev.shiftKey === true ? { type: "insertLineBreak" } : { type: "splitBlock" };
}
function keydownNamesEnter(ev) {
  return ev.key === "Enter";
}
function navFromKeydown(ev) {
  const extend = ev.shiftKey === true;
  const word = ev.ctrlKey === true || ev.altKey === true;
  const toDoc = ev.ctrlKey === true || ev.metaKey === true;
  if ((ev.ctrlKey === true || ev.metaKey === true) && ev.key.toLowerCase() === "a") {
    return { type: "selectAll" };
  }
  switch (ev.key) {
    case "ArrowLeft":
      return { type: "move", unit: word ? "word" : "char", dir: -1, extend };
    case "ArrowRight":
      return { type: "move", unit: word ? "word" : "char", dir: 1, extend };
    case "ArrowUp":
      return { type: "move", unit: "line", dir: -1, extend };
    case "ArrowDown":
      return { type: "move", unit: "line", dir: 1, extend };
    case "Home":
      return { type: "move", unit: toDoc ? "doc" : "lineEdge", dir: -1, extend };
    case "End":
      return { type: "move", unit: toDoc ? "doc" : "lineEdge", dir: 1, extend };
    default:
      return null;
  }
}
var IDLE = { composing: false, preedit: "", commit: null };
function compositionStart(ev) {
  return { composing: true, preedit: ev.data ?? "", commit: null };
}
function compositionUpdate(ev) {
  return { composing: true, preedit: ev.data ?? "", commit: null };
}
function compositionEnd(ev) {
  const text = ev.data ?? "";
  return text.length > 0 ? { ...IDLE, commit: text } : IDLE;
}
function gestureFromPointerDown(ev) {
  if (ev.pointerType === "touch") {
    return ev.isPrimary === false ? null : { kind: "defer" };
  }
  if (ev.isPrimary === false) return null;
  return { kind: "select" };
}
var TAP_SLOP_PX = 10;
var LONG_PRESS_MS = 500;
var TRIPLE_CLICK_DETAIL = 3;
var TABLE_CLICK_DETAIL = 4;
function exceedsTapSlop(down, now, slopPx = TAP_SLOP_PX) {
  const dx = now.x - down.x;
  const dy = now.y - down.y;
  return dx * dx + dy * dy > slopPx * slopPx;
}
function touchOutcome(down, up, selecting, slopPx = TAP_SLOP_PX) {
  if (selecting) return "select";
  return exceedsTapSlop(down, up, slopPx) ? "scroll" : "tap";
}
var SURFACE_TOUCH_ACTION = "pan-y pinch-zoom";
var MIME_DOCZ = "application/x-norkron-docz+json";
var MIME_HTML = "text/html";
var MIME_PLAIN = "text/plain";
function pickPasteFlavours(get) {
  return {
    payload: get(MIME_DOCZ) ?? "",
    html: get(MIME_HTML) ?? "",
    plain: get(MIME_PLAIN) ?? ""
  };
}
var DEFAULT_ARIA_LABEL = "Document editor";
var HiddenInputSink = class {
  constructor(host, cb, aria = {}) {
    this.cb = cb;
    this.emacsDelete = aria.emacsDeleteBindings ?? detectEmacsDeleteBindings();
    const doc = host.ownerDocument;
    const el = doc.createElement("textarea");
    el.setAttribute("aria-label", aria.label ?? DEFAULT_ARIA_LABEL);
    el.setAttribute("aria-multiline", "true");
    if (aria.describedBy != null && aria.describedBy.length > 0) {
      el.setAttribute("aria-describedby", aria.describedBy);
    }
    el.setAttribute("autocapitalize", "off");
    el.setAttribute("autocorrect", "off");
    el.setAttribute("spellcheck", "false");
    el.tabIndex = 0;
    el.dataset.testid = "pl-canvas-input";
    Object.assign(el.style, {
      position: "absolute",
      top: "0px",
      left: "0px",
      width: "1px",
      height: "1px",
      padding: "0",
      border: "0",
      margin: "0",
      outline: "none",
      resize: "none",
      overflow: "hidden",
      opacity: "0",
      whiteSpace: "pre",
      zIndex: "-1",
      caretColor: "transparent",
      background: "transparent",
      color: "transparent"
    });
    host.appendChild(el);
    this.el = el;
    el.addEventListener("beforeinput", this.onBeforeInput);
    el.addEventListener("keydown", this.onKeydown);
    el.addEventListener("compositionstart", this.onCompositionStart);
    el.addEventListener("compositionupdate", this.onCompositionUpdate);
    el.addEventListener("compositionend", this.onCompositionEnd);
    el.addEventListener("copy", this.onClipboardCopy);
    el.addEventListener("cut", this.onClipboardCut);
    el.addEventListener("paste", this.onClipboardPaste);
  }
  cb;
  el;
  composing = false;
  preedit = "";
  /** ONE-SHOT: the `beforeinput` types to swallow because the keydown that
   *  pairs with them was already turned into an intent. Cleared at the next
   *  keydown so a guard that never fired can never eat a later real event. */
  suppressPaired = null;
  /** ONE-SHOT: did the last `keydown` NAME the Enter key, and is that naming
   *  still unspent? A line break with nothing to explain it came from a soft
   *  keyboard — see {@link keydownNamesEnter}. Starts `false`, because a
   *  `beforeinput` arriving before any keydown at all is exactly the IME shape
   *  this distinguishes. */
  enterNamed = false;
  /** Whether the macOS emacs delete chords are this host's to claim. Resolved
   *  ONCE at construction: a keyboard layout does not change under a session,
   *  and re-reading `navigator` per keystroke would put a global lookup on the
   *  input hot path. */
  emacsDelete;
  /** Position the proxy at a caret rect (host-relative CSS pixels) so the OS
   *  caret and IME window anchor to the drawn caret. */
  moveTo(leftCss, topCss, heightCss) {
    this.el.style.left = `${leftCss}px`;
    this.el.style.top = `${topCss}px`;
    this.el.style.height = `${Math.max(1, heightCss)}px`;
  }
  /** Take keyboard focus (call on canvas pointerdown). */
  focus() {
    this.el.focus({ preventScroll: true });
  }
  get isComposing() {
    return this.composing;
  }
  /** The live IME pre-edit buffer (`''` when not composing) — what the host is
   *  currently painting provisionally. */
  get preeditText() {
    return this.preedit;
  }
  /** Detach listeners and remove the element. */
  destroy() {
    this.el.removeEventListener("beforeinput", this.onBeforeInput);
    this.el.removeEventListener("keydown", this.onKeydown);
    this.el.removeEventListener("compositionstart", this.onCompositionStart);
    this.el.removeEventListener("compositionupdate", this.onCompositionUpdate);
    this.el.removeEventListener("compositionend", this.onCompositionEnd);
    this.el.removeEventListener("copy", this.onClipboardCopy);
    this.el.removeEventListener("cut", this.onClipboardCut);
    this.el.removeEventListener("paste", this.onClipboardPaste);
    this.el.remove();
  }
  onBeforeInput = (ev) => {
    const ie = ev;
    ev.preventDefault();
    const enterNamed = this.enterNamed;
    if (ENTER_PAIRED_INPUT_TYPES.has(ie.inputType)) this.enterNamed = false;
    const paired = this.suppressPaired;
    if (paired?.has(ie.inputType) === true) {
      this.suppressPaired = null;
      if (!this.composing) this.el.value = "";
      return;
    }
    const intent = intentFromBeforeInput(ie, this.composing, enterNamed);
    if (intent) this.cb.onIntent(intent);
    if (!this.composing) this.el.value = "";
  };
  onKeydown = (ev) => {
    const ke = ev;
    this.suppressPaired = null;
    this.enterNamed = keydownNamesEnter(ke);
    const nav = navFromKeydown(ke);
    if (nav) {
      ev.preventDefault();
      this.cb.onNav(nav);
      return;
    }
    const edit = editFromKeydown({
      key: ke.key,
      shiftKey: ke.shiftKey,
      ctrlKey: ke.ctrlKey,
      metaKey: ke.metaKey,
      altKey: ke.altKey,
      isComposing: ke.isComposing === true || this.composing
    });
    if (edit) {
      ev.preventDefault();
      this.suppressPaired = ENTER_PAIRED_INPUT_TYPES;
      this.cb.onIntent(edit);
      return;
    }
    const del = deleteFromKeydown(
      {
        key: ke.key,
        shiftKey: ke.shiftKey,
        ctrlKey: ke.ctrlKey,
        metaKey: ke.metaKey,
        altKey: ke.altKey,
        isComposing: ke.isComposing === true || this.composing
      },
      { emacs: this.emacsDelete }
    );
    if (del) {
      ev.preventDefault();
      this.suppressPaired = DELETE_PAIRED_INPUT_TYPES.get(del.type) ?? null;
      this.cb.onIntent(del);
      return;
    }
    if (this.cb.onShortcut?.(ke) === true) ev.preventDefault();
  };
  onCompositionStart = (ev) => {
    const step = compositionStart(ev);
    this.composing = step.composing;
    this.preedit = step.preedit;
    this.cb.onCompositionStart?.();
    this.cb.onCompositionUpdate?.(step.preedit);
  };
  /** THE painted pre-edit. An IME rewrites its whole buffer on every update, so
   *  this hands the host the FULL provisional string each time (never a delta). */
  onCompositionUpdate = (ev) => {
    const step = compositionUpdate(ev);
    this.composing = step.composing;
    this.preedit = step.preedit;
    this.cb.onCompositionUpdate?.(step.preedit);
  };
  onCompositionEnd = (ev) => {
    const step = compositionEnd(ev);
    this.composing = step.composing;
    this.preedit = step.preedit;
    this.el.value = "";
    this.cb.onCompositionUpdate?.("");
    this.cb.onCompositionEnd?.();
    if (step.commit !== null) this.cb.onIntent({ type: "insertText", text: step.commit });
  };
  /**
   * Write the selection onto `ev` and report whether the clipboard GENUINELY
   * received it. Prefers the RICH three-flavour payload; falls back to the
   * historical `text/plain`-only path.
   *
   * `false` means EITHER nothing is selected OR the write could not be confirmed
   * — and in both cases {@link onClipboardCut} must not delete anything. The
   * confirmation itself is `@norkron/web-docz`'s {@link writeFlavoursTo}, the ONE
   * implementation both editing surfaces share (RULE ZERO / HR13): it returns
   * `false` for the `null` `clipboardData` a SYNTHETIC event carries, for a
   * transfer that silently refuses the write, and for one whose `setData` throws.
   *
   * This used to write through `data?.setData(...)` and `return true`
   * unconditionally, which is precisely how a cut deleted the selection into a
   * clipboard that never received it.
   */
  writeClipboard(ev) {
    const flavours = this.selectionFlavours();
    if (!flavours) return false;
    if (!writeFlavoursTo(ev.clipboardData, flavours)) return false;
    ev.preventDefault();
    return true;
  }
  /** The three flavours for the current selection, or `null` when nothing is
   *  selected. Falls back to a plain-only flavour set for a consumer that has not
   *  wired the rich {@link InputSinkCallbacks.readClipboard} seam. */
  selectionFlavours() {
    const rich = this.cb.readClipboard?.() ?? null;
    if (rich) return rich;
    const text = this.cb.readText();
    return text == null ? null : { plain: text, html: "", payload: "" };
  }
  onClipboardCopy = (ev) => {
    this.writeClipboard(ev);
  };
  onClipboardCut = (ev) => {
    if (this.writeClipboard(ev)) {
      this.cb.onIntent({ type: "deleteBackward" });
    }
  };
  onClipboardPaste = (ev) => {
    const handler = this.cb.onPasteFlavours;
    if (!handler) return;
    const data = ev.clipboardData;
    const flavours = pickPasteFlavours((mime) => data?.getData(mime) ?? null);
    if (handler(flavours)) ev.preventDefault();
  };
};

// src/tween.ts
function defaultNow() {
  const perf = globalThis.performance;
  return typeof perf?.now === "function" ? perf.now() : Date.now();
}
function defaultSchedule() {
  const raf = globalThis.requestAnimationFrame;
  if (typeof raf !== "function") return null;
  return (run) => {
    raf(run);
  };
}
function prefersReducedMotionQuery() {
  const mm = globalThis.matchMedia;
  if (typeof mm !== "function") return false;
  try {
    return mm("(prefers-reduced-motion: reduce)").matches === true;
  } catch {
    return false;
  }
}
function linear(t) {
  if (!(t > 0)) return 0;
  return t >= 1 ? 1 : t;
}
function createTween(opts) {
  const duration = Math.max(0, opts.durationMs);
  const easing = opts.easing ?? linear;
  const now = opts.now ?? defaultNow;
  const schedule = opts.schedule === void 0 ? defaultSchedule() : opts.schedule;
  const reducedMotion = opts.reducedMotion ?? prefersReducedMotionQuery;
  const setTimer = opts.setTimer ?? ((run, ms) => {
    if (typeof setTimeout !== "function") {
      run();
      return void 0;
    }
    return setTimeout(run, ms);
  });
  const clearTimer = opts.clearTimer ?? ((h) => {
    if (typeof clearTimeout === "function" && h !== void 0) {
      clearTimeout(h);
    }
  });
  let runId = 0;
  let active = false;
  let destroyed = false;
  let starts = 0;
  let framesScheduled = 0;
  let startValue = 0;
  let targetValue = 0;
  let startedAt = 0;
  let holdHandle;
  function clearHold() {
    if (holdHandle !== void 0) {
      clearTimer(holdHandle);
      holdHandle = void 0;
    }
  }
  function stop(reason) {
    clearHold();
    if (!active) return;
    active = false;
    runId += 1;
    if (reason) opts.onEnd?.(reason, opts.read());
  }
  function queue(id) {
    if (!schedule) return;
    framesScheduled += 1;
    schedule(() => frame(id));
  }
  function frame(id) {
    if (destroyed || !active || id !== runId) return;
    if (opts.shouldCancel?.()) {
      stop("cancelled");
      return;
    }
    const elapsed = now() - startedAt;
    if (duration <= 0 || elapsed >= duration) {
      opts.write(targetValue);
      stop("arrived");
      return;
    }
    opts.write(startValue + (targetValue - startValue) * easing(elapsed / duration));
    queue(id);
  }
  function withoutMotion(target) {
    stop(null);
    const hold = opts.holdMs;
    if (hold === void 0 || !(hold > 0)) {
      opts.write(target);
      opts.onEnd?.("instant", opts.read());
      return "instant";
    }
    active = true;
    starts += 1;
    const id = runId;
    targetValue = target;
    holdHandle = setTimer(() => {
      holdHandle = void 0;
      if (destroyed || !active || id !== runId) return;
      opts.write(targetValue);
      stop("instant");
    }, hold);
    return "instant";
  }
  return {
    to(target) {
      if (destroyed) return "noop";
      if (!Number.isFinite(target)) return "noop";
      if (reducedMotion()) return withoutMotion(target);
      const current = opts.read();
      if (current === target) {
        stop(null);
        return "noop";
      }
      if (!schedule || duration <= 0) {
        stop(null);
        opts.write(target);
        opts.onEnd?.("instant", opts.read());
        return "instant";
      }
      startValue = current;
      targetValue = target;
      startedAt = now();
      if (active) {
        return "animating";
      }
      active = true;
      starts += 1;
      queue(runId);
      return "animating";
    },
    cancel(o) {
      stop(o?.silent === true ? null : "cancelled");
    },
    get animating() {
      return active;
    },
    get starts() {
      return starts;
    },
    get framesScheduled() {
      return framesScheduled;
    },
    destroy() {
      stop(null);
      destroyed = true;
    }
  };
}

// src/scroll-animator.ts
var DEFAULT_SCROLL_DURATION_MS = 320;
var DEFAULT_SCROLL_MARGIN_CSS = 32;
var DEFAULT_SCROLL_DRIFT_TOLERANCE_CSS = 2;
var MIN_SCROLLER_TRAVEL_CSS = DEFAULT_SCROLL_DRIFT_TOLERANCE_CSS;
var SUBPIXEL_TOLERANCE_CSS = 1;
function hasMeaningfulTravel(maxScrollTopCss) {
  return Number.isFinite(maxScrollTopCss) && maxScrollTopCss > MIN_SCROLLER_TRAVEL_CSS;
}
function scrollTopForBand(t) {
  const finite = (n) => Number.isFinite(n);
  if (!finite(t.topCss) || !finite(t.heightCss) || !finite(t.currentTopCss) || !finite(t.viewportHeightCss) || !finite(t.maxScrollTopCss)) {
    return null;
  }
  const h = Math.max(0, t.heightCss);
  const vp = Math.max(0, t.viewportHeightCss);
  const m = Math.max(0, t.marginCss ?? 0);
  const max = Math.max(0, t.maxScrollTopCss);
  const cur = t.currentTopCss;
  const top = t.topCss;
  const bottom = top + h;
  const block = t.block ?? "center";
  if (t.ifNeeded !== false) {
    const alreadyShowing = h >= vp ? (
      // A band taller than the viewport can never be fully visible; what
      // matters is whether the reader is already looking INSIDE it.
      top <= cur && bottom >= cur + vp
    ) : top >= cur + m && bottom <= cur + vp - m;
    if (alreadyShowing) return null;
  }
  let desired;
  if (block === "nearest") {
    if (top < cur + m) desired = top - m;
    else if (bottom > cur + vp - m) desired = bottom - vp + m;
    else desired = cur;
  } else if (block === "start" || h >= vp) {
    desired = top - m;
  } else {
    desired = top - (vp - h) / 2;
  }
  return Math.max(0, Math.min(desired, max));
}
function canReachBand(t) {
  if (!hasMeaningfulTravel(t.maxScrollTopCss)) return false;
  const vp = t.viewportHeightCss;
  if (!(vp > 0) || !Number.isFinite(vp)) return false;
  const target = scrollTopForBand({
    topCss: t.bandTopCss,
    heightCss: t.bandHeightCss,
    currentTopCss: t.currentTopCss,
    viewportHeightCss: vp,
    maxScrollTopCss: t.maxScrollTopCss,
    ...t.block !== void 0 ? { block: t.block } : {},
    ...t.marginCss !== void 0 ? { marginCss: t.marginCss } : {},
    ifNeeded: false
  });
  if (target == null) return false;
  const h = Math.max(0, t.bandHeightCss);
  const landedTop = t.bandTopCss - target;
  if (landedTop < -SUBPIXEL_TOLERANCE_CSS) return false;
  return h >= vp ? landedTop < vp : landedTop + h <= vp + SUBPIXEL_TOLERANCE_CSS;
}
function easeInOutCubic(t) {
  if (!(t > 0)) return 0;
  if (t >= 1) return 1;
  return t < 0.5 ? 4 * t * t * t : 1 - (-2 * t + 2) ** 3 / 2;
}
function overflowScrolls(overflowY, overflow) {
  return /auto|scroll|overlay/.test(`${overflowY ?? ""} ${overflow ?? ""}`);
}
function pickScrollAncestor(start, scrolls, qualifies, parentOf) {
  let el = start;
  let guard = 0;
  while (el && guard++ < 1024) {
    if (scrolls(el) && qualifies(el)) return el;
    el = parentOf(el);
  }
  return null;
}
function scrollSurfaceFor(scroller) {
  if (!scroller || typeof scroller !== "object") return null;
  const asWin = scroller;
  if (typeof asWin.innerHeight === "number" && asWin.document && typeof asWin.document === "object") {
    const root = () => asWin.document?.scrollingElement ?? asWin.document?.documentElement ?? null;
    return {
      get scrollTopCss() {
        return typeof asWin.scrollY === "number" ? asWin.scrollY : root()?.scrollTop ?? 0;
      },
      get maxScrollTopCss() {
        const h = root()?.scrollHeight ?? 0;
        return Math.max(0, h - asWin.innerHeight);
      },
      get viewportHeightCss() {
        return asWin.innerHeight;
      },
      scrollToCss(topCss) {
        if (typeof asWin.scrollTo === "function") asWin.scrollTo(0, topCss);
        else {
          const r = root();
          if (r) r.scrollTop = topCss;
        }
      }
    };
  }
  const el = scroller;
  if (typeof el.scrollTop !== "number") return null;
  return {
    get scrollTopCss() {
      return el.scrollTop;
    },
    get maxScrollTopCss() {
      return Math.max(0, (el.scrollHeight ?? 0) - (el.clientHeight ?? 0));
    },
    get viewportHeightCss() {
      return el.clientHeight ?? 0;
    },
    scrollToCss(topCss) {
      el.scrollTop = topCss;
    }
  };
}
function createScrollAnimator(surface, opts = {}) {
  const drift = Math.max(0, opts.driftToleranceCss ?? DEFAULT_SCROLL_DRIFT_TOLERANCE_CSS);
  let lastObserved = surface.scrollTopCss;
  let destroyed = false;
  function clamp(v) {
    const max = Math.max(0, surface.maxScrollTopCss);
    if (!Number.isFinite(v)) return surface.scrollTopCss;
    return Math.max(0, Math.min(v, max));
  }
  const tween = createTween({
    durationMs: opts.durationMs ?? DEFAULT_SCROLL_DURATION_MS,
    easing: opts.easing ?? easeInOutCubic,
    ...opts.now !== void 0 ? { now: opts.now } : {},
    ...opts.schedule !== void 0 ? { schedule: opts.schedule } : {},
    ...opts.reducedMotion !== void 0 ? { reducedMotion: opts.reducedMotion } : {},
    read: () => surface.scrollTopCss,
    write: (v) => {
      surface.scrollToCss(v);
      lastObserved = surface.scrollTopCss;
    },
    // Did something OTHER than this tween move the scroller since our last
    // write? Then the user is driving and we are done. Browsers store
    // `scrollTop` at device-pixel granularity, so the comparison has to
    // tolerate the tween's own rounding — see DEFAULT_SCROLL_DRIFT_TOLERANCE_CSS.
    shouldCancel: () => Math.abs(surface.scrollTopCss - lastObserved) > drift,
    onEnd: (reason) => opts.onEnd?.(reason, surface.scrollTopCss)
  });
  return {
    to(targetTopCss) {
      if (destroyed) return "noop";
      const target = clamp(targetTopCss);
      if (Math.abs(target - surface.scrollTopCss) < 0.5) {
        tween.cancel({ silent: true });
        return "noop";
      }
      lastObserved = surface.scrollTopCss;
      const start = tween.to(target);
      return start === "noop" ? "instant" : start;
    },
    interrupt() {
      tween.cancel();
    },
    cancel() {
      tween.cancel();
    },
    get animating() {
      return tween.animating;
    },
    get starts() {
      return tween.starts;
    },
    destroy() {
      destroyed = true;
      tween.destroy();
    }
  };
}
var splitSeq = 0;
function defaultFreshBlockId() {
  splitSeq += 1;
  const rand = typeof crypto !== "undefined" && "randomUUID" in crypto ? crypto.randomUUID().slice(0, 8) : Math.random().toString(36).slice(2, 10);
  return `plc-${Date.now().toString(36)}-${splitSeq}-${rand}`;
}
function buildCanvasView(handle) {
  const projections = legacyProjectionsFromStructured(handle.toBlocks());
  const ir = handle.blocks;
  const irKind = /* @__PURE__ */ new Map();
  for (const b of ir) if (b.id != null && !irKind.has(b.id)) irKind.set(b.id, b.kind);
  const blocks = projections.map(
    (p) => (
      // `sourceKind` rides on the block itself so every downstream consumer — the
      // block map above all — sees the structural truth without a parallel array
      // to keep in step.
      { ...p.block, sourceKind: p.sourceKind }
    )
  );
  const positional = projections.length === ir.length;
  const refusals = [];
  const editableIds = projections.map(({ block, sourceKind }, i) => {
    const refuse = (why) => {
      refusals[i] = why;
      return null;
    };
    refusals[i] = null;
    if (block.kind !== "table_cell" && !SOURCE_KINDS_WITH_RUNS.has(sourceKind))
      return refuse("not-editable");
    const irb = positional ? ir[i] : void 0;
    const id = irb?.id ?? block.id;
    if (id == null) return refuse("unidentified");
    if (irb?.kind === "html") return refuse("not-editable");
    if (irKind.get(rootBlockId(id)) === "html") return refuse("not-editable");
    return id;
  });
  const deletableIds = projections.map(({ block }, i) => {
    const irb = positional ? ir[i] : void 0;
    const id = irb?.id ?? block.id;
    if (id == null) return null;
    const root = rootBlockId(id);
    if (root !== id) return null;
    if (irb?.kind === "html" || irKind.get(root) === "html") return null;
    return id;
  });
  return { blocks, editableIds, refusals, deletableIds };
}
var SOURCE_KINDS_WITH_RUNS = /* @__PURE__ */ new Set([
  "paragraph",
  "heading",
  "text",
  "list",
  "blockquote",
  "field"
]);
function rootBlockId(id) {
  return parseBlockAddress(id).rootId;
}
var DoczEditBridge = class _DoczEditBridge {
  constructor(handle, freshBlockId = defaultFreshBlockId) {
    this.handle = handle;
    this.freshBlockId = freshBlockId;
  }
  handle;
  /** Mint an id for a split's tail block; injectable for deterministic tests.
   *
   *  PUBLIC because a mutation is not only a split. A STRUCTURAL verb that
   *  produces a new bridge over a rebuilt handle (`table-context.ts`'s block
   *  insert, which must mint an id for an id-less block or project unroutable
   *  cells) has to carry the SAME minter forward, or a deterministic session
   *  silently starts minting random ids halfway through. */
  freshBlockId;
  /** Lazily-built canvas view. A handle is immutable and every mutation returns a
   *  NEW bridge, so one build per bridge is always current — and it matters:
   *  `toBlocks()` on a legacy handle re-parses every opaque html island, and the
   *  getter is hit on every relayout, hit-test and caret projection. */
  view = null;
  canvasView() {
    this.view ??= buildCanvasView(this.handle);
    return this.view;
  }
  /** Document-order blocks (index == the engine's `blockOrd`) — the LEGACY
   *  projection of the block list the layout pass actually encodes. See
   *  {@link buildCanvasView} for why this is not `handle.blocks`. */
  get blocks() {
    return this.canvasView().blocks;
  }
  /** The SAME array as {@link blocks}, typed as the `DoczBlock`s it really holds
   *  (comments included). A consumer that resolves a `blockOrd` against the docz
   *  transport — a comment seed, a clipboard fragment, a block-kind gate — MUST
   *  read this and never `handle.blocks`: the two are different documents the
   *  moment the handle carries an opaque `html` island, and indexing the wrong one
   *  by `blockOrd` silently addresses a different block. */
  get doczBlocks() {
    return this.canvasView().blocks;
  }
  /** The handle block id an edit at `blockOrd` may be routed to, or `null` when
   *  no sound routing exists (a list item, a quote/code block projected out of
   *  opaque markup — see {@link buildCanvasView}). */
  editableIdAt(blockOrd) {
    return this.canvasView().editableIds[blockOrd] ?? null;
  }
  /** Why a verb aimed at `blockOrd` cannot be routed, or `null` when it can be.
   *  See {@link EditRefusal}: a table is `'not-editable'`, a block the mapping
   *  could not name is `'unidentified'`, and those are different events. */
  routingRefusal(blockOrd) {
    if (this.canvasView().editableIds[blockOrd] != null) return null;
    return this.canvasView().refusals[blockOrd] ?? "unidentified";
  }
  /**
   * Build the bridge that carries `next` — **THE ONE EXTENSION SEAM.** A subclass
   * that adds state (elaborate's `FormatBridge` carries the session's per-block
   * styled-run overrides) overrides THIS, once, and every verb below — present
   * and future — keeps that state by construction.
   *
   * ## Why it is a seam and not four overridden verbs (P0, 2026-07-30)
   *
   * `FormatBridge` used to re-carry its overrides by overriding `editText`,
   * `split` and `merge` individually. That is a contract nobody can hold: the
   * moment this class grew a FOURTH verb — {@link deleteBlock} — the subclass
   * silently stopped being a `FormatBridge` after a block removal, and every mark
   * on the page would have vanished on the next repaint. The subclass was
   * correct; the shape it had to satisfy was not.
   *
   * One seam means a new verb cannot introduce that defect again.
   */
  withHandle(next) {
    return new _DoczEditBridge(next, this.freshBlockId);
  }
  /** Wrap the handle a mutation produced — but return THIS bridge unchanged when
   *  the handle declined the edit (`DoczHandle` returns the SAME reference for an
   *  honest no-op, e.g. a structured `list`/`code` block that exposes no editable
   *  runs). Identity is therefore a truthful signal to the caller that nothing
   *  moved, instead of a fresh wrapper that looks like a successful edit. */
  wrap(next) {
    return next === this.handle ? this : this.withHandle(next);
  }
  editText(blockOrd, at, deleteBytes, insert) {
    const id = this.editableIdAt(blockOrd);
    if (id == null) return this;
    return this.wrap(this.handle.editBlockText(id, { at, deleteBytes, insert }));
  }
  split(blockOrd, atByte) {
    const id = this.editableIdAt(blockOrd);
    if (id == null) return this;
    return this.wrap(this.handle.splitBlock(id, atByte, this.freshBlockId()));
  }
  merge(blockOrd) {
    const id = this.editableIdAt(blockOrd);
    if (id == null) return this;
    return this.wrap(this.handle.mergeBlock(id));
  }
  /**
   * Remove the block at `blockOrd` WHOLESALE — the verb that makes an ATOMIC
   * block (an image, a rule, a code block, a flattened table) removable at all.
   *
   * It routes on {@link CanvasView.deletableIds}, NOT on `editableIdAt`: the
   * latter is `null` for exactly the blocks this exists to reach, so reusing it
   * would have re-created the defect one layer down. `DoczHandle.deleteBlock`
   * owns the algebra (and independently refuses a sub-block address and the
   * document's last block); this only translates `blockOrd` → `blockId` and keeps
   * the identity convention so `applyEdit` can tell a real removal from a
   * refusal.
   */
  deleteBlock(blockOrd) {
    const id = this.canvasView().deletableIds[blockOrd] ?? null;
    if (id == null) return this;
    return this.wrap(this.handle.deleteBlock(id));
  }
};
function doczBridgeOf(bridge) {
  let b = bridge;
  for (let depth = 0; b != null && depth < 32; depth += 1) {
    if (b instanceof DoczEditBridge) return b;
    b = b.inner;
  }
  return null;
}
function handleFromBridge(bridge) {
  return doczBridgeOf(bridge)?.handle ?? null;
}
function canvasDoczBlocks(bridge) {
  return doczBridgeOf(bridge)?.doczBlocks ?? null;
}
var doczEncodeBlocks = makeDoczEncodeBlocks();
function makeDoczEncodeBlocks(fragmentFor, parseDocOverride) {
  return (blocks) => {
    const parseDoc = parseDocOverride ?? (typeof globalThis === "object" && globalThis.document != null ? globalThis.document : null);
    const palette = [];
    for (const b of blocks) {
      if (b.kind === "html" && parseDoc) {
        let exploded = null;
        try {
          exploded = explodeRawHtml(b.text, parseDoc);
        } catch {
          exploded = null;
        }
        if (exploded && exploded.length > 0) {
          palette.push(...exploded);
          continue;
        }
      }
      const frag = fragmentFor?.(b) ?? null;
      const useFrag = frag != null && frag.runs.length > 0 && fragmentRunText(frag.runs) === b.text;
      const runs = useFrag ? frag.runs : [{ text: b.text, marks: [] }];
      const kind = (useFrag ? frag?.kind : void 0) ?? b.kind;
      const level = (useFrag ? frag?.level : void 0) ?? b.level;
      if (kind === "heading") {
        palette.push({ kind: "heading", level: level ?? 1, runs });
      } else {
        palette.push({ kind: "paragraph", runs });
      }
    }
    return encodeBlockList(palette);
  };
}
function fragmentRunText(runs) {
  let out = "";
  for (const r of runs) out += r.text;
  return out;
}
function structuredRuns(block) {
  return block.kind === "paragraph" || block.kind === "heading" ? block.runs ?? null : null;
}
function legacyCount(block) {
  return legacyFromStructuredBlock(block).length;
}
function singleQuoteChildRuns(block) {
  if (block.kind !== "blockquote") return null;
  const children = block.children;
  if (children.length !== 1) return null;
  const only = children[0];
  return only.kind === "paragraph" ? only.runs : null;
}
function itemRuns(item) {
  return Array.isArray(item) ? item : item.runs;
}
function overlayList(block, owned, fragmentFor) {
  const items = block.items;
  let touched = false;
  const next = items.map((item, i) => {
    const eb = owned[i];
    if (!eb) return item;
    const frag = fragmentFor(eb);
    if (frag == null || frag.runs.length === 0) return item;
    if (fragmentRunText(frag.runs) !== fragmentRunText(itemRuns(item))) return item;
    touched = true;
    return frag.runs;
  });
  return touched ? { ...block, items: next } : block;
}
function overlayTable(block, owned, fragmentFor) {
  const tbl = block;
  let cursor = 0;
  let touched = false;
  const overlayRow = (row) => {
    const cells = row.cells.map((c) => {
      const eb = owned[cursor++];
      if (!eb) return c;
      const frag = fragmentFor(eb);
      if (frag == null || frag.runs.length === 0) return c;
      if (fragmentRunText(frag.runs) !== fragmentRunText(c.runs)) return c;
      touched = true;
      return { ...c, runs: frag.runs };
    });
    return { ...row, cells };
  };
  const header = tbl.header ? overlayRow(tbl.header) : void 0;
  const rows = tbl.rows.map(overlayRow);
  if (!touched) return block;
  return { ...block, ...header ? { header } : {}, rows };
}
function overlayQuote(block, base, frag) {
  if (fragmentRunText(frag.runs) !== fragmentRunText(base)) return block;
  return { ...block, children: [{ kind: "paragraph", runs: frag.runs }] };
}
function makeStructuredEncodeBlocks(fragmentFor, parseDocOverride) {
  const plain = makeDoczEncodeBlocks(fragmentFor, parseDocOverride);
  return (blocks, bridge) => {
    const handle = bridge ? handleFromBridge(bridge) : null;
    if (!handle) return { bytes: plain(blocks) };
    const structured = handle.toBlocks();
    const byId = /* @__PURE__ */ new Map();
    for (const b of blocks) if (b.id != null) byId.set(b.id, b);
    let total = 0;
    for (const sb of structured) total += legacyCount(sb);
    const positional = total === blocks.length;
    let cursor = 0;
    const palette = structured.map((sb) => {
      const owned = positional ? blocks.slice(cursor, cursor + legacyCount(sb)) : [];
      cursor += legacyCount(sb);
      if (!fragmentFor) return sb;
      if (sb.kind === "list") {
        return positional ? overlayList(sb, owned, fragmentFor) : sb;
      }
      if (sb.kind === "table") {
        return positional ? overlayTable(sb, owned, fragmentFor) : sb;
      }
      const id = sb.id;
      const eb = (positional ? owned[0] : void 0) ?? (id != null ? byId.get(id) : void 0);
      if (!eb) return sb;
      const frag = fragmentFor(eb);
      if (frag == null || frag.runs.length === 0) return sb;
      const quoteRuns = singleQuoteChildRuns(sb);
      if (quoteRuns) return overlayQuote(sb, quoteRuns, frag);
      const base = structuredRuns(sb);
      const baseText = base ? fragmentRunText(base) : null;
      if (baseText != null && fragmentRunText(frag.runs) !== baseText) return sb;
      const kind = frag.kind ?? sb.kind;
      const props = frag.para ?? sb.props;
      if (kind === "heading") {
        const sbLevel = sb.level;
        return { kind: "heading", level: frag.level ?? sbLevel ?? 1, runs: frag.runs, props };
      }
      return { kind: "paragraph", runs: frag.runs, props };
    });
    const restamped = palette.map((pb, i) => {
      const id = structured[i]?.id;
      return id != null ? { ...pb, id } : pb;
    });
    const effective = legacyProjectionsFromStructured(restamped).map(
      (p) => ({ ...p.block, sourceKind: p.sourceKind })
    );
    return { bytes: encodeBlockList(palette), blocks: effective };
  };
}
var structuredEncodeBlocks = makeStructuredEncodeBlocks();

// src/table-context.ts
var INDEX_CACHE = /* @__PURE__ */ new WeakMap();
function tableIndexOf(handle) {
  const cached = INDEX_CACHE.get(handle);
  if (cached) return cached;
  const structured = handle.toBlocks();
  const projections = legacyProjectionsFromStructured(structured);
  const sourceIndex = projections.map((p) => p.sourceIndex);
  const firstOrd = /* @__PURE__ */ new Map();
  const ordCount = /* @__PURE__ */ new Map();
  for (let ord = 0; ord < projections.length; ord += 1) {
    const src = sourceIndex[ord];
    if (!firstOrd.has(src)) firstOrd.set(src, ord);
    ordCount.set(src, (ordCount.get(src) ?? 0) + 1);
  }
  const built = { structured, sourceIndex, firstOrd, ordCount };
  INDEX_CACHE.set(handle, built);
  return built;
}
function gridOf(table) {
  const hasHeader = table.header != null;
  let cols = table.columns?.length ?? 0;
  if (table.header) cols = Math.max(cols, table.header.cells.length);
  for (const row of table.rows) cols = Math.max(cols, row.cells.length);
  return { rows: (hasHeader ? 1 : 0) + table.rows.length, cols, hasHeader };
}
function canvasCellAt(bridge, blockOrd) {
  const dz = doczBridgeOf(bridge);
  if (dz == null) return null;
  if (!Number.isInteger(blockOrd) || blockOrd < 0) return null;
  const block = dz.doczBlocks[blockOrd];
  if (block == null || !isTableCellOrdKind(block)) return null;
  const cellId = block.id;
  if (cellId == null) return null;
  const address = parseBlockAddress(cellId);
  const segment = address.segments[address.segments.length - 1];
  if (segment == null || segment.kind !== "tableCell") return null;
  const index = tableIndexOf(dz.handle);
  const tableIndex = index.sourceIndex[blockOrd];
  if (tableIndex == null) return null;
  const source = index.structured[tableIndex];
  if (source == null || source.kind !== "table") return null;
  const firstCellOrd = index.firstOrd.get(tableIndex);
  if (firstCellOrd == null) return null;
  const { rows, cols, hasHeader } = gridOf(source);
  const visualRow = segment.header ? 0 : hasHeader ? segment.row + 1 : segment.row;
  return {
    tableId: address.rootId,
    cellId,
    cellOrd: blockOrd,
    firstCellOrd,
    tableIndex,
    header: segment.header,
    row: segment.row,
    col: segment.col,
    visualRow,
    rows,
    cols,
    hasHeader
  };
}
function canvasCellRegion(bridge, sel) {
  const anchor = canvasCellAt(bridge, sel.anchor.blockOrd);
  if (anchor == null) return null;
  const focus = sel.focus.blockOrd === sel.anchor.blockOrd ? anchor : canvasCellAt(bridge, sel.focus.blockOrd);
  if (focus == null) return null;
  if (focus.tableIndex !== anchor.tableIndex || focus.tableId !== anchor.tableId) return null;
  return {
    anchor,
    focus,
    r0: Math.min(anchor.visualRow, focus.visualRow),
    c0: Math.min(anchor.col, focus.col),
    r1: Math.max(anchor.visualRow, focus.visualRow),
    c1: Math.max(anchor.col, focus.col)
  };
}
function canvasTableCellOrds(bridge, blockOrd) {
  const cell = canvasCellAt(bridge, blockOrd);
  if (cell == null) return null;
  const dz = doczBridgeOf(bridge);
  if (dz == null) return null;
  const index = tableIndexOf(dz.handle);
  const count = index.ordCount.get(cell.tableIndex);
  if (count == null || count <= 0) return null;
  return { first: cell.firstCellOrd, last: cell.firstCellOrd + count - 1 };
}
function canvasSelectTable(bridge, blockOrd) {
  const span = canvasTableCellOrds(bridge, blockOrd);
  if (span == null) return null;
  const lastText = bridge.blocks[span.last]?.text ?? "";
  return {
    anchor: { blockOrd: span.first, byteOffset: 0, affinity: "downstream" },
    focus: { blockOrd: span.last, byteOffset: byteLen(lastText), affinity: "upstream" }
  };
}
function canvasSelectedTable(bridge, sel) {
  const [lo, hi] = sel.anchor.blockOrd < sel.focus.blockOrd || sel.anchor.blockOrd === sel.focus.blockOrd && sel.anchor.byteOffset <= sel.focus.byteOffset ? [sel.anchor, sel.focus] : [sel.focus, sel.anchor];
  const span = canvasTableCellOrds(bridge, lo.blockOrd);
  if (span == null) return null;
  if (lo.blockOrd !== span.first || lo.byteOffset !== 0) return null;
  if (hi.blockOrd !== span.last) return null;
  if (hi.byteOffset !== byteLen(bridge.blocks[span.last]?.text ?? "")) return null;
  return canvasTableAt(bridge, span.first);
}
function canvasTableAt(bridge, blockOrd) {
  const cell = canvasCellAt(bridge, blockOrd);
  if (cell == null) return null;
  const dz = doczBridgeOf(bridge);
  if (dz == null) return null;
  const source = tableIndexOf(dz.handle).structured[cell.tableIndex];
  if (source == null || source.kind !== "table") return null;
  return { id: cell.tableId, table: source, index: cell.tableIndex };
}
function undecoratedDoczBridge(bridge) {
  const dz = doczBridgeOf(bridge);
  return dz != null && dz === bridge ? dz : null;
}
function structuredDocFrom(handle, blocks) {
  const doc = handle.doc;
  return {
    ...doc.id != null ? { id: doc.id } : {},
    ...doc.baseVersionId != null ? { baseVersionId: doc.baseVersionId } : {},
    ...carrySectionDecoration(doc),
    blocks
  };
}
function rebuild(dz, blocks) {
  return dz.withHandle(
    createStructuredDoczHandle(dz.handle.module, structuredDocFrom(dz.handle, blocks))
  );
}
function canvasReplaceStructuredBlock(bridge, blockId, next) {
  const dz = undecoratedDoczBridge(bridge);
  if (dz == null) return null;
  const structured = dz.handle.toBlocks();
  const at = structured.findIndex((b) => b.id === blockId);
  if (at < 0) return bridge;
  const blocks = structured.slice();
  blocks[at] = next.id != null ? next : { ...next, id: blockId };
  return rebuild(dz, blocks);
}
function canvasDeleteStructuredBlock(bridge, blockId) {
  const dz = undecoratedDoczBridge(bridge);
  if (dz == null) return null;
  const next = dz.handle.deleteBlock(blockId);
  if (next === dz.handle) return bridge;
  return dz.withHandle(next);
}
function canvasInsertStructuredAfter(bridge, blockOrd, block) {
  const dz = undecoratedDoczBridge(bridge);
  if (dz == null) return null;
  const index = tableIndexOf(dz.handle);
  const source = Number.isInteger(blockOrd) && blockOrd >= 0 ? index.sourceIndex[blockOrd] : void 0;
  const at = source ?? index.structured.length - 1;
  const minted = block.id != null ? block : { ...block, id: dz.freshBlockId() };
  const blocks = index.structured.slice();
  blocks.splice(at + 1, 0, minted);
  return rebuild(dz, blocks);
}

// src/render.ts
var PT_PER_IN2 = 72;
var CSS_PX_PER_IN2 = 96;
var MAX_CANVAS_SIDE_PX = 32767;
function resolve2(opts) {
  const zoom = opts?.zoom ?? 1;
  const dpr = opts?.dpr ?? (globalThis.devicePixelRatio || 1);
  const backing = Math.min(dpr, opts?.maxDpr ?? 2);
  return {
    zoom,
    backing,
    cssScale: CSS_PX_PER_IN2 / PT_PER_IN2 * zoom,
    exact: opts?.exact ?? false,
    paper: opts?.paper ?? "#ffffff",
    fontFamily: opts?.fontFamily ?? "Alexandria",
    maxCanvasSidePx: opts?.maxCanvasSidePx ?? MAX_CANVAS_SIDE_PX,
    imageSource: opts?.imageSource ?? null,
    missingImage: opts?.missingImage ?? "frame",
    onUnpainted: opts?.onUnpainted ?? null
  };
}
function maxPaintableHeightPt(opts) {
  const r = resolve2(opts);
  return r.maxCanvasSidePx / (r.backing * r.cssScale);
}
var CSS_GENERIC_FAMILIES = /* @__PURE__ */ new Set([
  "serif",
  "sans-serif",
  "monospace",
  "cursive",
  "fantasy",
  "system-ui",
  "ui-serif",
  "ui-sans-serif",
  "ui-monospace",
  "ui-rounded",
  "math",
  "emoji",
  "fangsong"
]);
function fontFamilyList(name, documentFamily) {
  const trimmed = name?.trim();
  if (!trimmed) return documentFamily;
  if (CSS_GENERIC_FAMILIES.has(trimmed.toLowerCase())) return `${trimmed}, ${documentFamily}`;
  return `"${trimmed.replace(/\\/g, "\\\\").replace(/"/g, '\\"')}", ${documentFamily}`;
}
function cssWeight(face) {
  if (!face) return 400;
  if (face.family === 1) return face.weight === 1 ? 900 : 300;
  return face.weight === 1 ? 700 : 400;
}
function colorCss(c) {
  const to = (v) => Math.max(0, Math.min(255, Math.round(v * 255)));
  return `rgb(${to(c[0])}, ${to(c[1])}, ${to(c[2])})`;
}
function pageCssSize(view, pageIndex, opts) {
  const r = resolve2(opts);
  const pg = view.page(pageIndex);
  return {
    widthCss: pg.widthPt * r.cssScale,
    heightCss: pg.heightPt * r.cssScale,
    widthPt: pg.widthPt,
    heightPt: pg.heightPt
  };
}
async function ensureFonts(urls, family = "Alexandria") {
  const fontsApi = globalThis.document?.fonts;
  if (!fontsApi || typeof FontFace === "undefined") return;
  const weights = [300, 400, 700, 900];
  await Promise.all(
    weights.map(async (w) => {
      const face = new FontFace(family, `url(${urls[w]})`, { weight: String(w) });
      await face.load();
      fontsApi.add(face);
    })
  );
  await fontsApi.ready;
}
function renderPage(canvas, view, pageIndex, opts) {
  const pg = view.page(pageIndex);
  return renderBand(canvas, view, pageIndex, { yFromPt: 0, yToPt: pg.heightPt }, opts);
}
function renderBand(canvas, view, pageIndex, band, opts) {
  const r = resolve2(opts);
  const pg = view.page(pageIndex);
  const yFrom = Math.max(0, Math.min(band.yFromPt, pg.heightPt));
  const yTo = Math.max(yFrom, Math.min(band.yToPt, pg.heightPt));
  const bandHeightPt = yTo - yFrom;
  const full = yFrom <= 0 && yTo >= pg.heightPt;
  const cssW = pg.widthPt * r.cssScale;
  const cssH = bandHeightPt * r.cssScale;
  const backingW = Math.round(cssW * r.backing);
  const backingH = Math.round(cssH * r.backing);
  if (backingW > r.maxCanvasSidePx || backingH > r.maxCanvasSidePx) {
    const where = full ? "" : ` band ${yFrom.toFixed(0)}..${yTo.toFixed(0)}pt`;
    throw new Error(
      `web-page-layout: page ${pageIndex}${where} needs a ${backingW}\xD7${backingH} device-pixel backing store but the maximum safe canvas side is ${r.maxCanvasSidePx}. ` + (view.continuous ? `This is a CONTINUOUS column ${pg.heightPt.toFixed(0)}pt tall; ` : "This page is taller than one canvas can be; ") + `a single canvas paints at most ${maxPaintableHeightPt(opts).toFixed(0)}pt at zoom ${r.zoom} / dpr ${r.backing}. VIRTUALISE the surface \u2014 \`buildTilePlan\` + \`createTilePainter\` paint one bounded scroll strip per canvas and serve paginated and continuous identically \u2014 or lower the zoom.`
    );
  }
  canvas.style.width = `${cssW}px`;
  canvas.style.height = `${cssH}px`;
  canvas.width = backingW;
  canvas.height = backingH;
  const ctx = canvas.getContext("2d");
  if (!ctx) throw new Error("web-page-layout: 2D context unavailable");
  const k = r.backing * r.cssScale;
  ctx.setTransform(k, 0, 0, k, 0, full ? 0 : -yFrom * k);
  ctx.clearRect(0, yFrom, pg.widthPt, bandHeightPt);
  ctx.fillStyle = r.paper;
  ctx.fillRect(0, yFrom, pg.widthPt, bandHeightPt);
  ctx.textBaseline = "alphabetic";
  ctx.fontKerning = "none";
  ctx.textRendering = "geometricPrecision";
  for (let ri = 0; ri < pg.ruleCount; ri++) {
    const rule = view.rule(pg.ruleFirst + ri);
    const top = pg.heightPt - rule.yPt - rule.hPt;
    if (!full && (top + rule.hPt < yFrom || top > yTo)) continue;
    ctx.fillStyle = rule.fillKind === 1 ? colorCss(rule.c) : grayCss(rule.c[0]);
    ctx.fillRect(rule.xPt, top, rule.wPt, rule.hPt);
  }
  paintImages(ctx, view, pg, pageIndex, r, full ? null : { yFromPt: yFrom, yToPt: yTo });
  const dcDone = /* @__PURE__ */ new Set();
  for (let i = 0; i < pg.runCount; i++) {
    const run = view.run(pg.runFirst + i);
    if (!full) {
      const ink2 = runInkExtentPt(run, pg.heightPt - run.baselineYPt);
      if (ink2.bottomPt < yFrom || ink2.topPt > yTo) continue;
    }
    if (run.runKind === RunKind.emptyLine) continue;
    if (!run.shaped || run.strLen === 0) {
      r.onUnpainted?.(unpaintedRun(pageIndex, pg.runFirst + i, run));
      continue;
    }
    const face = view.faceForFontId(run.fontId);
    const family = fontFamilyList(run.fontFamilyName, r.fontFamily);
    ctx.font = `${cssWeight(face)} ${run.sizePt}px ${family}`;
    const ink = run.hasColor ? colorCss(run.color) : "#000000";
    ctx.fillStyle = ink;
    const yTop = pg.heightPt - run.baselineYPt;
    const text = view.runText(run);
    const drawGlyphs = (paint) => {
      if (!run.cpGlyph1to1) paint(text, run.xPt, yTop);
      else drawRun(ctx, view, run, text, yTop, r.exact, paint);
    };
    const oblique = face?.italic === 1;
    if (oblique) obliqueBegin(ctx, yTop);
    paintRunEffects(ctx, run, ink, drawGlyphs);
    if (oblique) ctx.restore();
    if (run.underline || run.strikethrough) {
      const thickness = run.sizePt * DECO_THICKNESS_RATIO;
      ctx.fillStyle = ink;
      if (run.underline)
        drawDecoLine(ctx, run.xPt, yTop + run.sizePt * UNDERLINE_DROP_RATIO, run.widthPt, thickness);
      if (run.strikethrough)
        drawDecoLine(ctx, run.xPt, yTop - run.sizePt * STRIKE_RISE_RATIO, run.widthPt, thickness);
    }
    if (run.emphasis !== 0) paintEmphasis(ctx, view, run, text, yTop, ink);
    if (run.cbWidthPt > 0) paintCharBorder(ctx, run, yTop);
    if (run.dcLines > 0 && !dcDone.has(run.blockOrd)) {
      dcDone.add(run.blockOrd);
      paintDropCap(ctx, run, text, yTop, ink, family, cssWeight(face));
    }
  }
  return canvas;
}
function runInkExtentPt(run, baselineTopDownPt) {
  const s = Math.max(run.sizePt, 0);
  let topPt = baselineTopDownPt - s * 1.6;
  let bottomPt = baselineTopDownPt + s * 0.9;
  if (run.glow) {
    topPt -= s * 0.5;
    bottomPt += s * 0.5;
  }
  if (run.reflection) bottomPt = Math.max(bottomPt, baselineTopDownPt + s * 1.7);
  if (run.dcLines > 0) {
    const big = s * run.dcLines;
    const dcBaseline = baselineTopDownPt + s * (run.dcLines - 1);
    topPt = Math.min(topPt, dcBaseline - big * 1.2);
    bottomPt = Math.max(bottomPt, dcBaseline + big * 0.4);
  }
  return { topPt, bottomPt };
}
function unpaintedRun(pageIndex, recordIndex, run) {
  const unshaped = !run.shaped;
  return {
    section: "run",
    pageIndex,
    recordIndex,
    blockOrd: run.blockOrd,
    reason: unshaped ? "unshaped-run" : "empty-run-text",
    detail: unshaped ? "non-shaped (legacy CID) run: pdfz `capCidRun` captures no text and no advances, so the glyphs exist in the PDF and cannot exist on the canvas" : "run carries zero captured bytes \u2014 nothing to draw"
  };
}
function paintImages(ctx, view, pg, pageIndex, r, band) {
  for (let ii = 0; ii < pg.imageCount; ii++) {
    const idx = pg.imageFirst + ii;
    const img = view.image(idx);
    const yTop = pg.heightPt - img.yPt - img.hPt;
    if (band && (yTop + img.hPt < band.yFromPt || yTop > band.yToPt)) continue;
    const src = r.imageSource ? r.imageSource(img) : null;
    if (src) {
      ctx.drawImage(src, img.xPt, yTop, img.wPt, img.hPt);
      continue;
    }
    r.onUnpainted?.({
      section: "image",
      pageIndex,
      recordIndex: idx,
      blockOrd: img.blockOrd,
      reason: "no-image-source",
      detail: `${img.kind === 1 ? "svg" : "png"} image geometry arrived with no pixels: ` + (img.blobLen > 0 ? "the SoA v6 wire DOES carry a payload for this record, but no `RenderOptions.imageSource` turned it into a source \u2014 pass `createImageStore(view).resolve` (and await `prime()` for a complete first frame)" : "the SoA wire carries no payload for it (a pre-v6 buffer, or a producer that had no bytes) and no `RenderOptions.imageSource` resolved it")
    });
    if (r.missingImage === "frame") paintImagePlaceholder(ctx, img, yTop);
  }
}
function paintImagePlaceholder(ctx, img, yTop) {
  if (!(img.wPt > 0) || !(img.hPt > 0)) return;
  ctx.save();
  ctx.strokeStyle = "rgb(160, 160, 160)";
  ctx.lineWidth = 0.75;
  ctx.setLineDash([3, 2]);
  ctx.strokeRect(img.xPt, yTop, img.wPt, img.hPt);
  ctx.setLineDash([]);
  ctx.beginPath();
  ctx.moveTo(img.xPt, yTop);
  ctx.lineTo(img.xPt + img.wPt, yTop + img.hPt);
  ctx.stroke();
  ctx.restore();
}
function rgb255(c) {
  return `rgb(${c[0]}, ${c[1]}, ${c[2]})`;
}
function paintRunEffects(ctx, run, ink, draw) {
  const size = run.sizePt;
  const fill = (s, x, y) => ctx.fillText(s, x, y);
  if (run.emboss || run.engrave) {
    const d = Math.max(size * 0.04, 0.6);
    const lightDx = run.emboss ? -d : d;
    ctx.save();
    ctx.fillStyle = "rgba(255,255,255,0.85)";
    ctx.translate(lightDx, lightDx);
    draw(fill);
    ctx.restore();
    ctx.save();
    ctx.fillStyle = "rgba(0,0,0,0.55)";
    ctx.translate(-lightDx, -lightDx);
    draw(fill);
    ctx.restore();
  }
  ctx.save();
  if (run.glow) {
    ctx.shadowColor = rgb255(run.glowRgb);
    ctx.shadowBlur = size * 0.4;
  } else if (run.shadow) {
    ctx.shadowColor = "rgba(0,0,0,0.55)";
    ctx.shadowBlur = size * 0.08;
    ctx.shadowOffsetX = size * 0.06;
    ctx.shadowOffsetY = size * 0.06;
  }
  ctx.fillStyle = ink;
  draw(fill);
  if (run.glow) draw(fill);
  ctx.restore();
  if (run.outline) {
    ctx.save();
    ctx.strokeStyle = ink;
    ctx.lineWidth = Math.max(size * 0.035, 0.4);
    ctx.lineJoin = "round";
    draw((s, x, y) => ctx.strokeText(s, x, y));
    ctx.restore();
  }
  if (run.reflection) {
    ctx.save();
    ctx.globalAlpha = 0.28;
    ctx.fillStyle = ink;
    draw((s, x, y) => {
      ctx.save();
      ctx.translate(0, 2 * y + size * 0.14);
      ctx.scale(1, -1);
      ctx.fillText(s, x, y);
      ctx.restore();
    });
    ctx.restore();
  }
}
function paintEmphasis(ctx, view, run, text, yTop, ink) {
  const cps = Array.from(text);
  const n = Math.min(cps.length, run.glyphCount);
  const unit = run.sizePt / 1e3;
  const radius = Math.max(run.sizePt * 0.06, 0.8);
  const under = run.emphasis === 4;
  const markY = under ? yTop + run.sizePt * 0.16 : yTop - run.sizePt * 0.92;
  let prefix1000 = 0;
  for (let i = 0; i < n; i++) {
    const cp = cps[i];
    const advance = view.glyphAdvance(run.glyphFirst + i);
    if (cp !== " ") {
      const cx = run.xPt + (prefix1000 + advance / 2) * unit;
      ctx.fillStyle = ink;
      ctx.strokeStyle = ink;
      switch (run.emphasis) {
        case 1:
        // dot
        case 4:
          ctx.beginPath();
          ctx.arc(cx, markY, radius, 0, Math.PI * 2);
          ctx.fill();
          break;
        case 3:
          ctx.lineWidth = Math.max(run.sizePt * 0.03, 0.4);
          ctx.beginPath();
          ctx.arc(cx, markY, radius, 0, Math.PI * 2);
          ctx.stroke();
          break;
        case 2:
          ctx.beginPath();
          ctx.moveTo(cx, markY - radius);
          ctx.lineTo(cx + radius, markY);
          ctx.lineTo(cx, markY + radius);
          ctx.lineTo(cx - radius, markY);
          ctx.closePath();
          ctx.fill();
          break;
      }
    }
    prefix1000 += advance;
  }
}
function paintCharBorder(ctx, run, yTop) {
  const top = yTop - run.sizePt * 0.8;
  const height = run.sizePt;
  const pad = run.sizePt * 0.08;
  ctx.save();
  ctx.strokeStyle = rgb255(run.cbRgb);
  ctx.lineWidth = Math.max(run.cbWidthPt, 0.3);
  if (run.cbStyle === 1)
    ctx.setLineDash([run.sizePt * 0.18, run.sizePt * 0.12]);
  else if (run.cbStyle === 2) ctx.setLineDash([run.cbWidthPt, run.sizePt * 0.12]);
  const x = run.xPt - pad;
  const y = top - pad;
  const w = run.widthPt + 2 * pad;
  const h = height + 2 * pad;
  ctx.strokeRect(x, y, w, h);
  if (run.cbStyle === 3) ctx.strokeRect(x + 1.5, y + 1.5, w - 3, h - 3);
  ctx.restore();
}
function paintDropCap(ctx, run, text, yTop, ink, family, weight) {
  const first = Array.from(text)[0];
  if (!first || first === " ") return;
  const bigSize = run.sizePt * run.dcLines;
  const x = run.dcKind === 1 ? run.xPt - run.dcDistancePt - bigSize * 0.7 : run.xPt;
  ctx.save();
  ctx.font = `${weight} ${bigSize}px ${family}`;
  ctx.fillStyle = ink;
  ctx.fillText(first, x, yTop + run.sizePt * (run.dcLines - 1));
  ctx.restore();
}
var UNDERLINE_DROP_RATIO = 0.12;
var STRIKE_RISE_RATIO = 0.26;
var DECO_THICKNESS_RATIO = 0.055;
var OBLIQUE_TAN = Math.tan(12 * Math.PI / 180);
function obliqueBegin(ctx, baselineY) {
  ctx.save();
  ctx.transform(1, 0, -OBLIQUE_TAN, 1, OBLIQUE_TAN * baselineY, 0);
}
function drawDecoLine(ctx, x, y, width, thickness) {
  ctx.fillRect(x, y - thickness / 2, width, thickness);
}
function grayCss(g) {
  const v = Math.max(0, Math.min(255, Math.round(g * 255)));
  return `rgb(${v}, ${v}, ${v})`;
}
function drawRun(ctx, view, run, text, yTop, exact, paint = (s, x, y) => ctx.fillText(s, x, y)) {
  const cps = Array.from(text);
  const n = Math.min(cps.length, run.glyphCount);
  const unit = run.sizePt / 1e3;
  let prefix1000 = 0;
  if (exact) {
    for (let i = 0; i < n; i++) {
      const cp = cps[i];
      if (cp !== " ") paint(cp, run.xPt + prefix1000 * unit, yTop);
      prefix1000 += view.glyphAdvance(run.glyphFirst + i);
    }
    return;
  }
  let wordStartX = run.xPt;
  let word = "";
  for (let i = 0; i < n; i++) {
    const cp = cps[i];
    if (cp === " ") {
      if (word.length > 0) {
        paint(word, wordStartX, yTop);
        word = "";
      }
      prefix1000 += view.glyphAdvance(run.glyphFirst + i);
      wordStartX = run.xPt + prefix1000 * unit;
    } else {
      if (word.length === 0) wordStartX = run.xPt + prefix1000 * unit;
      word += cp;
      prefix1000 += view.glyphAdvance(run.glyphFirst + i);
    }
  }
  if (word.length > 0) paint(word, wordStartX, yTop);
}
function pageGuideOffsetsPt(view, pageIndex = 0) {
  if (!view.continuous || view.pageCount === 0) return [];
  const pg = view.page(pageIndex);
  const nominal = pg.nominalHeightPt;
  if (!(nominal > 0) || nominal >= pg.heightPt) return [];
  const out = [];
  for (let y = nominal; y < pg.heightPt; y += nominal) out.push(y);
  return out;
}
function renderSurface(container, view, opts) {
  const canvases = [];
  container.replaceChildren();
  const continuous = view.continuous;
  for (let p = 0; p < view.pageCount; p++) {
    const canvas = (container.ownerDocument ?? document).createElement("canvas");
    canvas.className = continuous ? "pl-page pl-page--continuous" : "pl-page";
    canvas.dataset.pageIndex = String(p);
    if (continuous) {
      canvas.dataset.continuous = "true";
      const sup = view.suppressed;
      if (sup.any) {
        const names = [];
        if (sup.pageChrome) names.push("page-chrome");
        if (sup.pageBorders) names.push("page-borders");
        if (sup.lineNumbering) names.push("line-numbering");
        if (sup.pageBreaks) names.push("page-breaks");
        if (sup.pageBackground) names.push("page-background");
        canvas.dataset.suppressed = names.join(" ");
      }
    }
    container.appendChild(canvas);
    renderPage(canvas, view, p, opts);
    canvases.push(canvas);
  }
  return canvases;
}

// src/tile-plan.ts
var PT_PER_IN3 = 72;
var CSS_PX_PER_IN3 = 96;
var DEFAULT_TILE_HEIGHT_CSS = 2048;
var DEFAULT_PAGE_GAP_CSS = 24;
var DEFAULT_OVERSCAN_CSS = 1024;
function tileWindow(starts, itemCount, scrollOffset, viewportSize) {
  if (itemCount <= 0) return { startIndex: 0, endIndex: 0 };
  const first = indexAtOffset(starts, itemCount, scrollOffset);
  const last = indexAtOffset(starts, itemCount, scrollOffset + Math.max(viewportSize, 0));
  return { startIndex: first, endIndex: Math.min(itemCount, last + 1) };
}
function indexAtOffset(starts, itemCount, target) {
  if (itemCount <= 0) return 0;
  if (target <= 0) return 0;
  const last = itemCount - 1;
  if (target >= (starts[itemCount] ?? 0)) return last;
  let lo = 0;
  let hi = last;
  while (lo < hi) {
    const mid = lo + hi >>> 1;
    if ((starts[mid + 1] ?? 0) <= target) lo = mid + 1;
    else hi = mid;
  }
  return lo;
}
function buildTilePlan(view, opts) {
  const zoom = opts?.zoom ?? 1;
  const dprRaw = opts?.dpr ?? (globalThis.devicePixelRatio || 1);
  const backing = Math.min(dprRaw, opts?.maxDpr ?? 2);
  const cssScale = CSS_PX_PER_IN3 / PT_PER_IN3 * zoom;
  const maxSide = opts?.maxCanvasSidePx ?? MAX_CANVAS_SIDE_PX;
  const gapCss = view.continuous ? 0 : opts?.pageGapCss ?? DEFAULT_PAGE_GAP_CSS;
  const capHeightCss = Math.max(1, Math.floor(maxSide / Math.max(backing, 1e-6)));
  const targetCss = Math.min(
    Math.max(1, opts?.tileHeightCss ?? DEFAULT_TILE_HEIGHT_CSS),
    capHeightCss
  );
  const tiles = [];
  const pages = [];
  let cursorCss = 0;
  let widthCss = 0;
  let widthExceedsCap = false;
  let maxBackingSidePx = 0;
  for (let p = 0; p < view.pageCount; p++) {
    const pg = view.page(p);
    const pageWidthCss = pg.widthPt * cssScale;
    const pageHeightCss = pg.heightPt * cssScale;
    widthCss = Math.max(widthCss, pageWidthCss);
    const backingW = Math.round(pageWidthCss * backing);
    if (backingW > maxSide) widthExceedsCap = true;
    maxBackingSidePx = Math.max(maxBackingSidePx, backingW);
    const bandCount = Math.max(1, Math.ceil(pageHeightCss / targetCss));
    const bandHeightPt = pg.heightPt / bandCount;
    const tileFirst = tiles.length;
    for (let b = 0; b < bandCount; b++) {
      const yFromPt = b * bandHeightPt;
      const yToPt = b === bandCount - 1 ? pg.heightPt : (b + 1) * bandHeightPt;
      const hCss = (yToPt - yFromPt) * cssScale;
      const backingH = Math.round(hCss * backing);
      maxBackingSidePx = Math.max(maxBackingSidePx, backingH);
      tiles.push({
        key: `${p}:${b}`,
        index: tiles.length,
        pageIndex: p,
        bandIndex: b,
        yFromPt,
        yToPt,
        topCss: cursorCss + yFromPt * cssScale,
        heightCss: hCss,
        widthCss: pageWidthCss,
        backingWidthPx: backingW,
        backingHeightPx: backingH
      });
    }
    pages.push({
      pageIndex: p,
      topCss: cursorCss,
      heightCss: pageHeightCss,
      widthCss: pageWidthCss,
      widthPt: pg.widthPt,
      heightPt: pg.heightPt,
      tileFirst,
      tileCount: tiles.length - tileFirst
    });
    cursorCss += pageHeightCss + (p === view.pageCount - 1 ? 0 : gapCss);
  }
  const tileStarts = new Array(tiles.length + 1);
  for (let i = 0; i < tiles.length; i++) tileStarts[i] = tiles[i].topCss;
  tileStarts[tiles.length] = cursorCss;
  const pageStarts = new Array(pages.length + 1);
  for (let i = 0; i < pages.length; i++) pageStarts[i] = pages[i].topCss;
  pageStarts[pages.length] = cursorCss;
  const plan = {
    tiles,
    pages,
    documentHeightCss: cursorCss,
    documentWidthCss: widthCss,
    zoom,
    cssScale,
    backing,
    maxBackingSidePx,
    widthExceedsCap,
    pageAtDocumentY(yCss) {
      if (pages.length === 0) return { pageIndex: 0, yPt: 0 };
      const i = indexAtOffset(pageStarts, pages.length, yCss);
      const band = pages[i];
      const rawPt = (yCss - band.topCss) / cssScale;
      return { pageIndex: i, yPt: Math.max(0, Math.min(rawPt, band.heightPt)) };
    },
    documentYForPage(pageIndex, yPt) {
      const band = pages[Math.max(0, Math.min(pageIndex, pages.length - 1))];
      if (!band) return 0;
      return band.topCss + yPt * cssScale;
    },
    documentXForPage(xPt) {
      return xPt * cssScale;
    },
    window(scrollTopCss, viewportHeightCss, overscanCss) {
      const over = overscanCss ?? DEFAULT_OVERSCAN_CSS;
      const from = scrollTopCss - over;
      const size = Math.max(viewportHeightCss, 0) + over * 2;
      const { startIndex, endIndex } = tileWindow(tileStarts, tiles.length, from, size);
      return tiles.slice(startIndex, endIndex);
    },
    renderOptionsFor(base) {
      return {
        ...base,
        zoom,
        dpr: backing,
        maxDpr: backing,
        maxCanvasSidePx: maxSide
      };
    }
  };
  return plan;
}
function fitsUntiled(view, opts) {
  const plan = buildTilePlan(view, { ...opts, tileHeightCss: Number.POSITIVE_INFINITY });
  return !plan.widthExceedsCap && plan.tiles.every((t) => t.bandIndex === 0);
}

// src/tile-painter.ts
var DEFAULT_BLOCK_MARGIN = "0";
function alignMargin(align, blockCss = DEFAULT_BLOCK_MARGIN) {
  if (align === "center") return `${blockCss} auto`;
  return align === "start" ? `${blockCss} auto ${blockCss} 0` : `${blockCss} 0 ${blockCss} auto`;
}
function mkCanvas(doc, className, z) {
  const c = doc.createElement("canvas");
  c.className = className;
  c.style.zIndex = String(z);
  c.style.display = "block";
  c.setAttribute("aria-hidden", "true");
  return c;
}
function createTilePainter(container, opts) {
  const doc = container.ownerDocument;
  const editable = opts.editable ?? false;
  const activation = opts.activation ?? false;
  const align = opts.align ?? "center";
  const blockMarginCss = opts.blockMarginCss ?? DEFAULT_BLOCK_MARGIN;
  const marginCss = alignMargin(align, blockMarginCss);
  const overscanCss = opts.overscanCss ?? DEFAULT_OVERSCAN_CSS;
  const clock = opts.now ?? defaultClock();
  let view = opts.view;
  let planOpts = opts.plan;
  let plan = buildTilePlan(view, planOpts);
  let overlays = {};
  let scrollTopCss = 0;
  let viewportHeightCss = 0;
  const mounted = /* @__PURE__ */ new Map();
  const pool = [];
  let tilePaints = 0;
  let groupsCreated = 0;
  let groupsRecycled = 0;
  let lastFrameTilePaints = 0;
  let lastFrameMs = 0;
  let maxFrameMs = 0;
  let totalPaintMs = 0;
  let peakTilesMounted = 0;
  let maxBackingSidePx = 0;
  let peakBackingPx = 0;
  let detachScroll = null;
  container.replaceChildren();
  container.style.position = "relative";
  container.dataset.testid = "pl-tile-surface";
  applyContainerSize();
  function containerOffsetIn(scroller) {
    if (typeof container.getBoundingClientRect !== "function") return 0;
    if (typeof scroller.getBoundingClientRect !== "function") return 0;
    const scrollTop = Number.isFinite(scroller.scrollTop) ? scroller.scrollTop : 0;
    const border = Number.isFinite(scroller.clientTop) ? scroller.clientTop : 0;
    return container.getBoundingClientRect().top - scroller.getBoundingClientRect().top - border + scrollTop;
  }
  function containerPageTop() {
    if (typeof container.getBoundingClientRect !== "function") return 0;
    const w = globalThis;
    return container.getBoundingClientRect().top + (w.scrollY ?? 0);
  }
  function applyContainerSize() {
    container.style.width = `${plan.documentWidthCss}px`;
    container.style.height = `${plan.documentHeightCss}px`;
    container.style.margin = marginCss;
    container.dataset.tileCount = String(plan.tiles.length);
  }
  function acquire() {
    const recycled = pool.pop();
    if (recycled) {
      groupsRecycled += 1;
      container.appendChild(recycled.wrapper);
      return recycled;
    }
    groupsCreated += 1;
    const wrapper = doc.createElement("div");
    wrapper.className = "pl-tile";
    wrapper.style.position = "absolute";
    wrapper.style.left = "0";
    const text = mkCanvas(doc, "pl-page pl-tile-text", 0);
    const highlight = mkCanvas(doc, "pl-highlight", 1);
    highlight.style.mixBlendMode = HIGHLIGHT_BLEND;
    const selection = mkCanvas(doc, "pl-selection", 2);
    const preedit = editable ? mkCanvas(doc, "pl-preedit", 3) : null;
    const caret = editable ? mkCanvas(doc, "pl-caret", 4) : null;
    const focusRing = activation ? mkCanvas(doc, "pl-focus-ring", 5) : null;
    const stack = [highlight, selection, preedit, caret, focusRing].filter(
      (c) => c !== null
    );
    for (const c of stack) {
      c.style.position = "absolute";
      c.style.top = "0";
      c.style.left = "0";
      c.style.pointerEvents = "none";
    }
    wrapper.append(text, ...stack);
    container.appendChild(wrapper);
    return { wrapper, text, highlight, selection, preedit, caret, focusRing };
  }
  function release(group) {
    for (const c of [
      group.text,
      group.highlight,
      group.selection,
      group.preedit,
      group.caret,
      group.focusRing
    ]) {
      if (!c) continue;
      c.width = 0;
      c.height = 0;
    }
    group.wrapper.remove();
    pool.push(group);
  }
  function overlayOptsFor(tile) {
    return {
      zoom: plan.zoom,
      dpr: plan.backing,
      maxDpr: plan.backing,
      ...opts.colors !== void 0 ? { colors: opts.colors } : {},
      band: { yFromPt: tile.yFromPt, yToPt: tile.yToPt }
    };
  }
  function paintTile(tile, group) {
    const page = plan.pages[tile.pageIndex];
    if (!page) return;
    group.wrapper.className = page.tileCount === 1 ? "pl-tile pl-page-wrap" : "pl-tile";
    group.wrapper.style.top = `${tile.topCss}px`;
    group.wrapper.style.width = `${tile.widthCss}px`;
    group.wrapper.style.height = `${tile.heightCss}px`;
    group.wrapper.dataset.tileKey = tile.key;
    group.wrapper.dataset.pageIndex = String(tile.pageIndex);
    group.wrapper.dataset.bandIndex = String(tile.bandIndex);
    renderBand(
      group.text,
      view,
      tile.pageIndex,
      { yFromPt: tile.yFromPt, yToPt: tile.yToPt },
      plan.renderOptionsFor(opts.render)
    );
    tilePaints += 1;
    lastFrameTilePaints += 1;
    maxBackingSidePx = Math.max(maxBackingSidePx, group.text.width, group.text.height);
    paintTileOverlays(tile, group, page.widthPt, page.heightPt);
  }
  function paintTileOverlays(tile, group, widthPt, heightPt) {
    const o = overlayOptsFor(tile);
    paintHighlights(
      group.highlight,
      tile.pageIndex,
      widthPt,
      heightPt,
      overlays.highlights ?? [],
      o,
      overlays.flash ?? void 0
    );
    paintSelection(group.selection, tile.pageIndex, widthPt, heightPt, overlays.selection ?? [], o);
    if (group.preedit)
      paintPreedit(group.preedit, tile.pageIndex, widthPt, heightPt, overlays.preedit ?? [], o);
    if (group.caret)
      paintCaret(
        group.caret,
        tile.pageIndex,
        widthPt,
        heightPt,
        overlays.caret ?? null,
        overlays.caretVisible ?? false,
        o
      );
    if (group.focusRing)
      paintFocusRing(
        group.focusRing,
        tile.pageIndex,
        widthPt,
        heightPt,
        overlays.focusRing ?? [],
        o
      );
  }
  function sync(repaintKept) {
    const started = clock();
    lastFrameTilePaints = 0;
    const wanted = plan.window(scrollTopCss, viewportHeightCss, overscanCss);
    const wantedKeys = new Set(wanted.map((t) => t.key));
    for (const [key, entry] of mounted) {
      if (wantedKeys.has(key)) continue;
      mounted.delete(key);
      release(entry.group);
    }
    for (const tile of wanted) {
      const existing = mounted.get(tile.key);
      if (existing) {
        if (repaintKept) paintTile(tile, existing.group);
        continue;
      }
      const group = acquire();
      mounted.set(tile.key, { tile, group });
      paintTile(tile, group);
    }
    peakTilesMounted = Math.max(peakTilesMounted, mounted.size);
    let backing = 0;
    for (const e of mounted.values()) backing += e.group.text.width * e.group.text.height;
    peakBackingPx = Math.max(peakBackingPx, backing);
    const ms = clock() - started;
    lastFrameMs = ms;
    maxFrameMs = Math.max(maxFrameMs, ms);
    totalPaintMs += ms;
  }
  return {
    get plan() {
      return plan;
    },
    setViewport(top, height) {
      scrollTopCss = top;
      viewportHeightCss = height;
      sync(false);
    },
    attachScroll(scroller) {
      let detached = false;
      let pending = false;
      const read = () => {
        if (scroller === globalThis) {
          const w = scroller;
          const top = (w.scrollY ?? 0) - containerPageTop();
          this.setViewport(top, sizeOr(w.innerHeight, viewportHeightCss));
          return;
        }
        const el = scroller;
        this.setViewport(
          el.scrollTop - containerOffsetIn(el),
          sizeOr(el.clientHeight, viewportHeightCss)
        );
      };
      const onEvent = () => {
        if (detached) return;
        if (!opts.schedule) {
          read();
          return;
        }
        if (pending) return;
        pending = true;
        opts.schedule(() => {
          pending = false;
          if (!detached) read();
        });
      };
      const target = scroller;
      target.addEventListener("scroll", onEvent, { passive: true });
      target.addEventListener("resize", onEvent, { passive: true });
      detachScroll?.();
      detachScroll = () => {
        detached = true;
        target.removeEventListener("scroll", onEvent);
        target.removeEventListener("resize", onEvent);
        detachScroll = null;
      };
      read();
      return () => detachScroll?.();
    },
    attachViewport() {
      let detached = false;
      let pending = false;
      const view2 = doc.defaultView ?? null;
      const read = () => {
        if (typeof container.getBoundingClientRect !== "function") return;
        const r = container.getBoundingClientRect();
        if (!r || !Number.isFinite(r.top)) return;
        this.setViewport(-r.top, sizeOr(view2?.innerHeight, viewportHeightCss));
      };
      const onEvent = () => {
        if (detached) return;
        if (!opts.schedule) {
          read();
          return;
        }
        if (pending) return;
        pending = true;
        opts.schedule(() => {
          pending = false;
          if (!detached) read();
        });
      };
      doc.addEventListener("scroll", onEvent, { capture: true, passive: true });
      const resizable = typeof view2?.addEventListener === "function" ? view2 : null;
      resizable?.addEventListener("resize", onEvent, { passive: true });
      detachScroll?.();
      detachScroll = () => {
        detached = true;
        doc.removeEventListener("scroll", onEvent, { capture: true });
        resizable?.removeEventListener("resize", onEvent);
        detachScroll = null;
      };
      read();
      return () => detachScroll?.();
    },
    setCaretVisible(visible) {
      overlays = { ...overlays, caretVisible: visible };
      for (const { tile, group } of mounted.values()) {
        if (!group.caret) continue;
        const page = plan.pages[tile.pageIndex];
        if (!page) continue;
        paintCaret(
          group.caret,
          tile.pageIndex,
          page.widthPt,
          page.heightPt,
          overlays.caret ?? null,
          visible,
          overlayOptsFor(tile)
        );
      }
    },
    setOverlays(state, o) {
      overlays = state;
      if (o?.paint === false) return;
      const started = clock();
      for (const { tile, group } of mounted.values()) {
        const page = plan.pages[tile.pageIndex];
        if (!page) continue;
        paintTileOverlays(tile, group, page.widthPt, page.heightPt);
      }
      const ms = clock() - started;
      lastFrameMs = ms;
      maxFrameMs = Math.max(maxFrameMs, ms);
      totalPaintMs += ms;
    },
    invalidate() {
      sync(true);
    },
    setView(next, nextPlanOpts) {
      view = next;
      if (nextPlanOpts !== void 0) planOpts = nextPlanOpts;
      plan = buildTilePlan(view, planOpts);
      applyContainerSize();
      for (const { group } of mounted.values()) release(group);
      mounted.clear();
      sync(true);
    },
    mountedTiles() {
      return plan.tiles.filter((t) => mounted.has(t.key)).map((t) => t.key);
    },
    textCanvas(key) {
      return mounted.get(key)?.group.text;
    },
    pageOriginCss(pageIndex) {
      const page = plan.pages[Math.max(0, Math.min(pageIndex, plan.pages.length - 1))];
      return { leftCss: 0, topCss: page?.topCss ?? 0 };
    },
    stats() {
      return {
        tilesTotal: plan.tiles.length,
        tilesMounted: mounted.size,
        peakTilesMounted,
        tilePaints,
        groupsCreated,
        groupsRecycled,
        lastFrameTilePaints,
        lastFrameMs,
        maxFrameMs,
        totalPaintMs,
        maxBackingSidePx,
        peakBackingPx
      };
    },
    destroy() {
      detachScroll?.();
      for (const { group } of mounted.values()) release(group);
      mounted.clear();
      for (const g of pool) g.wrapper.remove();
      pool.length = 0;
      container.replaceChildren();
    }
  };
}
function sizeOr(value, fallback) {
  return typeof value === "number" && Number.isFinite(value) && value > 0 ? value : fallback;
}
function defaultClock() {
  const perf = globalThis.performance;
  if (perf && typeof perf.now === "function") return () => perf.now();
  return () => Date.now();
}

// src/canvas-surface.ts
var declineSeen = /* @__PURE__ */ new Set();
function warnOnDecline(d) {
  const key = `${d.verb}:${d.blockOrd}:${d.intent}:${d.reason}`;
  if (declineSeen.has(key)) return;
  declineSeen.add(key);
  const why = d.reason === "unidentified" ? "the geometry\u2194document mapping could NOT IDENTIFY this block \u2014 this is a BUG in @norkron/web-page-layout, not a property of the content; please report it" : d.reason === "not-editable" ? "this block carries no editable run slice (a table, an image, a rule, or a block projected out of an opaque markup island)" : "the document declined the verb (typically a merge whose predecessor carries no run slice to join onto)";
  console.warn(
    `[web-page-layout] DECLINED \`${d.verb}\` at block ${d.blockOrd} byte ${d.byteOffset} (intent: ${d.intent}, reason: ${d.reason}) \u2014 the keystroke changed nothing: ${why}. Pass \`onDecline\` to render this to the user instead of the console.`
  );
}
var pointRefusalSeen = /* @__PURE__ */ new Set();
function warnOnPointRefusal(r) {
  if (!r.painted) return;
  const key = `${r.pageIndex}:${r.deadZone?.topPt ?? r.yPt}`;
  if (pointRefusalSeen.has(key)) return;
  pointRefusalSeen.add(key);
  console.warn(
    `[web-page-layout] REFUSED a press on page ${r.pageIndex} at (${r.xPt.toFixed(1)}, ${r.yPt.toFixed(1)}) \u2014 this is PAINTED text that carries no addressable caret yet (a table cell, a list item, or a quote lowered out of an opaque markup island). The caret did NOT move; the surface has scrolled the existing caret into view so the next keystroke is not aimed off-screen. Pass \`onPointRefused\` to render this to the user instead of the console.`
  );
}
var DEFAULT_FLASH_DURATION_MS = 1400;
function readOnlyBridge(inner) {
  const guard = {
    get blocks() {
      return inner.blocks;
    },
    editText: () => guard,
    split: () => guard,
    merge: () => guard,
    // A MUTATION, and the most destructive one there is — so the guard REFUSES
    // it, exactly like the three above, rather than forwarding it the way it
    // forwards `inner` and `routingRefusal`. It is written out even though
    // `EditBridge.deleteBlock` is optional and omitting it would also refuse:
    // a read surface must never be one careless decorator away from removing a
    // block out of the document it is only displaying.
    deleteBlock: () => guard,
    // The decorator link — see `EditBridge.inner`. This is the whole reason a
    // read surface lays out the same document an edit surface does.
    inner,
    ...inner.routingRefusal ? { routingRefusal: (blockOrd) => inner.routingRefusal?.(blockOrd) ?? null } : {}
  };
  return guard;
}
function ownedElement(container, target) {
  if (!target || typeof target !== "object") return null;
  if (target.nodeType !== 1) return null;
  const el = target;
  return container.contains(el) ? el : null;
}
function mkDomRect(left, top, width, height) {
  const Ctor = globalThis.DOMRect;
  if (Ctor) return new Ctor(left, top, width, height);
  return {
    x: left,
    y: top,
    width,
    height,
    top,
    left,
    right: left + width,
    bottom: top + height,
    toJSON() {
      return {
        x: left,
        y: top,
        width,
        height,
        top,
        left,
        right: left + width,
        bottom: top + height
      };
    }
  };
}
function createCanvasSurface(container, opts) {
  const interactivity = opts.interactivity ?? "edit";
  const editable = interactivity === "edit";
  const doc = container.ownerDocument;
  let zoom = opts.zoom ?? 1;
  let dpr = opts.dpr ?? (globalThis.devicePixelRatio || 1);
  const maxDpr = opts.maxDpr ?? 2;
  const overlayOpts = () => ({
    zoom: painter.plan.zoom,
    dpr: painter.plan.backing,
    maxDpr: painter.plan.backing,
    colors: opts.colors
  });
  container.replaceChildren();
  container.style.position = "relative";
  container.dataset.testid = editable ? "pl-canvas-editor" : "pl-canvas-read";
  container.dataset.interactivity = interactivity;
  const docEl = doc.createElement("div");
  docEl.className = "pl-canvas-doc";
  container.appendChild(docEl);
  let dragging = false;
  let highlightRects = [];
  const activation = opts.activatable !== void 0;
  let bands = orderActivatable(opts.activatable ?? []);
  let focusedBandKey = null;
  let flashState = null;
  let flashAlpha = 0;
  let flashTween = null;
  const blink = editable ? new CaretBlink((visible) => painter.setCaretVisible(visible)) : null;
  const core = new EditorCore({
    // READ: the guard makes mutation structurally impossible. Belt to the
    // braces of "no input sink is installed" — even a future caller reaching
    // `applyIntent` cannot move the document.
    bridge: editable ? opts.bridge : readOnlyBridge(opts.bridge),
    relayout: opts.relayout,
    ...opts.metrics !== void 0 ? { metrics: opts.metrics } : {},
    ...opts.now !== void 0 ? { now: opts.now } : {},
    onChange: () => {
      paint(false);
      blink?.restart();
      opts.onChange?.(core);
    },
    // LOUD BY DEFAULT. A host that wants to render the refusal supplies its own
    // handler; a host that supplies none still gets it on the console rather
    // than a keystroke that vanishes without trace.
    onDecline: opts.onDecline ?? warnOnDecline,
    onPointRefused: (r, c) => {
      (opts.onPointRefused ?? warnOnPointRefusal)(r, c);
      revealCaret();
    }
  });
  let lastView = core.getState().layout.view;
  function planOptions() {
    return {
      zoom,
      dpr,
      maxDpr,
      ...opts.pageGapCss !== void 0 ? { pageGapCss: opts.pageGapCss } : {},
      ...opts.tileHeightCss !== void 0 ? { tileHeightCss: opts.tileHeightCss } : {}
    };
  }
  function frameScheduler() {
    if (opts.schedule) return opts.schedule;
    const raf = globalThis.requestAnimationFrame;
    if (typeof raf !== "function") return void 0;
    return (run) => {
      raf(run);
    };
  }
  function renderOptions() {
    const r = {};
    if (opts.fontFamily !== void 0) r.fontFamily = opts.fontFamily;
    if (opts.paper !== void 0) r.paper = opts.paper;
    return Object.keys(r).length > 0 ? r : void 0;
  }
  const painter = createTilePainter(docEl, {
    view: lastView,
    editable,
    activation,
    plan: planOptions(),
    ...(() => {
      const r = renderOptions();
      return r ? { render: r } : {};
    })(),
    ...opts.align !== void 0 ? { align: opts.align } : {},
    ...opts.blockMarginCss !== void 0 ? { blockMarginCss: opts.blockMarginCss } : {},
    ...opts.colors !== void 0 ? { colors: opts.colors } : {},
    ...opts.overscanCss !== void 0 ? { overscanCss: opts.overscanCss } : {},
    ...(() => {
      const s = frameScheduler();
      return s ? { schedule: s } : {};
    })()
  });
  const scrollProbe = doc.createElement("div");
  scrollProbe.className = "pl-scroll-probe";
  scrollProbe.setAttribute("aria-hidden", "true");
  scrollProbe.style.cssText = "position:absolute;left:0;width:1px;height:1px;pointer-events:none";
  docEl.appendChild(scrollProbe);
  const sink = editable ? new HiddenInputSink(docEl, {
    onIntent: (intent) => core.applyIntent(intent),
    onNav: (cmd) => core.applyNav(cmd),
    readText: () => {
      const t = core.selectedText();
      return t.length > 0 ? t : null;
    },
    // IME: paint the provisional buffer through the real layout so a CJK /
    // Indic / dead-key / autocorrect user SEES what they are composing.
    // Nothing is committed until `compositionend` (see EditorCore.setPreedit).
    onCompositionUpdate: (text) => core.setPreedit(text),
    onCompositionEnd: () => core.clearPreedit(),
    ...opts.onShortcut ? { onShortcut: opts.onShortcut } : {},
    ...opts.readClipboard ? { readClipboard: opts.readClipboard } : {},
    ...opts.onPasteFlavours ? { onPasteFlavours: opts.onPasteFlavours } : {}
  }) : null;
  function currentSelection() {
    const slices = core.selectionSlices();
    if (slices.length === 0) return null;
    const first = slices[0];
    const last = slices[slices.length - 1];
    if (!first || !last) return null;
    if (slices.length === 1 && last.end <= first.start) return null;
    return {
      blockOrd: first.blockOrd,
      start: first.start,
      end: last.end,
      text: core.selectedText(),
      endBlockOrd: last.blockOrd,
      blocks: slices
    };
  }
  function overlayStateNow() {
    return {
      highlights: highlightRects,
      flash: flashState,
      selection: core.selectionRects(),
      preedit: editable ? core.preeditRects() : [],
      caret: editable ? core.caretRect() : null,
      caretVisible: blink?.isVisible ?? false,
      focusRing: focusRingRects()
    };
  }
  function focusedBand() {
    if (focusedBandKey === null) return null;
    return bands.find((b) => b.key === focusedBandKey) ?? null;
  }
  function focusRingRects() {
    const band = focusedBand();
    if (!band) return [];
    const from = { blockOrd: band.blockOrd, byteOffset: Math.min(band.start, band.end) };
    const to = { blockOrd: band.blockOrd, byteOffset: Math.max(band.start, band.end) };
    if (comparePos(from, to) === 0) return [];
    return core.rangeRects(from, to);
  }
  function stepActivation(step) {
    const current = focusedBandKey === null ? null : bands.findIndex((b) => b.key === focusedBandKey);
    const next = nextActivationIndex(bands.length, current === -1 ? null : current, step);
    focusedBandKey = next === null ? null : bands[next]?.key ?? null;
    paint(false);
    return focusedBand();
  }
  function clearActivation() {
    if (focusedBandKey === null) return false;
    focusedBandKey = null;
    paint(false);
    return true;
  }
  function activateFocused(source) {
    const band = focusedBand();
    if (!band) return false;
    opts.onActivateRange?.(band, source);
    return true;
  }
  function onBlur() {
    clearActivation();
  }
  function paint(force) {
    const state = overlayStateNow();
    const view = core.getState().layout.view;
    const replan2 = view !== lastView;
    painter.setOverlays(state, { paint: !(replan2 || force) });
    if (replan2) {
      lastView = view;
      painter.setView(view, planOptions());
    } else if (force) {
      painter.invalidate();
    }
    if (editable) positionInput(state.caret ?? null);
  }
  function replan() {
    painter.setOverlays(overlayStateNow(), { paint: false });
    lastView = core.getState().layout.view;
    painter.setView(lastView, planOptions());
    if (editable) positionInput(core.caretRect());
  }
  function positionInput(caret) {
    if (!caret || !sink) return;
    const css = caretRectToCss(caret, painter.pageOriginCss(caret.pageIndex), overlayOpts());
    sink.moveTo(css.leftCss, css.topCss, css.heightCss);
  }
  function docRect() {
    if (typeof docEl.getBoundingClientRect !== "function") return null;
    const r = docEl.getBoundingClientRect();
    if (!r || !Number.isFinite(r.left) || !Number.isFinite(r.top)) return null;
    return { left: r.left, top: r.top };
  }
  function pointToPage(clientX, clientY) {
    const base = docRect();
    if (!base) return null;
    const plan = painter.plan;
    const hit = plan.pageAtDocumentY(clientY - base.top);
    return { pageIndex: hit.pageIndex, xPt: (clientX - base.left) / plan.cssScale, yPt: hit.yPt };
  }
  function revealCaret() {
    if (!editable) return;
    const rect = core.caretRect();
    if (!rect) return;
    handle.scrollTo([rect], { animate: true });
  }
  function pointAtClient(clientX, clientY, extend) {
    const pt = pointToPage(clientX, clientY);
    if (!pt) return;
    core.pointAt(pt.pageIndex, pt.xPt, pt.yPt, extend);
    if (!editable) paint(false);
  }
  function takeFocus() {
    if (sink) sink.focus();
    else container.focus();
  }
  let touch = null;
  function endTouch() {
    if (touch?.timer != null) clearTimeout(touch.timer);
    touch = null;
  }
  function onPointerDown(ev) {
    const target = ownedElement(container, ev.target);
    if (!target) return;
    const gesture = gestureFromPointerDown(ev);
    if (!gesture) return;
    if (gesture.kind === "defer") {
      beginTouch(ev, target);
      return;
    }
    ev.preventDefault();
    if (ev.detail >= TABLE_CLICK_DETAIL && selectTableAtClient(ev.clientX, ev.clientY)) {
      dragging = false;
      takeFocus();
      opts.onSelect?.(currentSelection());
      return;
    }
    if (ev.detail >= TRIPLE_CLICK_DETAIL && selectBlockAtClient(ev.clientX, ev.clientY)) {
      dragging = false;
      takeFocus();
      opts.onSelect?.(currentSelection());
      return;
    }
    pointAtClient(ev.clientX, ev.clientY, ev.shiftKey);
    dragging = true;
    takeFocus();
    target.setPointerCapture?.(ev.pointerId);
  }
  function beginTouch(ev, target) {
    endTouch();
    const track = {
      pointerId: ev.pointerId,
      down: { x: ev.clientX, y: ev.clientY, t: Date.now() },
      target,
      moved: false,
      selecting: false,
      timer: null
    };
    touch = track;
    if (typeof setTimeout !== "function") return;
    track.timer = setTimeout(() => {
      if (touch !== track || track.moved) return;
      track.timer = null;
      if (!selectWordAtClient(track.down.x, track.down.y)) return;
      track.selecting = true;
      dragging = true;
      takeFocus();
      track.target.setPointerCapture?.(track.pointerId);
    }, LONG_PRESS_MS);
  }
  function onPointerMove(ev) {
    if (touch && ev.pointerId === touch.pointerId) {
      if (!touch.selecting) {
        if (exceedsTapSlop(touch.down, { x: ev.clientX, y: ev.clientY})) {
          touch.moved = true;
          if (touch.timer != null) clearTimeout(touch.timer);
          touch.timer = null;
        }
        return;
      }
      ev.preventDefault();
      pointAtClient(ev.clientX, ev.clientY, true);
      return;
    }
    if (!dragging) return;
    pointAtClient(ev.clientX, ev.clientY, true);
  }
  function onPointerUp(ev) {
    if (touch && ev.pointerId === touch.pointerId) {
      const outcome = touchOutcome(
        touch.down,
        { x: ev.clientX, y: ev.clientY},
        touch.selecting
      );
      endTouch();
      dragging = false;
      if (outcome === "tap") {
        pointAtClient(ev.clientX, ev.clientY, false);
        takeFocus();
      }
      if (outcome !== "scroll") opts.onSelect?.(currentSelection());
      return;
    }
    if (!dragging) return;
    dragging = false;
    opts.onSelect?.(currentSelection());
  }
  function onPointerCancel() {
    endTouch();
    dragging = false;
  }
  function selectWordAtClient(clientX, clientY) {
    const pt = pointToPage(clientX, clientY);
    if (!pt) return false;
    const pos = core.pointAt(pt.pageIndex, pt.xPt, pt.yPt, false);
    if (!pos) return false;
    const text = core.getState().bridge.blocks[pos.blockOrd]?.text ?? "";
    const start = prevWordBoundary(text, pos.byteOffset);
    const end = nextWordBoundary(text, pos.byteOffset);
    if (end <= start) return false;
    core.setSelection({
      anchor: { blockOrd: pos.blockOrd, byteOffset: start },
      focus: { blockOrd: pos.blockOrd, byteOffset: end }
    });
    paint(false);
    return true;
  }
  function selectBlockAtClient(clientX, clientY) {
    const pt = pointToPage(clientX, clientY);
    if (!pt) return false;
    const pos = core.pointAt(pt.pageIndex, pt.xPt, pt.yPt, false);
    if (!pos) return false;
    const text = core.getState().bridge.blocks[pos.blockOrd]?.text;
    if (text == null) return false;
    const end = byteLen(text);
    if (end <= 0) return false;
    core.setSelection({
      anchor: { blockOrd: pos.blockOrd, byteOffset: 0 },
      focus: { blockOrd: pos.blockOrd, byteOffset: end }
    });
    paint(false);
    return true;
  }
  function selectTableAtClient(clientX, clientY) {
    const pt = pointToPage(clientX, clientY);
    if (!pt) return false;
    const pos = core.pointAt(pt.pageIndex, pt.xPt, pt.yPt, false);
    if (!pos) return false;
    const sel = canvasSelectTable(core.getState().bridge, pos.blockOrd);
    if (!sel) return false;
    core.setSelection(sel);
    paint(false);
    return true;
  }
  function onDoubleClick(ev) {
    if (ev.detail >= TRIPLE_CLICK_DETAIL) return;
    if (!selectWordAtClient(ev.clientX, ev.clientY)) return;
    opts.onSelect?.(currentSelection());
  }
  function onKeyDown(ev) {
    if ((ev.metaKey || ev.ctrlKey) && ev.key.toLowerCase() === "a") {
      ev.preventDefault();
      core.selectAll();
      paint(false);
      opts.onSelect?.(currentSelection());
      return;
    }
    if (activationKeyDown(ev)) return;
    if (ev.key === "Escape") clearSelection();
  }
  function activationKeyDown(ev) {
    if (bands.length === 0) return false;
    if (ev.metaKey || ev.ctrlKey || ev.altKey) return false;
    if (ev.key === "Tab" || ev.key === "ArrowRight" || ev.key === "ArrowLeft") {
      const step = ev.key === "ArrowLeft" || ev.key === "Tab" && ev.shiftKey ? -1 : 1;
      const landed = stepActivation(step);
      if (landed === null) return false;
      ev.preventDefault();
      revealBand();
      return true;
    }
    if (ev.key === "Enter" || ev.key === " " || ev.key === "Spacebar") {
      if (!activateFocused("keyboard")) return false;
      ev.preventDefault();
      return true;
    }
    if (ev.key === "Escape" && clearActivation()) {
      ev.preventDefault();
      return true;
    }
    return false;
  }
  function revealBand() {
    const rects = focusRingRects();
    if (rects.length === 0) return;
    handle.scrollTo(rects, { block: "nearest", ifNeeded: true });
  }
  function onCopy(ev) {
    const text = core.selectedText();
    if (text.length === 0) return;
    ev.clipboardData?.setData("text/plain", text);
    ev.preventDefault();
  }
  function clearSelection() {
    const focus = core.getState().selection.focus;
    core.setSelection({ anchor: focus, focus });
    paint(false);
    opts.onSelect?.(null);
  }
  function resolveScroller(band) {
    if (opts.scroller !== void 0) {
      return { scroller: opts.scroller, anyTravel: true, explicit: true };
    }
    const view = doc.defaultView;
    if (!view || typeof view.getComputedStyle !== "function") {
      return { scroller: null, anyTravel: false, explicit: false };
    }
    let anyTravel = false;
    const reaches = (el) => {
      const surf = scrollSurfaceFor(el);
      if (!surf || !hasMeaningfulTravel(surf.maxScrollTopCss)) return false;
      anyTravel = true;
      const topCss = bandTopIn(el, band.probe, surf);
      if (topCss == null) return false;
      return canReachBand({
        bandTopCss: topCss,
        bandHeightCss: band.heightCss,
        currentTopCss: surf.scrollTopCss,
        viewportHeightCss: surf.viewportHeightCss,
        maxScrollTopCss: surf.maxScrollTopCss,
        block: band.block,
        marginCss: band.marginCss
      });
    };
    const found = pickScrollAncestor(
      container,
      (el) => {
        const st = view.getComputedStyle(el);
        return overflowScrolls(st?.overflowY, st?.overflow);
      },
      reaches,
      (el) => el.parentElement
    );
    if (found) return { scroller: found, anyTravel: true, explicit: false };
    if (reaches(view)) return { scroller: view, anyTravel: true, explicit: false };
    return { scroller: null, anyTravel, explicit: false };
  }
  let scroller = null;
  let scrollSurface = null;
  const animatorSurface = {
    get scrollTopCss() {
      return scrollSurface?.scrollTopCss ?? 0;
    },
    get maxScrollTopCss() {
      return scrollSurface?.maxScrollTopCss ?? 0;
    },
    get viewportHeightCss() {
      return scrollSurface?.viewportHeightCss ?? 0;
    },
    scrollToCss(topCss) {
      scrollSurface?.scrollToCss(topCss);
    }
  };
  const scrollAnimator = createScrollAnimator(animatorSurface, {
    ...opts.scrollDurationMs !== void 0 ? { durationMs: opts.scrollDurationMs } : {},
    ...opts.scrollEasing !== void 0 ? { easing: opts.scrollEasing } : {},
    ...opts.reducedMotion !== void 0 ? { reducedMotion: opts.reducedMotion } : {},
    ...opts.scrollSchedule !== void 0 ? { schedule: opts.scrollSchedule } : {},
    ...opts.scrollNow !== void 0 ? { now: opts.scrollNow } : {}
  });
  function bindScroller(next) {
    if (next !== scroller) {
      scroller = next;
      scrollSurface = next ? scrollSurfaceFor(next) : null;
    }
    return scrollSurface !== null && hasMeaningfulTravel(scrollSurface.maxScrollTopCss);
  }
  function interruptScroll() {
    scrollAnimator.interrupt();
  }
  const PASSIVE = { passive: true };
  container.addEventListener("wheel", interruptScroll, PASSIVE);
  container.addEventListener("touchstart", interruptScroll, PASSIVE);
  container.addEventListener("keydown", interruptScroll, PASSIVE);
  container.addEventListener("pointerdown", interruptScroll, PASSIVE);
  function bandTopIn(host, el, surf) {
    if (typeof el.getBoundingClientRect !== "function") return null;
    const r = el.getBoundingClientRect();
    if (!r || !Number.isFinite(r.top)) return null;
    let base = 0;
    if (host && host !== doc.defaultView) {
      const hostEl = host;
      if (typeof hostEl.getBoundingClientRect !== "function") return null;
      const hr = hostEl.getBoundingClientRect();
      if (!hr || !Number.isFinite(hr.top)) return null;
      base = hr.top + (hostEl.clientTop ?? 0);
    }
    return r.top - base + surf.scrollTopCss;
  }
  function probeOnScreen(el) {
    const view = doc.defaultView;
    const vh = view?.innerHeight;
    if (typeof vh !== "number" || !(vh > 0)) return false;
    if (typeof el.getBoundingClientRect !== "function") return false;
    const r = el.getBoundingClientRect();
    if (!r || !Number.isFinite(r.top)) return false;
    return r.top >= 0 && r.top <= vh;
  }
  container.style.touchAction = SURFACE_TOUCH_ACTION;
  container.addEventListener("pointerdown", onPointerDown);
  container.addEventListener("pointermove", onPointerMove);
  container.addEventListener("pointerup", onPointerUp);
  container.addEventListener("pointercancel", onPointerCancel);
  container.addEventListener("dblclick", onDoubleClick);
  if (!editable) {
    container.tabIndex = 0;
    container.addEventListener("keydown", onKeyDown);
    container.addEventListener("copy", onCopy);
  }
  if (activation) {
    container.addEventListener("blur", onBlur);
  }
  const detachScroll = opts.scroller === void 0 ? painter.attachViewport() : opts.scroller ? painter.attachScroll(opts.scroller) : null;
  if (opts.scroller === null) {
    const h = container.clientHeight;
    painter.setViewport(0, Number.isFinite(h) && h > 0 ? h : painter.plan.documentHeightCss);
  }
  paint(true);
  blink?.restart();
  const handle = {
    core,
    interactivity,
    focus: () => sink ? sink.focus() : container.focus(),
    repaint: () => paint(true),
    refresh: () => core.refresh(),
    applyBridge: (next, o) => core.applyBridge(next, o),
    historyBarrier: () => core.historyBarrier(),
    setHighlights: (rects) => {
      highlightRects = rects;
      paint(false);
    },
    flashHighlights: (rects, o) => {
      if (rects.length === 0) return () => {
      };
      flashTween?.destroy();
      flashAlpha = 1;
      flashState = { rects, color: o.color, alpha: 1 };
      const duration = Math.max(
        0,
        o.durationMs ?? opts.flashDurationMs ?? DEFAULT_FLASH_DURATION_MS
      );
      flashTween = createTween({
        durationMs: duration,
        // Under reduced motion the wash is HELD for the same window rather
        // than skipped — the reveal still happens, it just does not move.
        holdMs: duration,
        ...o.easing ?? opts.flashEasing ? { easing: o.easing ?? opts.flashEasing } : {},
        ...opts.flashNow !== void 0 ? { now: opts.flashNow } : {},
        ...opts.flashSchedule !== void 0 ? { schedule: opts.flashSchedule } : {},
        ...opts.reducedMotion !== void 0 ? { reducedMotion: opts.reducedMotion } : {},
        ...opts.flashSetTimer !== void 0 ? { setTimer: opts.flashSetTimer } : {},
        ...opts.flashClearTimer !== void 0 ? { clearTimer: opts.flashClearTimer } : {},
        read: () => flashAlpha,
        write: (v) => {
          flashAlpha = v;
          flashState = v <= 0 ? null : { rects, color: o.color, alpha: v };
          paint(false);
        },
        onEnd: (reason) => {
          if (reason !== "cancelled") {
            flashAlpha = 0;
            flashState = null;
            paint(false);
          }
          o.onEnd?.(reason);
        }
      });
      paint(false);
      flashTween.to(0);
      return () => {
        flashTween?.cancel();
        flashAlpha = 0;
        flashState = null;
        paint(false);
      };
    },
    pagePointAt: (clientX, clientY) => pointToPage(clientX, clientY),
    positionAt: (clientX, clientY) => {
      const pt = pointToPage(clientX, clientY);
      if (!pt) return null;
      const state = core.getState().layout;
      return hitTest(state.view, state.index, pt.pageIndex, pt.xPt, pt.yPt);
    },
    rangeRects: (blockOrd, start, end) => {
      const from = { blockOrd, byteOffset: Math.min(start, end) };
      const to = { blockOrd, byteOffset: Math.max(start, end) };
      if (comparePos(from, to) === 0) return [];
      return core.rangeRects(from, to);
    },
    setActivatable: (ranges) => {
      if (!activation) {
        console.warn(
          "[web-page-layout] `setActivatable` on a surface mounted WITHOUT the `activatable` option: the focus-ring layer does not exist, so keyboard focus will move with NO visible indicator (WCAG 2.4.7). Pass `activatable: []` at `createCanvasSurface` to opt in."
        );
      }
      bands = orderActivatable(ranges);
      if (focusedBandKey !== null && !bands.some((b) => b.key === focusedBandKey)) {
        focusedBandKey = null;
      }
      paint(false);
    },
    activationFocus: focusedBand,
    focusActivatable: (key) => {
      if (key === null) {
        clearActivation();
        return true;
      }
      if (!bands.some((b) => b.key === key)) return false;
      focusedBandKey = key;
      paint(false);
      return true;
    },
    moveActivationFocus: stepActivation,
    activateFocused: (source = "keyboard") => activateFocused(source),
    /** Projected through the PLAN, so a rect on an unpainted tile still yields a
     *  correct viewport position — a comment popover must open at the anchor even
     *  when the anchor's tile is not mounted. */
    viewportRectFor: (rects) => {
      if (rects.length === 0) return null;
      const base = docRect();
      if (!base) return null;
      const plan = painter.plan;
      const scale = plan.cssScale;
      let left = Number.POSITIVE_INFINITY;
      let top = Number.POSITIVE_INFINITY;
      let right = Number.NEGATIVE_INFINITY;
      let bottom = Number.NEGATIVE_INFINITY;
      for (const r of rects) {
        const rTop = base.top + plan.documentYForPage(r.pageIndex, r.yPt);
        const rLeft = base.left + plan.documentXForPage(r.xPt);
        left = Math.min(left, rLeft);
        top = Math.min(top, rTop);
        right = Math.max(right, rLeft + r.wPt * scale);
        bottom = Math.max(bottom, rTop + r.hPt * scale);
      }
      return mkDomRect(left, top, right - left, bottom - top);
    },
    scrollTo: (rects, o) => {
      const first = rects[0];
      if (!first) return false;
      const plan = painter.plan;
      if (!plan.pages[first.pageIndex]) return false;
      scrollProbe.style.top = `${plan.documentYForPage(first.pageIndex, first.yPt)}px`;
      const band = {
        probe: scrollProbe,
        heightCss: Math.max(first.hPt * plan.cssScale, 1),
        block: o?.block ?? "center",
        marginCss: o?.marginCss ?? DEFAULT_SCROLL_MARGIN_CSS
      };
      const res = resolveScroller(band);
      const bound = bindScroller(res.scroller);
      const top = bound && scrollSurface ? bandTopIn(scroller, scrollProbe, scrollSurface) : null;
      if (!bound || !scrollSurface || top == null) {
        const onScreen = probeOnScreen(scrollProbe);
        if (onScreen) return true;
        if (typeof scrollProbe.scrollIntoView !== "function") return false;
        scrollProbe.scrollIntoView({ behavior: "smooth", block: "center" });
        return res.explicit || res.anyTravel;
      }
      const target = scrollTopForBand({
        topCss: top,
        heightCss: band.heightCss,
        currentTopCss: scrollSurface.scrollTopCss,
        viewportHeightCss: scrollSurface.viewportHeightCss,
        maxScrollTopCss: scrollSurface.maxScrollTopCss,
        block: band.block,
        marginCss: band.marginCss,
        ...o?.ifNeeded !== void 0 ? { ifNeeded: o.ifNeeded } : {}
      });
      if (target == null) {
        scrollAnimator.cancel();
        return true;
      }
      if (o?.animate === false) {
        scrollAnimator.cancel();
        scrollSurface.scrollToCss(target);
        return true;
      }
      scrollAnimator.to(target);
      return true;
    },
    cancelScrollAnimation: () => scrollAnimator.interrupt(),
    selection: currentSelection,
    clearSelection,
    setZoom: (z) => {
      if (z === zoom) return;
      zoom = z;
      replan();
    },
    setDpr: (d) => {
      if (d === dpr) return;
      dpr = d;
      replan();
    },
    setViewport: (top, height) => painter.setViewport(top, height),
    get plan() {
      return painter.plan;
    },
    tileStats: () => painter.stats(),
    destroy: () => {
      blink?.stop();
      endTouch();
      detachScroll?.();
      sink?.destroy();
      scrollAnimator.destroy();
      flashTween?.destroy();
      flashTween = null;
      flashState = null;
      container.removeEventListener("wheel", interruptScroll);
      container.removeEventListener("touchstart", interruptScroll);
      container.removeEventListener("keydown", interruptScroll);
      container.removeEventListener("pointerdown", interruptScroll);
      container.removeEventListener("pointerdown", onPointerDown);
      container.removeEventListener("pointermove", onPointerMove);
      container.removeEventListener("pointerup", onPointerUp);
      container.removeEventListener("pointercancel", onPointerCancel);
      container.removeEventListener("dblclick", onDoubleClick);
      container.removeEventListener("keydown", onKeyDown);
      container.removeEventListener("copy", onCopy);
      container.removeEventListener("blur", onBlur);
      painter.destroy();
      docEl.remove();
    }
  };
  return handle;
}

// src/canvas-editor.ts
function createCanvasEditor(container, opts) {
  return createCanvasSurface(container, { ...opts, interactivity: "edit" });
}

// src/plinput.ts
var MAGIC2 = 1178815568;
var HEADER_LEN2 = 26;
var HEADER_LEN_V1 = 106;
var HEADER_LEN_V2 = 163;
var DECO_PRESENT_COLOR = 1;
var DECO_PRESENT_BORDERS = 2;
var DECO_PRESENT_LINE_NUM = 4;
var FLAG_GLYPH_GRANULAR2 = 1;
var FLAG_MEASURED_GREEDY = 2;
var PLCF_FLAG_CONTINUOUS = 4;
var BORDER_STYLE_TO_ORD = {
  solid: 0,
  dashed: 1,
  dotted: 2,
  double: 3
};
var PAGE_BORDER_SCOPE_TO_ORD = {
  all_pages: 0,
  first_page_only: 1,
  all_but_first: 2
};
var PAGE_BORDER_OFFSET_FROM_TO_ORD = {
  page: 0,
  text: 1
};
var LINE_NUMBER_RESTART_TO_ORD = {
  continuous: 0,
  new_page: 1,
  new_section: 2
};
var DEFAULT_HEADING_SIZES_PT = [
  24,
  18,
  14,
  12,
  11,
  10
];
var ZERO6 = [0, 0, 0, 0, 0, 0];
var A4_DEFAULT = {
  pageWidthPt: 595,
  pageHeightPt: 842,
  marginTopPt: 54,
  marginRightPt: 54,
  marginBottomPt: 54,
  marginLeftPt: 54,
  fontSetId: 0,
  justify: true,
  hyphenate: false,
  glyphGranular: true,
  measuredGreedy: false,
  continuous: false,
  headingSizesPt: [...DEFAULT_HEADING_SIZES_PT],
  headingSpaceBeforePt: [...ZERO6],
  headingSpaceAfterPt: [...ZERO6]
};
var CSS_PX_PER_IN4 = 96;
var PT_PER_IN4 = 72;
var MIN_COLUMN_WIDTH_PT = 144;
var MAX_COLUMN_WIDTH_PT = 2e3;
function columnWidthPtForCss(cssWidthPx, zoom = 1) {
  if (!Number.isFinite(cssWidthPx) || cssWidthPx <= 0) return null;
  if (!Number.isFinite(zoom) || zoom <= 0) return null;
  const pt = Math.round(cssWidthPx * PT_PER_IN4 / CSS_PX_PER_IN4 / zoom);
  if (pt < MIN_COLUMN_WIDTH_PT) return MIN_COLUMN_WIDTH_PT;
  if (pt > MAX_COLUMN_WIDTH_PT) return MAX_COLUMN_WIDTH_PT;
  return pt;
}
function normalizeColor(c) {
  if (c == null) return null;
  if (typeof c !== "string") {
    return [clampByte(c[0]), clampByte(c[1]), clampByte(c[2])];
  }
  const hex = c.replace("#", "");
  if (hex.length !== 6) return null;
  const r = Number.parseInt(hex.slice(0, 2), 16);
  const g = Number.parseInt(hex.slice(2, 4), 16);
  const b = Number.parseInt(hex.slice(4, 6), 16);
  if (Number.isNaN(r) || Number.isNaN(g) || Number.isNaN(b)) return null;
  return [r, g, b];
}
function clampByte(v) {
  return Math.max(0, Math.min(255, Math.round(v)));
}
function resolveConfig(cfg) {
  const merged = { ...A4_DEFAULT, ...cfg ?? {} };
  const color = normalizeColor(cfg?.headingColor);
  const { headingColor: _drop, ...rest } = merged;
  return color ? { ...rest, headingColor: color } : rest;
}
function emitV1(c) {
  if (c.headingColor != null) return true;
  if (c.bodyLeadingPt != null) return true;
  for (let i = 0; i < 6; i++)
    if ((c.headingSizesPt[i] ?? 0) !== (DEFAULT_HEADING_SIZES_PT[i] ?? 0)) return true;
  for (let i = 0; i < 6; i++) if ((c.headingSpaceBeforePt[i] ?? 0) !== 0) return true;
  for (let i = 0; i < 6; i++) if ((c.headingSpaceAfterPt[i] ?? 0) !== 0) return true;
  return false;
}
function emitV2(c) {
  return c.pageColor != null || c.pageBorders != null || c.lineNumbering != null;
}
function hexToRgb(hex) {
  const h = hex.replace("#", "");
  if (h.length !== 6) return [0, 0, 0];
  const r = Number.parseInt(h.slice(0, 2), 16);
  const g = Number.parseInt(h.slice(2, 4), 16);
  const b = Number.parseInt(h.slice(4, 6), 16);
  return [Number.isNaN(r) ? 0 : r, Number.isNaN(g) ? 0 : g, Number.isNaN(b) ? 0 : b];
}
function writeBorderEdge(dv, off, edge) {
  dv.setUint8(off, edge ? 1 : 0);
  dv.setFloat32(off + 1, edge?.widthPt ?? 0.5, true);
  const [r, g, b] = hexToRgb(edge?.color ?? "#000000");
  dv.setUint8(off + 5, r);
  dv.setUint8(off + 6, g);
  dv.setUint8(off + 7, b);
  dv.setUint8(off + 8, BORDER_STYLE_TO_ORD[edge?.style ?? "solid"]);
}
function encodePlInput(blockListBytes, cfg) {
  const c = resolveConfig(cfg);
  const v2 = emitV2(c);
  const v1 = v2 || emitV1(c);
  const headerLen = v2 ? HEADER_LEN_V2 : v1 ? HEADER_LEN_V1 : HEADER_LEN2;
  const out = new Uint8Array(headerLen + blockListBytes.byteLength);
  const dv = new DataView(out.buffer);
  dv.setUint32(0, MAGIC2, true);
  dv.setUint16(4, c.pageWidthPt & 65535, true);
  dv.setUint16(6, c.pageHeightPt & 65535, true);
  dv.setUint16(8, c.marginTopPt & 65535, true);
  dv.setUint16(10, c.marginRightPt & 65535, true);
  dv.setUint16(12, c.marginBottomPt & 65535, true);
  dv.setUint16(14, c.marginLeftPt & 65535, true);
  dv.setUint16(16, c.fontSetId & 65535, true);
  dv.setUint8(18, c.justify ? 1 : 0);
  dv.setUint8(19, c.hyphenate ? 1 : 0);
  dv.setUint8(
    20,
    (c.glyphGranular ? FLAG_GLYPH_GRANULAR2 : 0) | (c.measuredGreedy ? FLAG_MEASURED_GREEDY : 0) | (c.continuous ? PLCF_FLAG_CONTINUOUS : 0)
  );
  dv.setUint8(21, v2 ? 2 : v1 ? 1 : 0);
  dv.setUint32(22, blockListBytes.byteLength, true);
  if (v1) {
    for (let i = 0; i < 6; i++) dv.setFloat32(26 + i * 4, c.headingSizesPt[i] ?? 0, true);
    if (c.headingColor) {
      dv.setUint8(50, 1);
      dv.setUint8(51, clampByte(c.headingColor[0]));
      dv.setUint8(52, clampByte(c.headingColor[1]));
      dv.setUint8(53, clampByte(c.headingColor[2]));
    }
    dv.setFloat32(54, c.bodyLeadingPt ?? 0, true);
    for (let i = 0; i < 6; i++) dv.setFloat32(58 + i * 4, c.headingSpaceBeforePt[i] ?? 0, true);
    for (let i = 0; i < 6; i++) dv.setFloat32(82 + i * 4, c.headingSpaceAfterPt[i] ?? 0, true);
  }
  if (v2) {
    let present = 0;
    if (c.pageColor != null) present |= DECO_PRESENT_COLOR;
    if (c.pageBorders != null) present |= DECO_PRESENT_BORDERS;
    if (c.lineNumbering != null) present |= DECO_PRESENT_LINE_NUM;
    dv.setUint8(106, present);
    if (c.pageColor != null) {
      const [r, g, b] = hexToRgb(c.pageColor);
      dv.setUint8(107, r);
      dv.setUint8(108, g);
      dv.setUint8(109, b);
    }
    if (c.pageBorders != null) {
      const pb = c.pageBorders;
      writeBorderEdge(dv, 110, pb.top);
      writeBorderEdge(dv, 119, pb.left);
      writeBorderEdge(dv, 128, pb.bottom);
      writeBorderEdge(dv, 137, pb.right);
      dv.setUint8(146, PAGE_BORDER_SCOPE_TO_ORD[pb.scope ?? "all_pages"]);
      dv.setUint8(147, PAGE_BORDER_OFFSET_FROM_TO_ORD[pb.offsetFrom ?? "page"]);
      dv.setFloat32(148, pb.offsetPt ?? 24, true);
    }
    if (c.lineNumbering != null) {
      const ln = c.lineNumbering;
      dv.setUint32(152, ln.start ?? 1, true);
      dv.setUint16(156, ln.countBy ?? 1, true);
      dv.setUint8(158, LINE_NUMBER_RESTART_TO_ORD[ln.restart ?? "new_page"]);
      dv.setFloat32(159, ln.distancePt ?? 18, true);
    }
  }
  out.set(blockListBytes, headerLen);
  return out;
}
function decodePlInputHeader(input) {
  if (input.byteLength < HEADER_LEN2) throw new Error("web-page-layout: pl input truncated (header)");
  const dv = new DataView(input.buffer, input.byteOffset, input.byteLength);
  if (dv.getUint32(0, true) !== MAGIC2) throw new Error("web-page-layout: pl input bad magic");
  const flags = dv.getUint8(20);
  const version2 = dv.getUint8(21);
  const headerLen = version2 >= 1 ? HEADER_LEN_V1 : HEADER_LEN2;
  if (input.byteLength < headerLen)
    throw new Error("web-page-layout: pl input truncated (v1 header)");
  const blockBytesLen = dv.getUint32(22, true);
  const base = {
    pageWidthPt: dv.getUint16(4, true),
    pageHeightPt: dv.getUint16(6, true),
    marginTopPt: dv.getUint16(8, true),
    marginRightPt: dv.getUint16(10, true),
    marginBottomPt: dv.getUint16(12, true),
    marginLeftPt: dv.getUint16(14, true),
    fontSetId: dv.getUint16(16, true),
    justify: dv.getUint8(18) !== 0,
    hyphenate: dv.getUint8(19) !== 0,
    glyphGranular: (flags & FLAG_GLYPH_GRANULAR2) !== 0,
    measuredGreedy: (flags & FLAG_MEASURED_GREEDY) !== 0,
    continuous: (flags & PLCF_FLAG_CONTINUOUS) !== 0,
    headingSizesPt: [...DEFAULT_HEADING_SIZES_PT],
    headingSpaceBeforePt: [...ZERO6],
    headingSpaceAfterPt: [...ZERO6]
  };
  let config = base;
  if (version2 >= 1) {
    const sizes = [0, 0, 0, 0, 0, 0];
    for (let i = 0; i < 6; i++) sizes[i] = dv.getFloat32(26 + i * 4, true);
    const sb = [0, 0, 0, 0, 0, 0];
    const sa = [0, 0, 0, 0, 0, 0];
    for (let i = 0; i < 6; i++) sb[i] = dv.getFloat32(58 + i * 4, true);
    for (let i = 0; i < 6; i++) sa[i] = dv.getFloat32(82 + i * 4, true);
    config = { ...base, headingSizesPt: sizes, headingSpaceBeforePt: sb, headingSpaceAfterPt: sa };
    if (dv.getUint8(50) !== 0) {
      config = { ...config, headingColor: [dv.getUint8(51), dv.getUint8(52), dv.getUint8(53)] };
    }
    const bl = dv.getFloat32(54, true);
    if (bl > 0) config = { ...config, bodyLeadingPt: bl };
  }
  return { config, blockBytesLen, blockBytesOffset: headerLen };
}

// src/relayout.ts
function createWasmRelayout(opts) {
  const { module, encodeBlocks, config, metrics, sectionDecoration } = opts;
  return (blocks, bridge) => {
    const encoded = encodeBlocks(blocks, bridge);
    const body = encoded instanceof Uint8Array ? encoded : encoded.bytes;
    const reported = encoded instanceof Uint8Array ? void 0 : encoded.blocks;
    const laidOut = reported !== void 0 && reported.length === blocks.length ? reported : blocks;
    const deco = typeof sectionDecoration === "function" ? sectionDecoration() : sectionDecoration;
    const cfg = typeof config === "function" ? config() : config;
    const input = encodePlInput(body, deco ? { ...cfg, ...deco } : cfg);
    const soa = module.layout(input);
    const view = new LayoutView(soa);
    const map = buildBlockMap(view, laidOut);
    const index = buildGeometryIndex(view, { metrics, remapBlockOrd: map.geomToDoc });
    return { view, index, blockMap: map };
  };
}

// src/fragment-surface.ts
function fragmentPageConfig(opts) {
  const zoom = opts.zoom ?? 1;
  const pageWidthPt = columnWidthPtForCss(opts.widthCss, zoom) ?? (opts.fallbackWidthCss !== void 0 ? columnWidthPtForCss(opts.fallbackWidthCss, zoom) : null) ?? opts.fallbackWidthPt ?? MIN_COLUMN_WIDTH_PT;
  return {
    ...opts.inherit,
    continuous: true,
    pageWidthPt,
    marginTopPt: 0,
    marginRightPt: 0,
    marginBottomPt: 0,
    marginLeftPt: 0,
    ...opts.overrides
  };
}
function createFragmentSurface(opts) {
  if (opts.blocks.length === 0) return null;
  const handle = createStructuredDoczHandle(opts.docz, {
    ...opts.documentId !== void 0 ? { id: opts.documentId } : {},
    ...opts.baseVersionId !== void 0 ? { baseVersionId: opts.baseVersionId } : {},
    blocks: opts.blocks
  });
  const zoom = opts.zoom ?? 1;
  const surface = createCanvasSurface(opts.host, {
    interactivity: "read",
    // The read guard is applied UPSTREAM by `createCanvasSurface` whenever
    // `interactivity: 'read'`, so the plain bridge is passed exactly as the
    // document read surface passes it. Wrapping again would add a second
    // decorator layer for no additional guarantee.
    bridge: new DoczEditBridge(handle),
    relayout: createWasmRelayout({
      module: opts.engine,
      encodeBlocks: makeStructuredEncodeBlocks(),
      // A GETTER: re-read per layout pass, so a resize reflows on `refresh()`.
      config: opts.pageConfig
    }),
    zoom,
    ...opts.dpr !== void 0 ? { dpr: opts.dpr } : {},
    ...opts.maxDpr !== void 0 ? { maxDpr: opts.maxDpr } : {},
    ...opts.colors !== void 0 ? { colors: opts.colors } : {},
    ...opts.paper !== void 0 ? { paper: opts.paper } : {},
    ...opts.align !== void 0 ? { align: opts.align } : {},
    // `null` must survive: it is a real value here ("the host drives the window"),
    // distinct from omission ("auto-detect").
    ...opts.scroller !== void 0 ? { scroller: opts.scroller } : {},
    ...opts.activatable !== void 0 ? { activatable: opts.activatable } : {},
    ...opts.onActivateRange !== void 0 ? { onActivateRange: opts.onActivateRange } : {},
    ...opts.onSelect !== void 0 ? { onSelect: opts.onSelect } : {}
  });
  const mirrorOpts = opts.mirror === void 0 ? {} : opts.mirror;
  const mirror = mirrorOpts === false ? null : new DocumentMirror(opts.host, {
    ...mirrorOpts.label !== void 0 ? { label: mirrorOpts.label } : {},
    ...mirrorOpts.lang !== void 0 ? { lang: mirrorOpts.lang } : {},
    ...mirrorOpts.testId !== void 0 ? { testId: mirrorOpts.testId } : {},
    exposure: mirrorOpts.exposure ?? "exclusive"
  });
  mirror?.update(mirrorBlocksFromHandle(handle));
  const exposure = mirrorOpts === false ? "silent" : mirrorOpts.exposure ?? "exclusive";
  const scale = opts.applyScale ? opts.applyScale(opts.host, surface, zoom) : zoom;
  let destroyed = false;
  return {
    surface,
    handle,
    mirror,
    scale,
    refresh() {
      if (destroyed) return;
      surface.refresh();
      mirror?.setExposure(exposure);
    },
    destroy() {
      if (destroyed) return;
      destroyed = true;
      mirror?.destroy();
      surface.destroy();
    }
  };
}

// src/image-store.ts
var KIND_SVG = 1;
function keyOf(img) {
  return `${img.blobOff}:${img.blobLen}`;
}
function sniffMime(bytes, kind) {
  if (kind === KIND_SVG) return "image/svg+xml";
  if (bytes.length >= 8 && bytes[0] === 137 && bytes[1] === 80 && bytes[2] === 78)
    return "image/png";
  if (bytes.length >= 3 && bytes[0] === 255 && bytes[1] === 216 && bytes[2] === 255)
    return "image/jpeg";
  if (bytes.length >= 6 && bytes[0] === 71 && bytes[1] === 73 && bytes[2] === 70)
    return "image/gif";
  if (bytes.length >= 12 && bytes[0] === 82 && bytes[1] === 73 && bytes[8] === 87 && bytes[9] === 69)
    return "image/webp";
  return "application/octet-stream";
}
function createImageStore(view, opts) {
  const entries = /* @__PURE__ */ new Map();
  const failures = [];
  const svgTimeoutMs = opts?.svgTimeoutMs ?? 1e4;
  let disposed = false;
  function fail(img, reason, detail) {
    const f = {
      blobOff: img.blobOff,
      blobLen: img.blobLen,
      kind: img.kind,
      reason,
      detail
    };
    failures.push(f);
    opts?.onFailure?.(f);
  }
  async function decode(img, bytes) {
    const blob = new Blob([bytes.slice()], { type: sniffMime(bytes, img.kind) });
    if (img.kind === KIND_SVG) {
      if (typeof document === "undefined" || typeof Image !== "function" || typeof URL.createObjectURL !== "function")
        throw new Error("svg-needs-dom");
      const url = URL.createObjectURL(blob);
      let timer;
      try {
        const el = new Image();
        await new Promise((res, rej) => {
          timer = setTimeout(() => rej(new Error("decode-failed")), svgTimeoutMs);
          el.onload = () => res();
          el.onerror = () => rej(new Error("decode-failed"));
          el.src = url;
        });
        await el.decode?.().catch(() => void 0);
        return el;
      } finally {
        if (timer !== void 0) clearTimeout(timer);
        URL.revokeObjectURL(url);
      }
    }
    if (typeof createImageBitmap !== "function") throw new Error("decode-failed");
    return await createImageBitmap(blob);
  }
  function start(img) {
    const key = keyOf(img);
    const existing = entries.get(key);
    if (existing) return existing;
    if (img.blobLen === 0) {
      entries.set(key, { state: "failed" });
      fail(
        img,
        "empty-payload",
        "the SoA image record carries no payload \u2014 either a pre-v6 buffer, or the producer had no bytes for this image"
      );
      return entries.get(key) ?? null;
    }
    const bytes = view.imageBytes(img);
    if (!bytes) {
      entries.set(key, { state: "failed" });
      fail(img, "empty-payload", "the declared blob range resolved to no bytes");
      return entries.get(key) ?? null;
    }
    const promise = decode(img, bytes).then(
      (source) => {
        if (disposed) {
          releaseSource(source);
          return;
        }
        entries.set(key, { state: "ready", source });
        opts?.onDecoded?.(img.blobOff, img.blobLen);
      },
      (err) => {
        entries.set(key, { state: "failed" });
        const svg = err instanceof Error && err.message === "svg-needs-dom";
        fail(
          img,
          svg ? "svg-needs-dom" : "decode-failed",
          svg ? "SVG payloads need a DOM (an <img> + object URL); this environment has none" : `the browser could not decode this ${img.kind === KIND_SVG ? "svg" : "raster"} payload`
        );
      }
    );
    const entry = { state: "pending", promise };
    entries.set(key, entry);
    return entry;
  }
  function releaseSource(source) {
    const bmp = source;
    if (typeof bmp.close === "function") bmp.close();
  }
  function distinct() {
    const seen = /* @__PURE__ */ new Set();
    const out = [];
    for (let i = 0; i < view.imageCount; i++) {
      const img = view.image(i);
      const key = keyOf(img);
      if (seen.has(key)) continue;
      seen.add(key);
      out.push(img);
    }
    return out;
  }
  return {
    resolve(img) {
      if (disposed) return null;
      const entry = start(img);
      return entry && entry.state === "ready" ? entry.source : null;
    },
    async prime() {
      const pending = [];
      for (const img of distinct()) {
        const entry = start(img);
        if (entry && entry.state === "pending") pending.push(entry.promise);
      }
      await Promise.all(pending);
    },
    get failures() {
      return failures;
    },
    get decodedCount() {
      let n = 0;
      for (const e of entries.values()) if (e.state === "ready") n++;
      return n;
    },
    dispose() {
      disposed = true;
      for (const e of entries.values()) if (e.state === "ready") releaseSource(e.source);
      entries.clear();
    }
  };
}

// src/paint-coverage.ts
var PAINTABLE_SECTIONS = ["rule", "image", "run"];
function createProbe() {
  const ops = {
    fillRect: 0,
    strokeRect: 0,
    fillText: 0,
    strokeText: 0,
    drawImage: 0,
    strokePath: 0
  };
  const ctx = {
    fillStyle: "",
    strokeStyle: "",
    font: "",
    lineWidth: 0,
    lineJoin: "",
    globalAlpha: 1,
    textBaseline: "",
    fontKerning: "",
    textRendering: "",
    shadowColor: "",
    shadowBlur: 0,
    shadowOffsetX: 0,
    shadowOffsetY: 0,
    setTransform() {
    },
    save() {
    },
    restore() {
    },
    transform() {
    },
    translate() {
    },
    scale() {
    },
    setLineDash() {
    },
    clearRect() {
    },
    beginPath() {
    },
    moveTo() {
    },
    lineTo() {
    },
    closePath() {
    },
    arc() {
    },
    fill() {
    },
    stroke() {
      ops.strokePath += 1;
    },
    fillRect() {
      ops.fillRect += 1;
    },
    strokeRect() {
      ops.strokeRect += 1;
    },
    fillText() {
      ops.fillText += 1;
    },
    strokeText() {
      ops.strokeText += 1;
    },
    drawImage() {
      ops.drawImage += 1;
    }
  };
  const canvas = {
    style: {},
    width: 0,
    height: 0,
    getContext: () => ctx
  };
  return { canvas, ops };
}
function delivered(view) {
  let rule = 0;
  let image = 0;
  let run = 0;
  for (let p = 0; p < view.pageCount; p++) {
    const pg = view.page(p);
    rule += pg.ruleCount;
    image += pg.imageCount;
    for (let i = 0; i < pg.runCount; i++) {
      if (view.run(pg.runFirst + i).runKind !== RunKind.emptyLine) run++;
    }
  }
  return { rule, image, run };
}
function paintCoverage(view, opts) {
  const probe = createProbe();
  const unpainted = [];
  for (let p = 0; p < view.pageCount; p++) {
    renderPage(probe.canvas, view, p, { ...opts, onUnpainted: (rec) => unpainted.push(rec) });
  }
  const d = delivered(view);
  const paperFills = view.pageCount;
  const rulesPainted = Math.max(0, probe.ops.fillRect - paperFills);
  const imagesReported = unpainted.filter((u) => u.section === "image").length;
  const runsReported = unpainted.filter((u) => u.section === "run").length;
  const imagesInked = Math.min(d.image, probe.ops.drawImage + probe.ops.strokeRect);
  const report = {
    pagesWalked: view.pageCount,
    sections: {
      rule: { delivered: d.rule, painted: Math.min(d.rule, rulesPainted) },
      image: { delivered: d.image, painted: Math.min(d.image, imagesInked) },
      run: { delivered: d.run, painted: Math.max(0, d.run - runsReported) }
    },
    unpainted,
    silentlyDropped: 0,
    ops: probe.ops
  };
  const runSilent = Math.max(0, d.run - report.sections.run.painted - runsReported);
  const imageSilent = Math.max(0, d.image - probe.ops.drawImage - imagesReported);
  const ruleSilent = Math.max(0, d.rule - report.sections.rule.painted);
  report.silentlyDropped = runSilent + imageSilent + ruleSilent;
  return report;
}
function assertPaintConformance(view, opts) {
  const report = paintCoverage(view, opts);
  if (report.silentlyDropped > 0) {
    throw new Error(
      `web-page-layout: ${report.silentlyDropped} SoA record(s) were delivered by the wire, produced no ink, and produced no diagnostic. Silent content loss is never acceptable on a canvas-only surface \u2014 add the paint arm, or report the record.`
    );
  }
  if (opts?.requireComplete && report.unpainted.length > 0) {
    const first = report.unpainted[0];
    throw new Error(
      `web-page-layout: ${report.unpainted.length} delivered record(s) went unpainted. First: ${first.section} #${first.recordIndex} on page ${first.pageIndex} (block ${first.blockOrd}) \u2014 ${first.reason}: ${first.detail}`
    );
  }
  return report;
}

// src/wasm-loader.ts
var OK = 0;
var errName = {
  [-1]: "OOM",
  [-2]: "DECODE",
  [-3]: "FONT",
  [-4]: "LAYOUT",
  [-5]: "INVALID"
};
var ENOSYS = 52;
function wasiShim(getMemory) {
  return {
    fd_close: () => ENOSYS,
    fd_read: () => ENOSYS,
    fd_seek: () => ENOSYS,
    fd_filestat_get: () => ENOSYS,
    path_open: () => ENOSYS,
    fd_pwrite: () => ENOSYS,
    // Report "all written" for stderr/stdout so a diagnostic print is a no-op.
    fd_write: (_fd, iovs, iovsLen, nwritten) => {
      const dv = new DataView(getMemory().buffer);
      let total = 0;
      for (let i = 0; i < iovsLen; i++) total += dv.getUint32(iovs + i * 8 + 4, true);
      dv.setUint32(nwritten, total, true);
      return OK;
    },
    proc_exit: () => {
    },
    random_get: (buf, len) => {
      const mem = new Uint8Array(getMemory().buffer, buf, len);
      for (let i = 0; i < len; i++) mem[i] = Math.random() * 256 | 0;
      return OK;
    }
  };
}
function detectRuntime() {
  if (typeof Bun !== "undefined") return "bun";
  if (typeof Deno !== "undefined") return "deno";
  const proc = globalThis.process;
  const hasNode = typeof proc?.versions?.node === "string";
  if (!hasNode && typeof window !== "undefined") return "browser";
  if (!hasNode && typeof self !== "undefined") return "browser";
  return "node-esm";
}
function wasmUrl() {
  return new URL("../wasm/pdfz-layout.wasm", import.meta.url);
}
function wasmFsPath() {
  const url = wasmUrl();
  if (url.protocol === "file:") {
    const p = decodeURIComponent(url.pathname);
    return /^\/[A-Za-z]:/.test(p) ? p.slice(1) : p;
  }
  return url.href;
}
function toArrayBuffer(u8) {
  const out = new ArrayBuffer(u8.byteLength);
  new Uint8Array(out).set(u8);
  return out;
}
async function readWasmBytes(runtime) {
  switch (runtime) {
    case "bun": {
      const file = Bun.file(wasmFsPath());
      return await file.arrayBuffer();
    }
    case "deno": {
      const u8 = await Deno.readFile(
        wasmUrl()
      );
      return toArrayBuffer(u8);
    }
    case "node-esm":
    case "node-cjs": {
      const fsp = await import('fs/promises');
      const buf = await fsp.readFile(wasmUrl());
      return toArrayBuffer(buf);
    }
    case "browser": {
      const resp = await fetch(wasmUrl().href);
      return await resp.arrayBuffer();
    }
  }
}
var UTF83 = new TextDecoder();
function readCString(ex, ptr) {
  const mem = new Uint8Array(ex.memory.buffer);
  let end = ptr;
  while (mem[end] !== 0 && end < mem.length) end++;
  return UTF83.decode(mem.subarray(ptr, end));
}
function buildModule(ex) {
  const version2 = readCString(ex, ex.pl_version());
  const features = ex.pl_features() >>> 0;
  function layout(input) {
    const inPtr = ex.pl_alloc(input.byteLength);
    if (inPtr === 0) throw new Error("web-page-layout: pl_alloc(input) failed (OOM)");
    try {
      new Uint8Array(ex.memory.buffer, inPtr, input.byteLength).set(input);
      const outSlots = ex.pl_alloc(8);
      if (outSlots === 0) throw new Error("web-page-layout: pl_alloc(out-slots) failed (OOM)");
      try {
        const rc = ex.pl_layout(inPtr, input.byteLength, outSlots, outSlots + 4);
        if (rc !== OK) throw new Error(`web-page-layout: pl_layout failed (${errName[rc] ?? rc})`);
        const dv = new DataView(ex.memory.buffer);
        const outPtr = dv.getUint32(outSlots, true);
        const outLen = dv.getUint32(outSlots + 4, true);
        const copy = new Uint8Array(outLen);
        copy.set(new Uint8Array(ex.memory.buffer, outPtr, outLen));
        ex.pl_free(outPtr, outLen);
        return copy;
      } finally {
        ex.pl_free(outSlots, 8);
      }
    } finally {
      ex.pl_free(inPtr, input.byteLength);
    }
  }
  return { layout, version: version2, features, exports: ex };
}
function freshImports() {
  let mem;
  return {
    imports: {
      wasi_snapshot_preview1: wasiShim(() => {
        if (!mem) throw new Error("web-page-layout: memory not ready");
        return mem;
      })
    },
    adopt: (ex) => {
      mem = ex.memory;
    }
  };
}
async function instantiatePageLayout(bytes) {
  const { imports, adopt } = freshImports();
  const result = await WebAssembly.instantiate(bytes, imports);
  const ex = result.instance.exports;
  adopt(ex);
  return buildModule(ex);
}
async function instantiate(runtime) {
  const { imports, adopt } = freshImports();
  let ex;
  if (runtime === "browser") {
    const href = wasmUrl().href;
    try {
      if (typeof WebAssembly.instantiateStreaming === "function") {
        const result3 = await WebAssembly.instantiateStreaming(fetch(href), imports);
        ex = result3.instance.exports;
        adopt(ex);
        return ex;
      }
    } catch {
    }
    const bytes2 = await (await fetch(href)).arrayBuffer();
    const result2 = await WebAssembly.instantiate(bytes2, imports);
    ex = result2.instance.exports;
    adopt(ex);
    return ex;
  }
  const bytes = await readWasmBytes(runtime);
  const result = await WebAssembly.instantiate(bytes, imports);
  ex = result.instance.exports;
  adopt(ex);
  return ex;
}
var _cached = null;
function loadPageLayout() {
  if (_cached != null) return _cached;
  _cached = (async () => {
    const runtime = detectRuntime();
    const ex = await instantiate(runtime);
    return buildModule(ex);
  })().catch((e) => {
    _cached = null;
    throw e instanceof Error ? e : new Error(String(e));
  });
  return _cached;
}
function __resetPageLayoutCache() {
  _cached = null;
}

// src/index.ts
var version = "0.2.0";

export { A4_DEFAULT, BLOCK_ORD_AUX_BIT, BLOCK_ORD_NONE, CaretBlink, DEFAULT_BLOCK_MARGIN, DEFAULT_FLASH_DURATION_MS, DEFAULT_FOCUS_RING, DEFAULT_HEADING_SIZES_PT, DEFAULT_METRICS, DEFAULT_OVERLAY_COLORS, DEFAULT_OVERSCAN_CSS, DEFAULT_PAGE_GAP_CSS, DEFAULT_SCROLL_DRIFT_TOLERANCE_CSS, DEFAULT_SCROLL_DURATION_MS, DEFAULT_SCROLL_MARGIN_CSS, DEFAULT_TILE_HEIGHT_CSS, DoczEditBridge, EditorCore, FLAG_CONTINUOUS, FLAG_GLYPH_GRANULAR, FOCUS_RING_WIDTH_PT, HEADER_LEN2 as HEADER_LEN, HEADER_LEN_V1, HEADER_LEN_V2, HEADER_ROW, HIGHLIGHT_BLEND, HIGHLIGHT_BLEND_VAR, HIGHLIGHT_INFLATE_PT, HIGHLIGHT_RADIUS_PT, HiddenInputSink, LONG_PRESS_MS, LayoutView, MAGIC, MAX_CANVAS_SIDE_PX, MAX_COLUMN_WIDTH_PT, MIN_COLUMN_WIDTH_PT, MIN_SCROLLER_TRAVEL_CSS, MIN_VERSION, NO_CELL, NO_ORD, PAINTABLE_SECTIONS, PLCF_FLAG_CONTINUOUS, PREEDIT_UNDERLINE_PT, PlainTextEditBridge, RUN_BLANK_ORIGIN_OFF, RunKind, SUBPIXEL_TOLERANCE_CSS, SUPPRESSED_LINE_NUMBERING, SUPPRESSED_MASK, SUPPRESSED_PAGE_BACKGROUND, SUPPRESSED_PAGE_BORDERS, SUPPRESSED_PAGE_BREAKS, SUPPRESSED_PAGE_CHROME, SUPPRESSED_SHIFT, SURFACE_TOUCH_ACTION, TABLE_CLICK_DETAIL, TAP_SLOP_PX, TRIPLE_CLICK_DETAIL, TypingRun, VERSION, __resetPageLayoutCache, alignMargin, applyEdit, assertPaintConformance, blockLines, buildBlockMap, buildGeometryIndex, buildTilePlan, byteLen, canReachBand, canvasCellAt, canvasCellRegion, canvasDeleteStructuredBlock, canvasDoczBlocks, canvasInsertStructuredAfter, canvasReplaceStructuredBlock, canvasSelectTable, canvasSelectedTable, canvasTableAt, canvasTableCellOrds, caretRect, caretRectToCss, collapsed, columnWidthPtForCss, comparePos, compositionEnd, compositionStart, compositionUpdate, createCanvasEditor, createCanvasSurface, createFragmentSurface, createImageStore, createScrollAnimator, createTilePainter, createTween, createWasmRelayout, deadZoneAt, decodePlInputHeader, decodeSuppressed, deleteFromKeydown, docEnd, docStart, doczBridgeOf, doczEncodeBlocks, easeInOutCubic, editFromKeydown, emacsDeleteBindingsForPlatform, encodePlInput, ensureFonts, exceedsTapSlop, fitsUntiled, focusRingEdges, fontFamilyList, fragmentPageConfig, gestureFromPointerDown, handleFromBridge, hasMeaningfulTravel, hitTest, indexAtOffset, instantiatePageLayout, intentFromBeforeInput, isAddressable, isAuxBlockOrd, isAuxCaretEligible, isCanvasAddressableKind, isCanvasPaintable, isCollapsed, isEmptyLineRun, isHeadingOrdKind, isListItemOrdKind, isParagraphOrdKind, isSingleParagraphQuote, keydownNamesEnter, lineEnd, lineForOffset, lineRunKind, lineStart, linear, loadPageLayout, makeDoczEncodeBlocks, makeStructuredEncodeBlocks, maxPaintableHeightPt, moveCaretHorizontal, moveCaretWord, moveVertical, namespaceKindOf, navFromKeydown, nextActivationIndex, nextGraphemeBytes, nextWordBoundary, orderActivatable, overflowScrolls, pageCssSize, pageGuideOffsetsPt, paintCaret, paintCoverage, paintFocusRing, paintHighlights, paintPreedit, paintSelection, pickPasteFlavours, pickScrollAncestor, prefersReducedMotionQuery, prevGraphemeBytes, prevWordBoundary, rangeSlices, rangeText, readOnlyBridge, rectsForPage, renderBand, renderPage, renderSurface, requiredRunRec, resolveOverlayColor, runCaretXPt, runInTable, runInkExtentPt, runIsCell, runOffsetAtXPt, scrollSurfaceFor, scrollTopForBand, selectionRects, sizeOverlay, sliceBytes, sniffMime, spliceBytes, structuredEncodeBlocks, tileWindow, touchOutcome, version };
//# sourceMappingURL=index.js.map
//# sourceMappingURL=index.js.map